import { createSelector } from 'reselect';
import { MetaReducer } from '@ngrx/store';

import * as fromActiveRewards from './active-rewards';
import * as fromCalendar from './calendar';
import * as fromCare from './care';
import * as fromCustomer from './customer';
import * as fromDiscover from './discover';
import * as fromEngage from './engage';
import * as fromCampaignEngage from './campaignEngage';
import * as fromEngagement from './engagement';
import * as fromAutomations from './automations';
import * as fromError from './error';
import * as fromEula from './eula';
import * as fromLayout from './layout';
import * as fromMeet from './meet';
import * as fromNotifications from './notifications';
import * as fromProfile from './profile';
import * as fromSettings from './settings';
import * as fromUnderstand from './understand';
import * as fromUser from './user';

import * as R from 'ramda';
import moment from 'moment-timezone';
import {
  addExtraFilters,
  getAllFormattedSelectedFilters,
  getFormattedSelectedFilters,
  getMatchingCustomer,
  isCustomDateRangeFilterIncomplete,
  replaceCareHtml,
  replaceMGHtml
} from './util';
import { AppConstants } from '../app.constants';
import { CalendarConstants } from '../calendar/calendar.constants';
import { CareConstants } from '../care/care.constants';
import { DiscoverConstants } from '../discover/discover.constants';
import { EngageConstants } from '../engage/engage.constants';
import { Inactives } from '../models/shared/inactive-flag.model';
import { RepeatToggleOption, SubToggleOption } from '../models/discover/repeat-options.model';
import { MeetConstants } from '../meet/meet.constants';
import { NavMenuConstants } from '../nav-menu/nav-menu.constants';
import { NotificationListConstants } from '../notifications/notification-list.constants';
import { ProfileConstants } from '../customer-profile/customer-profile.constants';
import { SharedConstants } from '../shared/shared.constants';
import { Tile } from '../models/understand/tile.model';
import {
  isRewardCampaignsTile,
  withoutUniqueCustomerSections
} from '../understand/reward-metrics-tiles';
import { CategorizedRewards } from '../models/engage/categorized-rewards.model';
import { CalendarCategory } from '../models/calendar/clandar-category.model'
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { CampaignStatus, CampaignType, EventDetails, RewardDetails } from '../models/engage/campaign-details.model';
import { TemplateDetails } from '../models/engage/template-details.model';
import { CampaignTemplatesModel, CategorizedTemplates } from '../models/engage/campaign-templates.model';
import { AutomationCampaignDetails, SelectedAutomation } from '../models/discover/automation-template.model';

import { NotificationLayout } from '../models/engage/notification-layout.model';
import { NotificationBanner } from '../models/user/notification-banner';
import { ActionType } from 'spotlight-ui/app/discover/dependencies/digital-marketing-automation-source';
import { UnderstandConstants } from 'spotlight-ui/app/understand/understand.constants';

export const metaReducers: MetaReducer<State>[] = [];

export interface State {
  activeRewards: fromActiveRewards.State;
  calendar: fromCalendar.State;
  care: fromCare.State;
  customer: fromCustomer.State;
  discover: fromDiscover.State;
  engage: fromEngage.State;
  campaignEngage: fromCampaignEngage.State;
  engagement: fromEngagement.State;
  automations: fromAutomations.State;
  error: fromError.State;
  eula: fromEula.State;
  layout: fromLayout.State;
  meet: fromMeet.State;
  notifications: fromNotifications.State;
  profile: fromProfile.State;
  settings: fromSettings.State;
  understand: fromUnderstand.State;
  user: fromUser.State;
}

export const reducers = {
  activeRewards: fromActiveRewards.reducer,
  calendar: fromCalendar.reducer,
  care: fromCare.reducer,
  customer: fromCustomer.reducer,
  discover: fromDiscover.reducer,
  engage: fromEngage.reducer,
  campaignEngage: fromCampaignEngage.reducer,
  engagement: fromEngagement.reducer,
  automations: fromAutomations.reducer,
  error: fromError.reducer,
  eula: fromEula.reducer,
  layout: fromLayout.reducer,
  meet: fromMeet.reducer,
  notifications: fromNotifications.reducer,
  profile: fromProfile.reducer,
  settings: fromSettings.reducer,
  understand: fromUnderstand.reducer,
  user: fromUser.reducer
};

/**
 * ActiveRewards state root selector
 */
export const getActiveRewardsState = (state) => state.activeRewards;

/**
 * ActiveRewards state children selectors
 */
export const getActiveRewardCustomers = createSelector(getActiveRewardsState, fromActiveRewards.getActiveRewardCustomers);

export const getActiveRewardLoading = createSelector(getActiveRewardsState, fromActiveRewards.getLoading);

export const getActiveRewardError = createSelector(getActiveRewardsState, fromActiveRewards.getError);

export const getIncludeActiveCustomers = createSelector(getActiveRewardsState, fromActiveRewards.getIncludeActiveCustomers);

/**
 * Layout state root selector
 */
export const getLayoutState = (state: State) => state.layout;

/**
 * Layout state children selectors
 */
export const getScreenSize = createSelector(getLayoutState, fromLayout.getScreenSize);

export const getIsMobile = createSelector(getScreenSize , (size) => {
  return size === AppConstants.extraSmall || size === AppConstants.small;
});

export const getIsExtraSmallMobile = createSelector(getScreenSize , (size) => {
  return size === AppConstants.extraSmall;
});

export const getIsSmallMobile = createSelector(getScreenSize , (size) => {
  return size === AppConstants.small;
});

/**
 * User root selector
 */
export const getUserState = (state: State) => state.user;

/*
 * User child reducers
 */

export const getUserName = createSelector(getUserState, fromUser.getUserName);

export const getUserLocations = createSelector(getUserState, fromUser.getLocations);

export const getUserLoading = createSelector(getUserState, fromUser.getLoading);

export const getUserError = createSelector(getUserState, fromUser.getError);

export const getLocationError = createSelector(getUserState, fromUser.getLocationError);

export const getShowWelcomeModalFromBanner = createSelector(getUserState, fromUser.getWelcomeModal);

export const getFavoriteLocations = createSelector(getUserState, fromUser.getFavoriteLocations);

export const getDarkModeEnabled = createSelector(getUserState, fromUser.getDarkModeEnabled);

export const getSelectedLocation = createSelector(getUserState, fromUser.getSelectedLocation);
export const getFeatureFlags = createSelector(getUserState, fromUser.getFeatureFlags);

export const getAdminFull = createSelector(getUserState, fromUser.getAdminFull);

export const getAdminView = createSelector(getUserState, fromUser.getAdminView);

export const getReadOnlyUser = createSelector(getAdminFull, getAdminView,
  (adminFull, adminView) => adminView && !adminFull);

export const isAdminUser = createSelector(getAdminFull, getAdminView,
  (adminFull, adminView) => adminFull || adminView);

export const getAutomationsFeatureEnabledMap = createSelector(
  getFeatureFlags,
  (flags: LDFlagSet) => {
    return {
      'grow-breakfast': flags[DiscoverConstants.campaignEngineV2LapsedBreakfast],
      'churn-flag': flags[DiscoverConstants.campaignEngineV2ReduceDropOffRate],
      'operator-led-delivery': flags[DiscoverConstants.campaignEngineV2CfaDelivery],
      'recognize-catering': flags[DiscoverConstants.campaignEngineV2RecognizeCatering],
      'engage-first': flags[DiscoverConstants.campaignEngineV2EngageMobileOrder]
    }
  }
);

export const getFeatureEnabled = (featureName: string) => {
  return createSelector(getSelectedLocation, getFeatureFlags,(loc, flags: LDFlagSet) => {
    if (flags?.[featureName] != undefined) {
      let feature = flags[featureName];
      // Feature flags that are not location specific will be just booleans
      if (typeof feature == 'boolean') {
        return feature;
      }
      // Check if all locations are enabled or if the current location is in the locations list
      return feature.enabledForAllLocations || (feature.enabledForLocations && feature.enabledForLocations.includes(loc?.locationNumber));
    }
    return false;
  });
};

export const getCustomerQrCodeSearchEnabled = createSelector(
  getFeatureEnabled(AppConstants.customerQrCodeSearchFlag),
  getFeatureEnabled(AppConstants.customerQrCodeSearchFlagKey),
  (camelCaseKeyEnabled, rawKeyEnabled) => Boolean(camelCaseKeyEnabled || rawKeyEnabled));

export const getThirdPartyEnabled = () => {
  return createSelector(getSelectedLocation, getFeatureEnabled('thirdPartyPromotion'), (location, enabled) => {
    return enabled ? Boolean(location?.hasThirdParty) : false;
  });
};

export const getMaintenanceText = () => {
  return createSelector(getFeatureFlags, (flags) => {
    const maintenanceFeatureFlag = flags[AppConstants.maintenanceFlag];
    return maintenanceFeatureFlag.maintenanceText;
  });
}

export const getNavMenuOptions = createSelector(
  isAdminUser, getSelectedLocation, getUserLocations, getFeatureEnabled(SharedConstants.disableCalendarTabFlag),
  (adminUser, selectedLoc, locations, disableCalendarTab) => {
    let menuOptions = disableCalendarTab ? NavMenuConstants.navMenuOptionsWithoutCalendar : NavMenuConstants.navMenuOptions;
    if (adminUser) {
      menuOptions.forEach(menuItem => {
        if (menuItem.isLocationMenu) {
          menuItem.options[0].label = selectedLoc.locationName;
          menuItem.options[0].locationNumber = selectedLoc.locationNumber;
          menuItem.options[0].imageURL = selectedLoc.imageUrl;
        }
      });
    } else {
      menuOptions.forEach(menuItem => {
        if (menuItem.isLocationMenu) {
          let locMenu = locations.map(loc => {
            let obj : any = {
              id: 'location',
              label: loc.locationName,
              imageURL: loc.imageUrl,
              locationNumber: loc.locationNumber
            };
            return obj;
          });
          if (locMenu.length === 1) {
            locMenu[0].customClass = true;
            menuItem.title = 'Your Restaurant';
          }
          menuItem.options = locMenu;
        }
      });
    }
    return menuOptions;
  });

export const getMeetSelectedIndex = createSelector(
  getFeatureEnabled(SharedConstants.disableDiscoverTabFlag),
  (isDiscoverTabDisabled) => isDiscoverTabDisabled ? 0 : 1);

export const getRecoverSelectedIndex = createSelector(
  getFeatureEnabled(SharedConstants.disableDiscoverTabFlag), getFeatureEnabled(SharedConstants.disableMeetTabFlag),
  (isDiscoverTabDisabled, isMeetTabDisabled) => {
    let recoverIndex = 2;
    if (isDiscoverTabDisabled) {
      recoverIndex--;
    }
    if (isMeetTabDisabled) {
      recoverIndex--;
    }
    return recoverIndex;
  });

export const getEngageSelectedIndex = createSelector(
  getFeatureEnabled(SharedConstants.disableDiscoverTabFlag), getFeatureEnabled(SharedConstants.disableMeetTabFlag),
  getFeatureEnabled(SharedConstants.disableRecoverTabFlag),
  (isDiscoverTabDisabled, isMeetTabDisabled, isRecoverTabDisabled) => {
    let engageIndex = 3;
    if (isDiscoverTabDisabled) {
      engageIndex--;
    }
    if (isMeetTabDisabled) {
      engageIndex--;
    }
    if (isRecoverTabDisabled) {
      engageIndex--;
    }
    return engageIndex;
  });

export const getCalendarSelectedIndex = createSelector(
  getFeatureEnabled(SharedConstants.disableDiscoverTabFlag), getFeatureEnabled(SharedConstants.disableMeetTabFlag),
  getFeatureEnabled(SharedConstants.disableRecoverTabFlag), getFeatureEnabled(SharedConstants.disableEngageTabFlag),
  (isDiscoverTabDisabled, isMeetTabDisabled, isRecoverTabDisabled, isEngageTabDisabled) => {
    let calendarIndex = 4;
    if (isDiscoverTabDisabled) {
      calendarIndex--;
    }
    if (isMeetTabDisabled) {
      calendarIndex--;
    }
    if (isRecoverTabDisabled) {
      calendarIndex--;
    }
    if (isEngageTabDisabled) {
      calendarIndex--;
    }
    return calendarIndex;
  });

export const getBannerById = (id: string) => {
  return createSelector(getSelectedLocation, (selectedLocation): NotificationBanner => {
    if (selectedLocation && selectedLocation.notificationBanners) {
      return selectedLocation.notificationBanners.find((b) => b.id === id);
    }
  });
};

/**
 * Eula state root selector
 */
export const getEulaState = (state: State) => state.eula;

/**
 * Eula state children selectors
 */

export const getShowWelcomeModalFromEula = createSelector(getEulaState, fromEula.getDisplayWelcomeModal);

export const getEulaLoading = createSelector(
  getEulaState, fromEula.getLoading);

export const getEulaError = createSelector(
  getEulaState, fromEula.getError);

export const getEulaAgree = createSelector(
  getEulaState, fromEula.getAgree);

export const getEulaHtml = createSelector(
  getEulaState, fromEula.getHtml);

export const getShowWelcomeModal = createSelector(
  getShowWelcomeModalFromBanner, getShowWelcomeModalFromEula, (
    showWelcomeModalBanner, showWelcomeModalEula) =>{
    return showWelcomeModalEula || showWelcomeModalBanner;
  });
/**
 * Meet root selector
 */
export const getMeetState = (state: State) => state.meet;

/**
 * Meet Child reducers
 */
export const getSearchTerm = createSelector(getMeetState, fromMeet.getSearchTerm);

export const getSearchBy = createSelector(getMeetState, fromMeet.getSearchBy);

export const getDisplayErrorText = createSelector(getMeetState, fromMeet.getDisplayErrorText);

export const getPageLimit = createSelector(getMeetState, fromMeet.getPageLimit);

export const getPage = createSelector(getMeetState, fromMeet.getPage);

export const getSort = createSelector(getMeetState, fromMeet.getSort);

export const getSortOrder = createSelector(getMeetState, fromMeet.getSortOrder);

export const getMeetColumns = createSelector(getMeetState, fromMeet.getColumns);

export const getMeetColumnsWithFlag = createSelector(getMeetState, fromMeet.getColumnsWithFlag);

export const getAllFilters = createSelector(getMeetState, fromMeet.getAllFilters);

export const getFiltersLoading = createSelector(getMeetState, fromMeet.getFiltersLoading);

export const getFiltersWithoutSelectedSubOption = createSelector(getMeetState, fromMeet.getFiltersWithoutSelectedSubOption);

export const getFiltersWithoutSelectedToggle = createSelector(getMeetState, fromMeet.getFiltersWithoutSelectedToggle);

export const getMeetFiltersStartDate = createSelector(getMeetState, fromMeet.getMeetFiltersStartDate);

export const getEngageFiltersStartDate = createSelector(getMeetState, fromMeet.getEngageFiltersStartDate);

export const getFiltersStartDateByPage = (page) => {
  return createSelector(getMeetFiltersStartDate, getEngageFiltersStartDate, (meetStartDate, engageStartDate) => {
    if (page === AppConstants.meetPage) {
      return meetStartDate;
    } else if (page === AppConstants.engagePage) {
      return engageStartDate;
    }
  });
};

export const getMeetFiltersEndDate = createSelector(getMeetState, fromMeet.getMeetFiltersEndDate);

export const getEngageFiltersEndDate = createSelector(getMeetState, fromMeet.getEngageFiltersEndDate);

export const getFiltersEndDateByPage = (page) => {
  return createSelector(getMeetFiltersEndDate, getEngageFiltersEndDate, (meetEndDate, engageEndDate) => {
    if (page === AppConstants.meetPage) {
      return meetEndDate;
    } else if (page === AppConstants.engagePage) {
      return engageEndDate;
    }
  });
};

export const getMeetFiltersOnBetweenId = createSelector(getMeetState, fromMeet.getMeetFiltersOnBetweenId);

export const getEngageFiltersOnBetweenId = createSelector(getMeetState, fromMeet.getEngageFiltersOnBetweenId);

export const getFiltersOnBetweenIdByPage = (page) => {
  return createSelector(getMeetFiltersOnBetweenId, getEngageFiltersOnBetweenId, (meetOnBetweenId, engageOnBetweenId) => {
    if (page === AppConstants.meetPage) {
      return meetOnBetweenId;
    } else if (page === AppConstants.engagePage) {
      return engageOnBetweenId;
    }
  });
};

export const getFiltersError = createSelector(getMeetState, fromMeet.getFiltersError);

export const getMeetFilters = createSelector(getMeetState, fromMeet.getMeetDisplayFilters);

export const getEngageDefaultAudienceApplied = createSelector(getMeetState, fromMeet.getEngageDefaultAudienceApplied);

export const getEngageFilters = createSelector(getMeetState, fromMeet.getEngageDisplayFilters);

export const areFiltersLoaded = createSelector(getAllFilters, (filters) => filters.length > 0);

export const getFiltersByPage = (page) => {
  return createSelector(getMeetFilters, getEngageFilters, (meetFilters, engageFilters) => {
    if (page === AppConstants.meetPage) {
      return meetFilters;
    } else if (page === AppConstants.engagePage) {
      return engageFilters;
    }
  });
};

export const getIfFiltersDateIsNull = (page) => {
  return createSelector(getFiltersStartDateByPage(page), getFiltersEndDateByPage(page), (startDate, endDate) => !startDate || !endDate)
};

export const getMeetSelectedFilters = createSelector(getMeetState, fromMeet.getMeetSelectedFilters);

export const getEngageSelectedFilters = createSelector(getMeetState, fromMeet.getEngageSelectedFilters);

export const getSelectedFiltersByPage = (page) => {
  return createSelector(getMeetSelectedFilters, getEngageSelectedFilters, (meetSelectedFilters, engageSelectedFilters) => {
    if (page === AppConstants.meetPage) {
      return meetSelectedFilters;
    } else if (page === AppConstants.engagePage) {
      return engageSelectedFilters;
    }
  });
};

export const getSelectedFiltersIdsByPage = (page) => {
  return createSelector(getSelectedFiltersByPage(page), (selectedFilters) => {
    return selectedFilters.map((f) => f.id);
  });
};

export const getAllSavedGroups = createSelector(getMeetState, fromMeet.getAllSavedGroups);

export const getMeetSavedGroups = createSelector(getMeetState, fromMeet.getMeetDisplaySavedGroups);

export const getEngageSavedGroups = createSelector(getMeetState, fromMeet.getEngageDisplaySavedGroups);

export const getSavedGroupsByPage = (page) => {
  return createSelector(getMeetSavedGroups, getEngageSavedGroups, (meetSavedGroups, engageSavedGroups) => {
    if (page === AppConstants.meetPage) {
      return meetSavedGroups;
    } else if (page === AppConstants.engagePage) {
      return engageSavedGroups;
    }
  });
};

export const getMeetSelectedSavedGroups = createSelector(getMeetState, fromMeet.getMeetSelectedSavedGroups);

export const getEngageSelectedSavedGroups = createSelector(getMeetState, fromMeet.getEngageSelectedSavedGroups);

export const getMeetSavedGroupBeingEdited = createSelector(getMeetState, fromMeet.getMeetSavedGroupBeingEdited);

export const getSelectedSavedGroupsByPage = (page) => {
  return createSelector(getMeetSelectedSavedGroups, getEngageSelectedSavedGroups,
    (meetSelectedSavedGroups, engageSelectedSavedGroups) => {
      let formattedSavedGroups = [];
      if (page === AppConstants.meetPage) {
        formattedSavedGroups = meetSelectedSavedGroups;
      } else if (page === AppConstants.engagePage) {
        formattedSavedGroups = engageSelectedSavedGroups;
      }

      if (formattedSavedGroups.length > 0) {
        formattedSavedGroups = formattedSavedGroups.map(savedGroup => {
          let formattedName = savedGroup.name ? 'Are Part of ' + savedGroup.name : 'N/A';
          return { ...savedGroup, formattedName };
        });
      }

      return formattedSavedGroups;
    });
};

export const getSavedGroupsLoading = createSelector(getMeetState, fromMeet.getSavedGroupsLoading);

export const getSavedGroupsError = createSelector(getMeetState, fromMeet.getSavedGroupsError);

export const getModifySavedGroupsLoading = createSelector(getMeetState, fromMeet.getModifySavedGroupsLoading);

export const getDisplaySavedGroups = (page) => {
  return createSelector(getSavedGroupsByPage(page), getSelectedSavedGroupsByPage(page),
    (allSavedGroups, selectedSavedGroups) => {
      let displaySavedGroups = allSavedGroups;
      displaySavedGroups.forEach(displaySavedGroup => {
        const selectedGroupIndex = R.findIndex(R.propEq('id', displaySavedGroup.id))(selectedSavedGroups);
        displaySavedGroup.isSelected = selectedGroupIndex > -1;
      });
      return displaySavedGroups;
    });
};

export const isSavedGroupsCategoryOpen = createSelector(getMeetFilters, (allFilters) => {
  let savedGroupCategory = R.find(R.propEq('id', MeetConstants.savedGroupsFilterId))(allFilters);
  return savedGroupCategory && savedGroupCategory.open;
});

export const getMeetIncludedCustomers = createSelector(getMeetState, fromMeet.getMeetIncludedCustomers);

export const getEngageIncludedCustomers = createSelector(getMeetState, fromMeet.getEngageIncludedCustomers);

export const getMeetExcludedCustomers = createSelector(getMeetState, fromMeet.getMeetExcludedCustomers);

export const getEngageExcludedCustomers = createSelector(getMeetState, fromMeet.getEngageExcludedCustomers);

export const getEngageExcludedCustomersIds = createSelector(getEngageExcludedCustomers,
  (excludedCustomers) => R.pluck('id', excludedCustomers));

export const getSelectedSavedGroupId = createSelector(getMeetState, fromMeet.getSelectedSavedGroupId);

export const getSelectedSavedGroupCustomersLoading = createSelector(getMeetState, fromMeet.getSelectedSavedGroupCustomersLoading);

export const getSelectedSavedGroupCustomersError = createSelector(getMeetState, fromMeet.getSelectedSavedGroupCustomersError);

export const getSelectedSavedGroupCustomers = createSelector(getMeetState, fromMeet.getSelectedSavedGroupCustomers);

export const getSelectedSavedGroupAllCustomerIds = createSelector(getMeetState, fromMeet.getSelectedSavedGroupAllCustomerIds);

export const getIsSelectedSavedGroupLarge = createSelector(getMeetState, fromMeet.isSelectedSavedGroupLarge);

export const getMeetScrollTo = createSelector(getMeetState, fromMeet.scrollTo);

export const areMeetFiltersSelected = createSelector(
  getMeetSelectedFilters, getMeetSelectedSavedGroups, getMeetIncludedCustomers, getMeetExcludedCustomers,
  (selectedFilters, selectedSavedGroups, includedCustomers, excludedCustomers) => {
    return (selectedFilters.length + selectedSavedGroups.length + excludedCustomers.length + includedCustomers.length) > 0;
  });

export const areEngageFiltersSelected = createSelector(
  getEngageSelectedFilters, getEngageSelectedSavedGroups, getEngageIncludedCustomers, getEngageExcludedCustomers,
  (selectedFilters, selectedSavedGroups, includedCustomers, excludedCustomers) => {
    return (selectedFilters.length + selectedSavedGroups.length + excludedCustomers.length + includedCustomers.length) > 0;
  });

export const getSelectedCustomerCountByPage = (page) => {
  return createSelector(getTotalCountOfCustomers, getEngageFilterDisplayCustomerCount, (meetTotalCustomers, engageTotalCustomers) => {
    if (page === AppConstants.meetPage) {
      return meetTotalCustomers;
    } else if (page === AppConstants.engagePage) {
      return engageTotalCustomers;
    }
  });
};

export const getIndividualSelectedFilterIdsByPage = (page) => {
  return createSelector(getIncludedCustomersByPage(page), getExcludedCustomersByPage(page),
    (includedCustomers, excludedCustomers) => {
      let selectedFilterIds = [];
      if (!R.isEmpty(includedCustomers)) {
        selectedFilterIds.push(MeetConstants.includeFilterId);
      }
      if (!R.isEmpty(excludedCustomers)) {
        selectedFilterIds.push(MeetConstants.excludeFilterId);
      }
      return selectedFilterIds;
    });
};

export const getIncludedCustomersByPage = (page) => {
  return createSelector(getMeetIncludedCustomers, getEngageIncludedCustomers,
    (meetIncludedCustomers, engageIncludedCustomers) => {
      if (page === AppConstants.meetPage) {
        return meetIncludedCustomers;
      } else if (page === AppConstants.engagePage) {
        return engageIncludedCustomers;
      }
    });
};

export const getExcludedCustomersByPage = (page) => {
  return createSelector(getMeetExcludedCustomers, getEngageExcludedCustomers,
    (meetExcludedCustomers, engageExcludedCustomers) => {
      if (page === AppConstants.meetPage) {
        return meetExcludedCustomers;
      } else if (page === AppConstants.engagePage) {
        return engageExcludedCustomers;
      }
    });
};

export const getDisplayFilters = (page) => {
  return createSelector(
    getFiltersByPage(page), getSelectedFiltersIdsByPage(page),
    getIndividualSelectedFilterIdsByPage(page), getSelectedSavedGroupsByPage(page),
    getFiltersOnBetweenIdByPage(page),
    (allFilterCategories, selectedFilterIds, individualSelectedFilterIds, selectedGroups, onBetweenFilterIds) => {
      let displayFilters = R.clone(allFilterCategories);
      allFilterCategories.forEach((category, categoryIndex) => {
        category.subFilters.forEach((filter, filterIndex) => {
          if ((selectedFilterIds as unknown as any[]).includes(filter.id)) {
            displayFilters[categoryIndex].subFilters[filterIndex].isSelected = true;
            displayFilters[categoryIndex].hasAFilterSelected = true;
          }

          if ((individualSelectedFilterIds as unknown as any[]).includes(filter.id)) {
            displayFilters[categoryIndex].hasAFilterSelected = true;
          }

          if (filter.id === SharedConstants.filtersCustomDateSubFilterId && filter.isSelected) {
            displayFilters[categoryIndex].hasAFilterSelected = true;
          }
        });

        if (category.savedGroupsCategory && selectedGroups.length > 0) {
          displayFilters[categoryIndex].hasAFilterSelected = true;
        }

        if (category.id === SharedConstants.filtersDateRangeId && onBetweenFilterIds !== SharedConstants.onBetweenDefaultId) {
          displayFilters[categoryIndex].hasAFilterSelected = true;
        }
      });
      return displayFilters;
    });
};

export const getTotalFilterAppliedCount = (page) => {
  return createSelector(getSelectedFiltersByPage(page), getIncludedCustomersByPage(page),
    getExcludedCustomersByPage(page), getSelectedSavedGroupsByPage(page),
    (selectedFilters, includedCustomers, excludedCustomers, selectedSavedGroups) => {
      let appliedFilters = 0;
      if (selectedFilters && selectedFilters.length > 0) {
        appliedFilters = selectedFilters.length;
      }
      if (includedCustomers && includedCustomers.length > 0) {
        appliedFilters = appliedFilters + 1;
      }
      if (excludedCustomers && excludedCustomers.length > 0) {
        appliedFilters = appliedFilters + 1;
      }
      if (selectedSavedGroups && selectedSavedGroups.length > 0) {
        appliedFilters = appliedFilters + selectedSavedGroups.length;
      }
      return appliedFilters;
    }
  )
};

export const getAllEngageSelectedFilterTypes = createSelector(
  getDisplayFilters(AppConstants.engagePage), getEngageSelectedFilters, getEngageIncludedCustomers, getEngageExcludedCustomers,
  getEngageSelectedSavedGroups, getEngageFiltersOnBetweenId, getEngageFiltersStartDate, getEngageFiltersEndDate,
  (allFilters, selectedFilters, includedCustomers, excludedCustomers, selectedSavedGroups, onBetweenId, startDate, endDate) => {
    let formattedSelectedFilters = getFormattedSelectedFilters(allFilters, selectedFilters);

    return {
      selectedFilters: formattedSelectedFilters,
      includedCustomers,
      excludedCustomers,
      selectedSavedGroups,
      onBetweenId,
      startDate,
      endDate
    };
  }
)

export const isMobileWithFiltersCount = (page) => {
  return createSelector(getTotalFilterAppliedCount(page), getIsMobile, (filtersCount, isMobile) => {
    return (filtersCount < 1) && isMobile;
  })
};

export const getView = createSelector(getMeetState, fromMeet.getView);

export const getFeaturedMeetColumns = createSelector(getMeetColumns, getMeetColumnsWithFlag, getView,
  getFeatureEnabled(AppConstants.signatureMembership), getFeatureEnabled(AppConstants.digitalRefundsFlag),
  (meetTableColumns, meetTableColumnsWithFlag, meetView, signatureMembershipEnabled, digitalRefundsEnabled) => {
    let columns = meetView === MeetConstants.allCustomerView && digitalRefundsEnabled ? meetTableColumnsWithFlag : meetTableColumns;
    let membershipColumn: any = R.find(R.propEq('prop', 'membership'))(columns);
    membershipColumn.sort = signatureMembershipEnabled;
    return columns;
  }
);

export const getSortObj = createSelector(getSort, getSortOrder, (prop, order) => ({ prop, order }));

/**
 * Autocomplete state children selectors
 */
export const getAutocompleteCustomers = createSelector(
  getMeetState, fromMeet.getCustomers);

export const getAutocompleteError = createSelector(
  getMeetState, fromMeet.getError);

export const getAutocompleteLoading = createSelector(
  getMeetState, fromMeet.getLoading);

export const getFilterAutocompleteCustomersByPage = (page) => {
  return createSelector(getAutocompleteCustomers, getIncludedCustomersByPage(page), getExcludedCustomersByPage(page),
    (customers, includeFilter, excludeFilter) => {
      let alreadyAddedIds = [];
      if (!R.isEmpty(excludeFilter)) {
        alreadyAddedIds = excludeFilter.map((item) => item.id);
      }
      if (!R.isEmpty(includeFilter)) {
        alreadyAddedIds = alreadyAddedIds.concat(includeFilter.map((item) => item.id));
      }
      let alreadyAdded = (cust) => !alreadyAddedIds.includes(cust.id);

      return R.filter(alreadyAdded, customers);
    });
};

/**
 * Customers state root selector
 */
export const getCustomerState = (state: State) => state.customer;

/**
 * Customer profile state root selector
 */
export const getProfileState = (state: State) => state.profile;

export const getProfileById = (id) => {
  // Until the profile is added to the store, treat it as loading so every section shows its loader
  return createSelector(getProfileState, (profiles) => {
    return profiles[id] ?? fromProfile.profileInitialState;
  });
};

export const getCemLoadingById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.cemData.loading;
    }
  });
};

export const getCemErrorById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.cemData.error;
    }
  });
};

export const getCemDataById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.cemData.data.comments;
    }
  });
};

export const getCaresLoadingById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.caresData.loading;
    }
  });
};

export const getCaresErrorById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.caresData.error;
    }
  });
};

export const getCaresDataById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile && profile.caresData && profile.caresData.data && profile.caresData.data.length) {
      const caresData = profile.caresData.data.map((cares) => {
        const formattedVisitDateTime = moment(cares.visitDateTime).format('ddd, MMM DD YYYY hh:mm A');
        const formattedReportDateTime = moment(cares.reportDateTime).format('ddd, MMM DD YYYY hh:mm A');
        return {
          ...cares,
          visitDateTime: formattedVisitDateTime,
          reportDateTime: formattedReportDateTime
        }
      })
      return caresData;
    }
  });
};

export const getActionHistoryLoadingById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.actionHistory.loading;
    }
  });
};

export const getActionHistoryErrorById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.actionHistory.error;
    }
  });
};

export const getActionHistoryDataById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return R.isEmpty(profile.actionHistory.data) ? [] : profile.actionHistory.data;
    }
  });
};

export const getDigitalRefundsLoadingById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile && profile.digitalRefundsData) {
      return profile.digitalRefundsData.loading;
    }
  });
};

export const getDigitalRefundsErrorById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile && profile.digitalRefundsData) {
      return profile.digitalRefundsData.error;
    }
  });
};

export const getDigitalRefundsDataById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile && profile.digitalRefundsData) {
      return R.isEmpty(profile.digitalRefundsData.data) ? [] : profile.digitalRefundsData.data;
    }
  });
};

export const getRecoverEmailLoadingById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.recoverEmail.loading;
    }
  });
};

export const getRecoverEmailErrorById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.recoverEmail.error;
    }
  });
};

export const getRecoverEmailDataById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.recoverEmail.data;
    }
  });
};

export const getCemByCommentId = (customerId, cemUid) => {
  return createSelector(getProfileById(customerId), (profile) => {
    if (profile && profile.cemData && profile.cemData.data.comments) {
      let cemData = profile.cemData.data;
      let baseComment = R.find(R.propEq('cemUid', cemUid))(cemData.comments);
      return {
        ...baseComment,
        address: cemData.address,
        email: cemData.email,
        name: cemData.name
      };
    }
  });
};

export const getCustomerInfoLoadingById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.customerInformation.loading;
    }
  });
};

export const getCustomerInfoErrorById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.customerInformation.error;
    }
  });
};

export const getCustomerInfoById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.customerInformation.data;
    }
  });
};

export const getCustomerName = (id) => {
  return createSelector(getCustomerInfoById(id), (info) => {
    return (info && !R.isEmpty(info) && info.displayName) ? info.displayName : '';
  });
};

export const getActionMenuLoading = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    return profile ? profile.actionMenuLoading : false;
  });
};

export const getIdMeStatus = (id) => {
  return createSelector(getCustomerInfoById(id), (info) => {
    return info && !R.isEmpty(info) ? info.idMeStatus : null;
  });
};

export const getCustomerNotes = (id) => {
  return createSelector(getCustomerInfoById(id), (info) => {
    return (info && !R.isEmpty(info) && info.notes) ? info.notes : [];
  });
};

export const getDisplayNotes = (id) => {
  return createSelector(getCustomerInfoById(id), (info) => {
    return (info && !R.isEmpty(info) && info.displayNotes) ? info.displayNotes : [];
  });
};

export const getFavoritedLocationFlag = (id) => {
  return createSelector(getCustomerInfoById(id), (info) => {
    return (info && !R.isEmpty(info)) ? info.isFavoriteLocation : false;
  });
};

export const getCustomerCateringObject = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile[ProfileConstants.cateringData];
    }
  });
};

export const getCustomerTransactionStatObject = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile[ProfileConstants.transactionStats];
    }
  });
};

export const getCustomerTransactionHistoryObject = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile[ProfileConstants.transactionHistory];
    }
  });
};

export const getCustomerOffersObject = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile[ProfileConstants.offers];
    }
  });
};

export const getMembershipDetailsObject = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile[ProfileConstants.membershipDetails];
    }
  });
};

export const getCustomerCemObject = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile[ProfileConstants.cemData];
    }
  });
};

export const getDoorDashRecoveryLoadingById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.doorDashRecoveryData.loading;
    }
  });
};

export const getDoorDashRecoveryErrorById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile.doorDashRecoveryData.error;
    }
  });
};

export const getDoorDashRecoveryDataById = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile && profile.transactionHistory && profile.doorDashRecoveryData && profile.doorDashRecoveryData.data
        && profile.transactionHistory.data && profile.transactionHistory.data.transactionHistory && !profile.transactionHistory.error) {
      return Array.from(profile.doorDashRecoveryData.data).map((doorDashRecoverData: any) => {
        let transID = ProfileConstants.idAttributeForTransactionDetails;
        if (doorDashRecoverData[transID]) {
          doorDashRecoverData.isSelectable = true;
          let rowDataIndex =
            R.findIndex(R.propEq(transID, doorDashRecoverData[transID]))(profile.transactionHistory.data.transactionHistory);
          if (rowDataIndex === -1) {
            doorDashRecoverData.isSelectable = false;
          }
        }
        return doorDashRecoverData;
      });
    } else if (profile && profile.transactionHistory && profile.doorDashRecoveryData && profile.doorDashRecoveryData.data
        && profile.transactionHistory.error) {
      return Array.from(profile.doorDashRecoveryData.data).map((doorDashRecoverData: any) => {
        doorDashRecoverData.isSelectable = false;
        return doorDashRecoverData;
      });
    }
  });
};

export const getSortedTransactionDetailsData = (id) => {
  return createSelector(getProfileById(id), (profile) => {
    if (profile) {
      return profile[ProfileConstants.transactionHistory].data.sortedTransactionDetailsData;
    }
  });
};

/**
 * Customers state children selectors
 */
export const getCustomers = createSelector(getCustomerState, fromCustomer.getCustomers);

export const getSelectedCustomers = createSelector(getCustomerState, fromCustomer.getSelectedCustomers);

export const getCustomersError = createSelector(getCustomerState, fromCustomer.getCustomersError);

export const getCustomerActionError = createSelector(getCustomerState, fromCustomer.getCustomerActionError);

export const getNumberOfPages = createSelector(getCustomerState, fromCustomer.getNumberOfPages);

export const getTotalCountOfCustomers = createSelector(getCustomerState, fromCustomer.getTotalCountOfCustomers);

export const getAllCustomerIds = createSelector(getCustomerState, fromCustomer.getAllCustomerIds);

export const getFullCustomerIdList = createSelector(getCustomerState, fromCustomer.getFullCustomerIdList);

export const getLoading = createSelector(getCustomerState, fromCustomer.getLoading);

export const getFormattedFiltersWithDateAndIndividuals = (page) => {
  return createSelector(
    getSelectedFiltersByPage(page),
    getDisplayFilters(page),
    getIncludedCustomersByPage(page),
    getExcludedCustomersByPage(page),
    getFiltersStartDateByPage(page),
    getFiltersEndDateByPage(page),
    getFiltersOnBetweenIdByPage(page),
    getMeetSavedGroupBeingEdited,
    (
      selectedFilters,
      allFilters,
      includedCustomers,
      excludedCustomers,
      startDate,
      endDate,
      onBetweenId,
      meetSavedGroupBeingEdited
    ) => {
      return getAllFormattedSelectedFilters(
        allFilters, selectedFilters, includedCustomers, excludedCustomers, onBetweenId, startDate, endDate, meetSavedGroupBeingEdited
      );
    });
};

export const getCustomerAPIRequestByPage = (page) => {
  return createSelector(
    getMeetState,
    getEngageState,
    getFormattedFiltersWithDateAndIndividuals(page),
    getSelectedSavedGroupsByPage(page),
    (
      meetState: any,
      engageState: any,
      selectedFilters,
      selectedSavedGroups,
    ) => {
      let state = page === AppConstants.meetPage ? meetState : engageState;
      let searchBy = page === AppConstants.meetPage ? state.searchBy : '';
      let searchTerm = page === AppConstants.meetPage ? state.searchTerm : '';
      let limit = page === AppConstants.meetPage ? state.limit : state.filterDisplayCustomerLimit;
      let selectedPage = page === AppConstants.meetPage ? state.page : state.filterDisplayCustomerPage;
      let sort = page === AppConstants.meetPage ? state.sort : state.filterDisplayCustomerSort;
      let sortOrder = page === AppConstants.meetPage ? state.sortOrder : state.filterDisplayCustomerSortOrder;
      let isCatering = page === AppConstants.meetPage ? state.isCatering : false;
      let isFeedback = page === AppConstants.meetPage ? state.isFeedback : false;
      let inactiveFlag = page === AppConstants.meetPage ? state.inactiveFlag : Inactives.EXCLUDE_INACTIVES;

      let customGroupIds = R.flatten(R.pluck('id', selectedSavedGroups));
      let selectedFilterTemp = [...selectedFilters];

      selectedFilters.forEach((filter) => {
        // Remove "Placed an Individual Order" filter from the /customers call if there is no selected button toggle
        if (filter.selectedButtonToggle === undefined && filter.id === SharedConstants.filterPlaceIndividualOrder) {
          let index = R.findIndex(R.propEq('id', filter.id))(selectedFilterTemp);
          selectedFilterTemp.splice(index, 1);
        }
        // Remove "Specify Membership" filter from the /customers call if there is no selected sub option
        if (filter.id === SharedConstants.filterSpecifyMembership) {
          let subOptionSelected = false;
          filter.subOptions.forEach((subOption) => {
            if (subOption.selected === true) {
              subOptionSelected = true;
            }
          });
          if (!subOptionSelected) {
            let index = R.findIndex(R.propEq('id', filter.id))(selectedFilterTemp);
            selectedFilterTemp.splice(index, 1);
          }
        }
      });

      selectedFilters = [...selectedFilterTemp];

      return {
        selectedFilters,
        customGroupIds,
        searchBy,
        searchTerm,
        limit,
        page: selectedPage,
        sort,
        sortOrder,
        isCatering,
        isFeedback,
        inactiveFlag
      };
    });
};

export const getCustomerAPIRequestForAutocompleteByPage = (page) => {
  return createSelector(
    getMeetState,
    getEngageState,
    getFormattedFiltersWithDateAndIndividuals(page),
    getSelectedSavedGroupsByPage(page),
    (
      meetState: any,
      engageState: any,
    ) => {
      let state = page === AppConstants.meetPage ? meetState : engageState;
      let searchBy = MeetConstants.customerNameSearchBy;
      let searchTerm = page === AppConstants.meetPage ? state.searchTerm : '';
      let limit = page === AppConstants.meetPage ? state.limit : state.filterDisplayCustomerLimit;
      let selectedPage = page === AppConstants.meetPage ? state.page : state.filterDisplayCustomerPage;
      let sort = page === AppConstants.meetPage ? state.sort : state.filterDisplayCustomerSort;
      let sortOrder = page === AppConstants.meetPage ? state.sortOrder : state.filterDisplayCustomerSortOrder;

      // state.autocompleteLoading signifies the use of the autocomplete search bar
      limit = state.autocompleteLoading ? 10 : limit;
      searchTerm = state.autocompleteLoading ? state.autocomplete : searchTerm;

      return {
        searchBy,
        searchTerm,
        limit,
        page: selectedPage,
        sort,
        sortOrder
      };
    });
};

export const getCreateGroupRequest = createSelector(
  getFormattedFiltersWithDateAndIndividuals(AppConstants.meetPage), getSelectedSavedGroupsByPage(AppConstants.meetPage),
  (selectedFilters, savedGroups) => {
    let selectedFilterTemp = [...selectedFilters];
    selectedFilters.forEach((filter) => {
    // Remove "Placed an Individual Order" filter from the call to create a saved filter if there is no selected button toggle
      if (filter.selectedButtonToggle === undefined && filter.id === SharedConstants.filterPlaceIndividualOrder) {
        let index = R.findIndex(R.propEq('id', filter.id))(selectedFilterTemp);
        selectedFilterTemp.splice(index, 1);
      }});
    selectedFilters = [...selectedFilterTemp];
    return { selectedFilters, savedGroups };
  });

/**
 * Discover/Engagement state root selector
 * Have together since they are dependent on each other
 */
export const getDiscoverState = (state) => state.discover;

export const getEngagementState = (state) => state.engagement;

/**
 * Discover/Engagement state children selectors
 */
export const getDiscoverTiles = createSelector(getDiscoverState, fromDiscover.getDiscoverTiles);

// Only returns campaign tiles so that they can be displayed as a half card column
export const getCampaignDiscoverTiles = createSelector(getDiscoverTiles, (tiles) => {
  return R.filter((tile) => tile.category === 'campaign', tiles);
});

// Only returns promotion tiles so that they can be displayed as a half card column
export const getPromotionDiscoverTiles = createSelector(getDiscoverTiles, getFeatureEnabled(CalendarConstants.bonusPointPromotionsFlag),
  (tiles, promotionFeatureFlag) => {
    if (!promotionFeatureFlag) {
      return R.filter((tile) => tile.id !== DiscoverConstants.bonusPoint && tile.category === DiscoverConstants.promotion, tiles);
    } else {
      return R.filter((tile) => tile.category === DiscoverConstants.promotion && !tile.automated, tiles);
    }
  });

// Returns an array of 2 item arrays of the automated tiles. This is done so that
// the discover page can display them in a column format since Flexbox is a one dimensional
// layout technology and doesn't allow the mixture of columns and rows needed to display the
// stacked column automated half tiles in the current row layout
export const getAutomatedDiscoverTiles = createSelector(getDiscoverTiles, (tiles) => {
  let automatedTiles = R.filter((tile) => tile.automated && !(tile.stayInPlaceAfterAutomating), tiles);
  let sortedAutomatedTiles = R.sortBy(R.prop('order'), automatedTiles);

  let neededArrays = Math.ceil(sortedAutomatedTiles.length / 2);
  let automatedTileArrays = [];
  for (let i = 0; i < neededArrays; i++) {
    let subArray = sortedAutomatedTiles.slice(i * 2, i * 2 + 2);
    automatedTileArrays.push(subArray);
  }

  return automatedTileArrays;
});

export const getCampaignPromotionTiles = createSelector(
  getPromotionDiscoverTiles, getDiscoverTiles,
  getFeatureEnabled(DiscoverConstants.communityCareEventsFlag),
  getThirdPartyEnabled(), getFeatureEnabled(SharedConstants.MultipleCommunityCareEvents),
  getFeatureEnabled(DiscoverConstants.freeItemInCartPromotionFlag),
  (promotionTiles, discoverTiles,
    communityCareEventsFlag,thirdPartyPromotionFlag,
    multipleCommunityCareEventsEnabled, freeItemInCartEnabled) => {
    let sortedTiles = [];
    let discoverTilesArr = R.filter((tile) =>
      ((tile.category === DiscoverConstants.categoryCampaign ||
          (tile.category === DiscoverConstants.thirdPartyPromotion && thirdPartyPromotionFlag) ||
          (tile.category === DiscoverConstants.categoryCommunityCare && communityCareEventsFlag) ||
          (tile.category === DiscoverConstants.freeItemInCartPromotion && freeItemInCartEnabled))
          && !tile.automated) ||
      (tile.category === DiscoverConstants.categoryCampaign && tile.stayInPlaceAfterAutomating && tile.automated) ||
      (tile.stayInPlaceAfterAutomating && tile.automated), discoverTiles);
    discoverTilesArr = discoverTilesArr.map((tile) =>
      tile.category === DiscoverConstants.categoryCommunityCare
        ? { ...tile, multipleSendsEnabled: multipleCommunityCareEventsEnabled }
        : tile
    );
    // add promotion tile at first level in array
    if (promotionTiles.length > 0) {
      promotionTiles.forEach(tile => {
        discoverTilesArr.unshift(tile);
      });
    }
    sortedTiles = R.sortBy(R.prop('order'), discoverTilesArr);
    let neededArrays = Math.ceil(sortedTiles.length / 2);
    let promotionAndCampaignTileArrays = [];
    for (let i = 0; i < neededArrays; i++) {
      let subArray = sortedTiles.slice(i * 2, i * 2 + 2);
      promotionAndCampaignTileArrays.push(subArray);
    }
    return promotionAndCampaignTileArrays;
  });

// Only returns non-automated and non-campaign tiles
export const getGeneralDiscoverTiles = createSelector(getDiscoverTiles, (tiles) => {
  const isGeneralTile = tile => (
    (tile.category !== 'campaign' && tile.category !== 'banner' && tile.category !== 'error' &&
     tile.category !== 'promotion' && tile.category !== DiscoverConstants.categoryCommunityCare
        && tile.category !== DiscoverConstants.thirdPartyPromotion && tile.category != DiscoverConstants.freeItemInCartPromotion)
    && !tile.automated
  );
  return R.filter(isGeneralTile, tiles);
});

export const getBannerTile = createSelector(getDiscoverTiles, (tiles) => {
  let bannerTiles = R.filter((tile) => tile.type === 'text' && tile.category === 'banner', tiles);
  return bannerTiles[0];
});

export const getErrorTile = createSelector(getDiscoverTiles, (tiles) => {
  let errorTile = R.filter((tile) => tile.type === 'text' && tile.category === 'error' , tiles);
  return errorTile[0];
});

export const getDiscoverLoading = createSelector(getDiscoverState, fromDiscover.getDiscoverLoading);

export const getDiscoverError = createSelector(getDiscoverState, fromDiscover.getDiscoverError);

export const getEngagementEmail = createSelector(getEngagementState, fromEngagement.getEngagementEmail);

export const getCurrentEngagementId = createSelector(getEngagementState, fromEngagement.getEngagementFlowId);

export const getDiscoverTile = (tileId) => {
  return createSelector(
    getDiscoverTiles,
    (tiles) => tiles.find(tile => tile.id === tileId) || {});
}

export const getDiscoverTileById = createSelector(
  getDiscoverState,
  getCurrentEngagementId,
  (discover, id) => discover.tiles[id] || {});

export const getEngagementLoading = createSelector(getEngagementState,
  fromEngagement.getEngagementLoading);

export const getEngagementCount = createSelector(getEngagementState,
  fromEngagement.getEngagementCount);

export const getEngagementRewards = createSelector(getEngagementState,
  fromEngagement.getEngagementRewards);

export const getCategorizedEngagementRewards = createSelector(
  getEngagementRewards,
  (rewards) => {
    let groupedRewards = R.groupBy(R.prop('treatCategory'), rewards);
    let categories = R.keys(groupedRewards);
    return categories.map((category) => {
      return { category, rewards: groupedRewards[category] };
    });
  });

export const getEngagementMenuItems = createSelector(getEngagementState,
  fromEngagement.getEngagementMenuItems);

export const getIsEngagementRewardsAvailable = createSelector(
  getCategorizedEngagementRewards,
  (rewardCategories) => {
    return rewardCategories && rewardCategories[0] && (rewardCategories[0].rewards.length > 1 || rewardCategories.length > 1);
  });

export const getEngagementType = createSelector(getEngagementState,
  fromEngagement.getEngagementType);

export const getEngagementMode = createSelector(getEngagementState,
  fromEngagement.getEngagementMode);

export const getEngagementIncludeActiveTreats = createSelector(getEngagementState,
  fromEngagement.getIncludeActiveTreats);

export const getEngagementError = createSelector(getEngagementState,
  fromEngagement.getEngagementError);

export const getEngagementCampaignName = createSelector(getEngagementState,
  fromEngagement.getEngagementCampaignName);

export const getEngagementAllDaysBlocked = createSelector(getEngagementState,
  fromEngagement.getEngagementAllDaysBlocked);

export const getEngagementOrganizations = createSelector(getEngagementState, fromEngagement.getEngagementOrganizations);

export const getEngagementOptInOptions = createSelector(getEngagementState, fromEngagement.getEngagementOptInOptions);

export const getEngagementOptInOptionButtons = createSelector(getEngagementOptInOptions,
  (optInOptions) => {
    if (optInOptions) {
      optInOptions.forEach(toggle => toggle.selected = toggle.defaultToggle);
      return optInOptions;
    }
  });

export const getCurrentCommunityCareEvents = createSelector(getEngagementState, fromEngagement.getCurrentCommunityCareEvents);

export const getSortedAndShortenedCommunityCareEvents = createSelector(getCurrentCommunityCareEvents, (events) => {
  events.sort((a, b) => {
    return moment.unix(a.start).isBefore(moment.unix(b.start)) ? -1 : 1;
  });
  return events.slice(0, 5);
});

export const getEngagementPlatforms = createSelector(getEngagementState, fromEngagement.getEngagementPlatforms);

export const getEngagementCustomerTypes = createSelector(getEngagementState, fromEngagement.getEngagementCustomerTypes);

export const getEngagementSelectedOptIn = createSelector(getEngagementState, fromEngagement.getEngagementSelectedOptIn);

export const getEngagementMessage = createSelector(getEngagementState, fromEngagement.getEngagementMessage);

export const getEngagementStrippedMessage = createSelector(getEngagementMessage,
  (copy) => {
    let newCopy = copy ? copy.replace(/<[^>]*>?/gm, '') : '';
    return newCopy.replace(/&nbsp;/gm, ' ');
  });
//
export const getEngagementDefaultMessageForApp = createSelector(getEngagementState,
  fromEngagement.getEngagementDefaultMessageForApp);

export const getEngagementPushMessageTitle = createSelector(getEngagementState,
  fromEngagement.getEngagementPushMessageTitle);

export const getEngagementPushMessageText = createSelector(getEngagementState,
  fromEngagement.getEngagementPushMessageText);

export const getEngagementSubject = createSelector(getEngagementState,
  fromEngagement.getEngagementSubject);

export const getEngagementStartDate = createSelector(getEngagementState,
  fromEngagement.getStartDate);

export const getEngagementEndDate = createSelector(getEngagementState,
  fromEngagement.getEndDate);

export const getEngagementMaxDaysAllowed = createSelector(getEngagementState,
  fromEngagement.getMaxDaysAllowed);

export const getEngagementDisabledDays = createSelector(getEngagementState,
  fromEngagement.getDisabledDays);

export const getEngagementFailedPlatforms = createSelector(
  getEngagementState, fromEngagement.getEngagementFailedPlatforms);

export const getEngagementSuccessfulPlatforms = createSelector(
  getEngagementState, fromEngagement.getEngagementSuccessfulPlatforms);

export const getEngagementThirdPartyPromoCreatedId = createSelector(
  getEngagementState, fromEngagement.getEngagementThirdPartyPromoCreatedId);

export const getEngagementDisabledTimes = createSelector(getEngagementState, fromEngagement.getDisabledTimes);

export const getEngagementCCEModalAlreadyShown = createSelector(getEngagementState, fromEngagement.getCCEModalAlreadyShown);

export const getEngagementEndDateRestriction = createSelector(getEngagementState,
  fromEngagement.getEndDateRestriction);

export const getEngagementSelectedCustomers = createSelector(getEngagementState,
  fromEngagement.getEngagementSelectedCustomers);

export const getEngagementDetailRequest = createSelector(getEngagementState,
  fromEngagement.getEngagementDetailRequest);

export const getEngagementLength = createSelector(getEngagementState,
  fromEngagement.getTreatLength);

export const getEngagementSelectedReward = createSelector(getEngagementState,
  fromEngagement.getSelectedReward);

export const getEngagementDestinationOptions = createSelector(getEngagementState,
  fromEngagement.getDestinationOptions);

export const getEngagementTimeOfDayOptions = createSelector(getEngagementState,
  fromEngagement.getTimeOfDayOptions);

export const getEngageRepeatPromotionOptions = createSelector(getEngagementState,
  fromEngagement.getRepeatThisPromotion);

export const getEngagementSelectedDestinationId = createSelector(getEngagementState,
  fromEngagement.getSelectedDestinationId);

export const getEngagementSelectedDestinationIdsForMultiSelect = createSelector(getEngagementState,
  fromEngagement.getSelectedDestinationIdsForMultiSelect);

export const getEngagementSelectedTimeOfDay = createSelector(getEngagementState,
  fromEngagement.getSelectedTimeOfDay);

export const getEngagementSendError = createSelector(getEngagementState,
  fromEngagement.getSendError);

export const getEngagementSendLoading = createSelector(getEngagementState,
  fromEngagement.getSendLoading);

export const getEngagementAutomationAction = createSelector(getEngagementState,
  fromEngagement.getAutomationAction);
export const getEngagementSelectedRepeatPromotion = createSelector(getEngagementState,
  fromEngagement.getSelectedRepeatPromotion);

export const getEngageWeeklyButtonsRepeatPromotion = createSelector(getEngageRepeatPromotionOptions, getEngagementState,
  (repeatPromotions, engagement) => {
    if (!repeatPromotions) {
      return;
    }

    let subOptions: SubToggleOption[] = [];
    repeatPromotions.forEach((repeatPromotion: RepeatToggleOption) => {
      if (repeatPromotion.id === SharedConstants.weeklyRepeatPromotion) {
        subOptions = repeatPromotion.subOptions || [];
      }
    });

    return subOptions.map((toggle: SubToggleOption) => {
      const isSelected = engagement.selectedRepeatId === SharedConstants.weeklyRepeatPromotion
        ? toggle.id === engagement.repeatPromotionSubId
        : !!toggle.defaultToggle;

      return {
        ...toggle,
        selected: isSelected
      };
    });
  }
);

export const getEngageRepeatPromotionButtons = createSelector(getEngageRepeatPromotionOptions, getEngagementSelectedRepeatPromotion,
  (repeatPromotions, selectedRepeatPromotion) => {
    if (!repeatPromotions) {
      return;
    }

    return repeatPromotions.map((repeatPromotion: RepeatToggleOption) => ({
      ...repeatPromotion,
      selected: repeatPromotion.id === selectedRepeatPromotion
    }));
  }
);

export const getEngagementStartTime = createSelector(getEngagementState,
  fromEngagement.getStartTime);

export const getEngagementEndTime = createSelector(getEngagementState,
  fromEngagement.getEndTime);

export const getEngagementStartTimeRestriction = createSelector(getEngagementState,
  fromEngagement.getStartTimeRestriction);

export const getEngagementStartTimeEndRestriction = createSelector(getEngagementState,
  fromEngagement.getStartTimeEndRestriction);

export const getEngagementEndTimeRestriction = createSelector(getEngagementState,
  fromEngagement.getEndTimeRestriction);

export const getEngagementEndTimeEndRestriction = createSelector(getEngagementState,
  fromEngagement.getEndTimeEndRestriction);

export const getEngagementDatesAndTimes = createSelector(getEngagementStartDate, getEngagementEndDate, getEngagementStartTime,
  getEngagementEndTime, getEngagementStartTimeRestriction, getEngagementEndTimeRestriction, getEngagementEndDateRestriction,
  (startDate, endDate, startTime, endTime, startTimeRestriction, endTimeRestriction, endDateRestriction) => {
    return { startDate, endDate, startTime, endTime, startTimeRestriction, endTimeRestriction, endDateRestriction };
  })

export const getEngagementAreDatesWithinSevenDays = createSelector(getEngagementState,
  fromEngagement.getAreDatesWithinSevenDays);

export const getOverlappingEngagementType = createSelector(getEngagementState,
  fromEngagement.getOverlappingEngagementType);

export const getReplacedOnePageFlowHtml = createSelector(getEngagementState, (eng: fromEngagement.State) => {
  let merge;
  const html = eng.previewHtml;

  // Only show countdown for rewards that are longer than 1 day and end more than 1 day away
  // Discover rewards always start on the current day so only treatLength needs to be checked
  // Currently we require all Discover rewards to be at least 7 days, but keeping this logic here in
  // case we change that requirement in the future to allow for 1 day Discover rewards/automation
  const showCountdownTimer = eng.treatLength > 1 ? '' : 'style="display: none;"';

  if ((eng.engagementType === DiscoverConstants.reward || eng.engagementType === DiscoverConstants.automation) && eng.selectedReward) {
    merge = {
      ...eng.selectedReward,
      offerDetails: eng.message,
      showCountdownTimer: showCountdownTimer,
      subTitle: eng.subTitle,
      endDate: eng.endDate
    };
  } else if (eng.engagementType === DiscoverConstants.email && eng.email) {
    merge = {
      ...eng.email,
      description: eng.message
    };
  }

  // Don't process html if it is a promotion or community care with static images
  if ((eng.engagementType !== DiscoverConstants.promotion) &&
    (eng.engagementType !== DiscoverConstants.categoryCommunityCare)
      && (eng.engagementType !== DiscoverConstants.thirdPartyPromotion) &&
    eng.engagementType !== DiscoverConstants.freeItemInCartPromotion) {
    return replaceMGHtml(merge, html);
  } else {
    return '';
  }
});

export const getIsEndDateRestriction = createSelector(getEngagementEndDateRestriction, getEngagementState,
  (engagementEndDate,
    engagementState) => {
    if (engagementEndDate) {
      let endDate = moment().add(engagementState.treatLength, 'd');
      let restrictDate = moment(engagementEndDate);
      const diff = restrictDate.diff(endDate, 'days');
      return diff < 0;
    }
    return false;
  });

export const getEngagementRewardRequest = createSelector(getEngagementState, getActiveRewardsState,
  (engagement, activeRewards) => {
    let treats = engagement.treats.map((treat) => {
      const offerRecipients = getTreatRecipients(activeRewards, treat);
      const hasMessage = R.find(R.propEq('id', 'description'))(engagement.flow);
      return {
        ...treat,
        campaignName: engagement.campaignName,
        customizedMessage: hasMessage ? engagement.message : treat.customizedMessage,
        endDateLiteral: moment(engagement.endDate).format('YYYY-MM-DD'),
        offerDetails: hasMessage ? engagement.message : treat.offerDetails,
        offerRecipients,
        startDateLiteral: moment(engagement.startDate).format('YYYY-MM-DD')
      };
    }).filter((treat) => treat.offerRecipients.length > 0);

    return {
      recommendationType: engagement.flowId,
      treats
    };
  });

export const getOnePageRewardRequest = createSelector(getEngagementState, getEngagementCount,
  (engagement, customerCount) => {
    let treats = [{
      ...engagement.selectedReward,
      campaignName: engagement.campaignName,
      customizedMessage: engagement.message,
      endDateLiteral: engagement.endDate.format('YYYY-MM-DD'),
      headline: engagement.subTitle,
      offerDetails: engagement.message,
      offerRecipients: engagement.offerRecipients.slice(0, customerCount),
      startDateLiteral: engagement.startDate.format('YYYY-MM-DD'),
      subject: engagement.subject,
      themeId: engagement.themeId,
      treatMessageForApp: engagement.defaultMessageForApp
    }];

    return {
      recommendationType: engagement.flowId,
      treats
    };
  });

export const getOnePageEmailRequest = createSelector(getEngagementState, (engagement) => {
  return {
    ...engagement,
    campaignName: engagement.campaignName,
    engagementType: engagement.engagementType,
    flowId: engagement.flowId,
    subject: engagement.subject,
    emailMessage: engagement.message
  };
});

export const getOnePageThirdPartyPromotionRequest = createSelector(
  getEngagementState, (engagement) => {
    let platforms = R.filter((platform: any) => platform.selected, engagement.platforms as any[])
      .map((platform: any) => { return platform.id; })
    let customerType = R.filter((type: any) => type.selected, engagement.availableCustomerTypes as any[]).map((type: any) => {
      return type.displayTitle.toUpperCase();
    });

    let startDate = moment().isSame(R.clone(engagement.startDate), 'day') ? moment()
      : R.clone(engagement.startDate).set({ hour:0, minute:0 });
    let endDate = R.clone(engagement.endDate).set({ hour:23, minute:59 })

    return {
      campaignName: engagement.campaignName,
      engagementType: engagement.engagementType,
      flowId: engagement.flowId,
      startDate: startDate.utc().unix()*1000,
      endDate: endDate.utc().unix() * 1000,
      currencyCode: engagement.currencyCode,
      selectedReward: engagement.selectedReward,
      offerTemplateId: engagement.selectedReward.offerTemplateId,
      customerType,
      platforms
    };
  });

export const getOnePageFreeInCartRequest = createSelector(getEngagementState, (engagement) => {
  let customerType = R.filter((type: any) => type.selected, engagement.availableCustomerTypes as any[]).map((type: any) => {
    return type.id;
  });

  let startDate = moment().isSame(R.clone(engagement.startDate), 'day')
    ? moment() : R.clone(engagement.startDate).set({ hour: 0, minute: 0 });
  let endDate = R.clone(engagement.endDate).set({ hour: 23, minute: 59 })
  let repeatOption = {
    repeatOptionId: engagement.selectedRepeatId,
    subOptions: [{ id: engagement.repeatPromotionSubId, name: engagement.repeatPromotionSubId }]
  }

  return {
    campaignName: engagement.campaignName,
    customerType,
    destinationIds: engagement.selectedDestinationIdsForMultiSelect,
    endDate: endDate.unix() * 1000,
    endTime: engagement.endTime,
    engagementType: engagement.engagementType,
    flowId: engagement.flowId,
    offerTemplateId: engagement.selectedReward.offerTemplateId,
    repeatOption,
    selectedReward: engagement.selectedReward,
    timeOfDayId: engagement.selectedTimeOfDayId,
    startDate: startDate.unix() * 1000,
    startTime: engagement.startTime
  };
});

export const getOnePagePromotionRequest = createSelector(getEngagementState, getSelectedLocation, (engagement, selectedLocation) => {
  let selectedRepeatOption = engagement.repeatOptions.filter(option => option.id === engagement.selectedRepeatId);
  let selectedSubOptions = selectedRepeatOption[0].subOptions;
  let selectedRepeatSubOption = null;

  if (selectedSubOptions && selectedSubOptions.length > 0) {
    selectedRepeatSubOption = selectedSubOptions.filter(subOption => subOption.id === engagement.repeatPromotionSubId);
  }

  let repeatOption = {
    repeatOptionId: engagement.selectedRepeatId,
    subOptions: selectedRepeatSubOption
  }

  const locationTimeZone = selectedLocation?.timeZone;
  // Normalize moment instants to location timezone before deriving day literals.
  const startMomentForLiteral = locationTimeZone
    ? R.clone(engagement.startDate).tz(locationTimeZone)
    : R.clone(engagement.startDate);
  const endMomentForLiteral = locationTimeZone
    ? R.clone(engagement.endDate).tz(locationTimeZone)
    : R.clone(engagement.endDate);
  const startDateLiteral = startMomentForLiteral.format('YYYY-MM-DD');
  const endDateLiteral = endMomentForLiteral.format('YYYY-MM-DD');
  const nowInLocation = locationTimeZone ? moment.tz(locationTimeZone) : moment();
  const isStartDateTodayInLocation = startDateLiteral === nowInLocation.format('YYYY-MM-DD');

  let startDate;
  if (isStartDateTodayInLocation) {
    startDate = nowInLocation;
  } else if (locationTimeZone) {
    startDate = moment.tz(startDateLiteral, 'YYYY-MM-DD', locationTimeZone).startOf('day');
  } else {
    startDate = R.clone(engagement.startDate).set({ hour:0, minute:0, second: 0, millisecond: 0 });
  }

  let endDate;
  if (locationTimeZone) {
    endDate = moment.tz(endDateLiteral, 'YYYY-MM-DD', locationTimeZone).set({ hour:23, minute:59, second: 0, millisecond: 0 });
  } else {
    endDate = R.clone(engagement.endDate).set({ hour:23, minute:59, second: 0, millisecond: 0 });
  }

  return {
    campaignName: engagement.campaignName,
    engagementType: engagement.engagementType,
    destinations: engagement.selectedDestinationIdsForMultiSelect,
    timeOfDayId: engagement.selectedTimeOfDayId,
    flowId: engagement.flowId,
    startDate: startDate.unix()*1000,
    endDate: endDate.unix() * 1000,
    startTime: engagement.startTime,
    endTime: engagement.endTime,
    repeatOption
  };
});

export const getOnePageCommunityEventRequest = createSelector(getEngagementState , (engagement) =>{
  let formattedEndDate = engagement.endDate.format('YYYY-MM-DD') + ' ' + moment(engagement.endTime, 'h:mm a').format('HH:mm:ss');
  let formattedStartDate = engagement.startDate.format('YYYY-MM-DD') + ' ' + moment(engagement.startTime, 'h:mm a').format('HH:mm:ss');
  let startDate: moment.Moment = moment(formattedStartDate);
  let endDate = moment(formattedEndDate)

  // Trim whitespace from any organization's name
  if (engagement.organizations) {
    engagement.organizations.forEach((organization, index) => {
      engagement.organizations[index] = {
        ...engagement.organizations[index],
        organizationName: organization.organizationName.trim()
      }
    });
  }

  return {
    campaignName: engagement.campaignName,
    optingIn: engagement.selectedOptIn === 'organizationCode',
    engagementType: engagement.engagementType,
    flowId: engagement.flowId,
    startDate: startDate.isValid() ? startDate.unix() * 1000 : null,
    endDate: endDate.isValid() ? endDate.unix() * 1000 : null,
    startTime: engagement.startTime,
    endTime: engagement.endTime,
    engagementOrganizations: engagement.organizations,
  };
})

export const getAutomationTreatRequest = createSelector(getEngagementState,
  (engage) => {
    return {
      awardId: engage.selectedReward ? engage.selectedReward.awardId : '',
      treatValidityDays: engage.treatLength,
      campaignName: engage.campaignName,
      emailSubject: engage.subject,
      emailText: engage.message,
      emailHeadline: engage.subTitle,
      headline: engage.subTitle,
      mobileHeaderImage: engage.mobileImage,
      treatMessageForApp: engage.defaultMessageForApp,
      includeActiveTreats: engage.includeActiveTreats,
      id: engage.flowId
    };
  });

function getTreatRecipients(activeTreats, treat) {
  if (activeTreats.includeActiveCustomers) {
    return treat.offerRecipients;
  } else {
    return treat.offerRecipients.filter((recipient) =>
      !R.includes(recipient.customerUid, activeTreats.activeTreatCustomers));
  }
}

/**
 * Automations state root selector
 */
export const getAutomationState = (state) => state.automations;

/**
 * Automations state children selectors
 */
export const getSelectedAutomation = createSelector(getAutomationState, fromAutomations.getSelectedAutomation);

export const getAutomationMode = createSelector(getAutomationState, fromAutomations.getMode)

export const getAutomationTemplateLoading = createSelector(getAutomationState, fromAutomations.getTemplateLoading);

export const getAutomationTemplateError = createSelector(getAutomationState, fromAutomations.getTemplateError);

export const getSelectedAutomationCreationDetails = createSelector(getAutomationState,
  fromAutomations.getSelectedAutomationCreationDetails);

export const getAutomationTemplateCampaignCategories = createSelector(getSelectedAutomation, (selectedAutomation: SelectedAutomation ) => {
  const campaignTemplates: TemplateDetails[] | undefined = selectedAutomation?.automationCampaignDetails?.campaignTemplates;
  if (!campaignTemplates) {
    return [];
  }
  const templatesMap: Map<string, TemplateDetails[]> = new Map<string, TemplateDetails[]>();
  for (let template of campaignTemplates) {
    if (!template.category) {
      continue;
    }
    if (templatesMap.has(template.category)) {
      templatesMap.get(template.category).push(template)
      continue;
    }
    templatesMap.set(template.category, [template])
  }
  const groupedTemplates: { category: string; templates: TemplateDetails[] }[] = [];
  for (let [key, value] of templatesMap) {
    groupedTemplates.push({ category: key, templates: value })
  }
  return groupedTemplates;
});

export const getAutomationHtml = createSelector(getSelectedAutomation, (selectedAutomation: SelectedAutomation) => {
  if (!selectedAutomation?.automationCampaignDetails) {
    return '';
  }
  const customizations = selectedAutomation.customizations;
  const automationCampaignDetails: AutomationCampaignDetails = selectedAutomation.automationCampaignDetails;

  if (!automationCampaignDetails.campaignTemplates || !automationCampaignDetails.selectedCampaignTemplateID) {
    return '';
  }
  const templateDetails: TemplateDetails | undefined = automationCampaignDetails.campaignTemplates
    .find(template => template.id === automationCampaignDetails.selectedCampaignTemplateID);
  if (!templateDetails?.notificationLayouts?.[0]) {
    return '';
  }
  const notificationLayout = templateDetails.notificationLayouts[0];
  if (!notificationLayout.html) {
    return '';
  }
  const theme = notificationLayout.themes?.find(theme => theme.id === customizations.themeID);
  let replacements: { [key: string]: string } = {
    '.Theme.PreHeader': theme?.preHeader ?? '',
    '.Theme.HeaderImage.DesktopURL': theme?.headerImage?.desktopURL ?? '',
    '.Theme.HeaderImage.MobileURL' : theme?.headerImage?.mobileURL ?? '',
    '.User.FirstName': '[First Name]',
    '.Theme.EmailBody': customizations.body,
    '.RecruitmentVisibility': customizations.includeRecruitment ? '' : 'style="display:none;"',
    '.EndDatetime': moment().add(automationCampaignDetails.durationDays, 'days').format('MM/DD/YYYY')
  };
  const fallbackDesktopImage = theme?.heroImage?.desktopURL ?? templateDetails.heroImages?.[0]?.desktopURL ?? '';
  const fallbackMobileImage = theme?.heroImage?.mobileURL ?? templateDetails.heroImages?.[0]?.mobileURL ?? '';
  replacements['.Theme.HeroImage.DesktopURL'] = customizations?.heroImage?.desktopURL ?? fallbackDesktopImage;
  replacements['.Theme.HeroImage.MobileURL'] = customizations?.heroImage?.mobileURL ?? fallbackMobileImage;
  replacements['.Theme.CtaButtonImage.DesktopURL'] = theme?.ctaButtonImage?.desktopURL ?? '';
  replacements['.Theme.CtaButtonImage.MobileURL'] = theme?.ctaButtonImage?.mobileURL ?? '';
  let html = notificationLayout.html.replaceAll(/\[\[\s*([\w.]+)\s*]]/g, (match, key) => {
    return key in replacements ? replacements[key] : match;
  });
  html = html.replaceAll('id="tableWrapper"', '');
  return html;
})

export const getAutomationConfirmation = (type, campaignType)=> {
  return createSelector(getSelectedAutomation, getSelectedAutomationCreationDetails, getIsMobile,
    (automation: SelectedAutomation, automationCreationDetails, isMobile) => {
      let headingMessage = `Yay! Your ${campaignType?.toLowerCase() ?? 'automation'} has been ` +
      `${type?.toLowerCase() ?? 'updated'} successfully!`;
      let subText = type === ActionType.Stop ?
        '' :
        `Please wait up to 15 minutes after email sending completes to see statistics for this campaign.`
      if (automationCreationDetails.selectedAutomationError) {
        headingMessage = 'Oh no! Looks like something went wrong.';
        subText = resolveCampaignSendErrorMessage(automationCreationDetails.selectedAutomationError)
      }
      return {
        loading: automationCreationDetails.selectedAutomationLoading,
        error: automationCreationDetails.selectedAutomationError,
        isMobile,
        headingMessage,
        subText,
        campaignID: automation.automationID,
        type: campaignType,
        actionType: type,
        discover: true
      };
    });
}

/**
 * Care state root selector
 */
export const getCareState = (state) => state.care;

/**
 * Care state children selectors
 */
export const getCasesList = createSelector(getCareState, fromCare.getCasesList);

export const getStatusOptions = createSelector(getCareState, fromCare.getStatusOptions);

export const getCareSelectedCustomer = createSelector(getCareState, fromCare.getSelectedCustomer);

export const getSelectedCareReport = createSelector(getCareState, fromCare.getSelectedReport);

export const getSelectedCareRewards = createSelector(getCareState, fromCare.getSelectedRewards);

export const getCareServiceNowCase = createSelector(getCareState, fromCare.getServiceNowCase);

export const getCareServiceNowCaseForReassign = createSelector(getCareServiceNowCase, (report) => {
  // Reformat the dates back to how CLRS expects them: 'yyyy-MM-DD HH:mm:ss'
  const dateFormat = 'yyyy-MM-DD HH:mm:ss';
  const assignmentDate = report.assignmentDate ? moment(report.assignmentDate).format(dateFormat) : '';
  const contactDate = report.contactDate ? moment(report.contactDate).format(dateFormat) : '';
  const lastAssignmentDate = report.lastAssignmentDate ? moment(report.lastAssignmentDate).format(dateFormat) : '';
  const lastContactDate = report.lastContactDate ? moment(report.lastContactDate).format(dateFormat) : '';
  const incidentDate = report.incidentDate ? moment(report.incidentDate).format(dateFormat) : '';
  return { ...report, assignmentDate, contactDate, lastAssignmentDate, lastContactDate, incidentDate };
});

export const getCareServiceNowCaseError = createSelector(getCareState, fromCare.getServiceNowCaseError);

export const getCareServiceNowCaseLoading = createSelector(getCareState, fromCare.getServiceNowCaseLoading);

export const getCareTemplateHtml = createSelector(getCareState, fromCare.getTemplateHtml);

export const getCareCopy = createSelector(getCareState, fromCare.getCopy);

export const getCareDefaultCopy = createSelector(getCareState, fromCare.getDefaultCopy);

export const getCareThemes = createSelector(getCareState, fromCare.getThemes);

export const getCareThemeName = createSelector(getCareState, fromCare.getThemeName);

export const getCareDefaultThemeName = createSelector(getCareState, fromCare.getDefaultThemeName);

export const getCareHeadline = createSelector(getCareState, fromCare.getHeadline);

export const getCarePreHeader = createSelector(getCareState, fromCare.getPreHeader);

export const getCareThemeUrl = createSelector(getCareState, fromCare.getThemeUrl);

export const getCareSelectedView = createSelector(getCareState, fromCare.getCareSelectedView);

export const getRecoveredSelectedView = createSelector(getCareState, fromCare.getRecoveredSelectedView)

export const getClosedCareSelectedView = createSelector(getCareState, fromCare.getClosedCareSelectedView)

export const getCfaOneSelectedView = createSelector(getCareState, fromCare.getCfaOneSelectedView)

export const getCareOpeningStatement = createSelector(getCareState, fromCare.getOpeningStatement);

export const getCareSubject = createSelector(getCareState, fromCare.getSubject);

export const getCareDateSent = createSelector(getCareState, fromCare.getDateSent);

export const getCareRewardCategories = createSelector(getCareState, fromCare.getRewardCategories);

export const getFormattedCareRewardCategories = createSelector(getCareRewardCategories, getSelectedCareRewards,
  (rewardCategories, selectedRewards) => {
    // Set correct count on the rewards based on user selected rewards
    rewardCategories.forEach((rewardCategory, categoryIndex) => {
      rewardCategory.rewards.forEach((reward, rewardIndex) => {
        let selectedRewardIndex = R.findIndex(R.propEq('id', reward.id))(selectedRewards);
        if (selectedRewardIndex > -1) {
          rewardCategories[categoryIndex].rewards[rewardIndex] = {
            ...reward,
            count: selectedRewards[selectedRewardIndex].count
          };
        } else {
          rewardCategories[categoryIndex].rewards[rewardIndex] = { ...reward, count: 0 };
        }
      })
    })
    return rewardCategories.sort((a,b) => a.categoryName.includes('LTO') ? -1 : 1);
  });

export const getLimitedCareRewardDates = createSelector(getSelectedCareRewards,
  (selectedRewards) => {
    let limitedCareRewardDates = [];
    let rewardStartDate = moment().format('MM/DD/YYYY');
    let rewardEndDate = moment().add('90', 'days').format('MM/DD/YYYY');

    selectedRewards.forEach(reward => {
      reward.rewardStartDate = rewardStartDate;
      reward.rewardEndDate = rewardEndDate;
      if (moment(reward.endDateRestriction) < moment(rewardEndDate) && reward.limitedtimeoffer) {
        reward.rewardEndDate = reward.endDateRestriction;
      }
      limitedCareRewardDates.push({ startDate: reward.rewardStartDate, endDate: reward.rewardEndDate });
    });

    // remove duplicates values
    limitedCareRewardDates = limitedCareRewardDates.filter((value, index, self) =>
      index === self.findIndex((t) => (t.endDate === value.endDate))
    );

    return limitedCareRewardDates;
  });

export const getCareTemplateLoading = createSelector(getCareState, fromCare.getTemplateLoading);

export const getCareTemplateError = createSelector(getCareState, fromCare.getTemplateError);

export const getIncludeReward = createSelector(getCareState, fromCare.getIncludeReward);

export const getMemberType = createSelector(getCareState, fromCare.getMemberType);

export const getCarePage = createSelector(getCareState, fromCare.getPage);

export const getCarePageSize = createSelector(getCareState, fromCare.getPageSize);

export const getCareSortBy = createSelector(getCareState, fromCare.getSortBy);

export const getCareSortOrder = createSelector(getCareState, fromCare.getSortOrder);

export const getCareLoading = createSelector(getCareState, fromCare.getLoading);

export const getCareRecoverEligibilityLoading = createSelector(getCareState, fromCare.getRecoverEligibilityLoading);

export const getCareError = createSelector(getCareState, fromCare.getError);

export const getCareSearchBy = createSelector(getCareState, fromCare.getSearchBy);

export const getCareSearchTerm = createSelector(getCareState, fromCare.getSearchTerm);

export const getCareCustomerNotes = createSelector(getCareState, fromCare.getCareCustomerNotes);

export const getCareCustomerNotesToDisplay = createSelector(getCareState, fromCare.getCareCustomerNotesToDisplay);

export const getCareCustomerNotesLoading = createSelector(getCareState, fromCare.getCareCustomerNotesLoading);

export const getCareCustomerNotesError = createSelector(getCareState, fromCare.getCareCustomerNotesError);

export const getNoSearchResultMessage = createSelector(getCareSearchTerm, (searchTerm) => {
  if (searchTerm != null) {
    return CareConstants.noSearchResultMessage + ' ' + searchTerm + '.';
  } else {
    return CareConstants.noSearchResultMessage;
  }
});

export const getNoResultsMessage = createSelector(getNoSearchResultMessage, getCareSearchTerm,
  (noSearchResultMessage, searchTerm) => {
    if (searchTerm) {
      return noSearchResultMessage;
    } else {
      return CareConstants.noResultsMessage;
    }
  });

export const getSpotlightContactsSearchBy = createSelector(getCareState, fromCare.getSpotlightContactsSearchBy);

export const getSpotlightContactsSearchTerm = createSelector(getCareState, fromCare.getSpotlightContactsSearchTerm);

export const getCareDisplayErrorText = createSelector(getCareState, fromCare.getSpotlightContactsDisplayErrorText);

export const getSpotlightContactsPageLimit = createSelector(getCareState, fromCare.getSpotlightContactsPageLimit);

export const getSpotlightContactsPage = createSelector(getCareState, fromCare.getSpotlightContactsPage);

export const getSpotlightContactsTotalCountOfCustomers = createSelector(getCareState, fromCare.getSpotlightContactsTotalCountOfCustomers);

export const getSpotlightContactsCustomers = createSelector(getCareState, fromCare.getSpotlightContactsCustomers);

export const getSpotlightContactsLoading = createSelector(getCareState, fromCare.getSpotlightContactsLoading);

export const getSpotlightContactsNumberOfPages = createSelector(getCareState, fromCare.getSpotlightContactsNumberOfPages);

export const getSpotlightContactsError = createSelector(getCareState, fromCare.getSpotlightContactsError);

export const getRecoveredCfaOneContactsTotalElements = createSelector(getCareState, fromCare.getRecoveredCfaOneContactsTotalElements);

export const getRecoveredCfaOneContacts = createSelector(getCareState, fromCare.getRecoveredCfaOneContacts);

export const spotlightContactsSelectedCategoryTitle = createSelector(
  getSpotlightContactsSearchBy, (searchBy) => {
    return R.find(R.propEq('value', searchBy))(CareConstants.spotlightContactsSearchByCategories).title;
  }
)

export const getSpotlightContactsSortBy = createSelector(getCareState, fromCare.getSpotlightContactsSortBy);

export const getSpotlightContactsSortOrder = createSelector(getCareState, fromCare.getSpotlightContactsSortOrder);

export const getSpotlightContactsSortObj = createSelector(
  getSpotlightContactsSortBy, getSpotlightContactsSortOrder,
  (prop, order) => {
    return { prop, order };
  });

export const getSpotlightContactsRequest = createSelector(
  getSpotlightContactsSearchTerm, getSpotlightContactsSearchBy, getSpotlightContactsPageLimit, getSpotlightContactsPage,
  getSpotlightContactsSortBy, getSpotlightContactsSortOrder,getCfaOneSelectedView,
  (searchTerm, searchBy, limit, page, sortBy, sortOrder, view) => {
    let fromDate = moment().subtract(view.slice(5,7), 'days').format('YYYY-MM-DD');
    let toDate = moment().format('YYYY-MM-DD');
    return {
      searchTerm: searchTerm,
      searchBy: searchBy,
      limit: limit,
      page: page,
      sort: sortBy,
      sortOrder: sortOrder,
      anyCfaoneCustomers: searchTerm ? true : false,
      fromDate,
      toDate
    }
  });

export const getCareSelectedCustomerRow = createSelector(
  getCareSelectedCustomer, (customer) => customer ? [customer] : []);

export const getCareCustomerName = createSelector(
  getCareSelectedCustomer, (customer) => customer.fullName);

export const getSelectedCareReportId = createSelector(
  getSelectedCareReport, (report) => report.id);

export const getSelectedCareReportFullName = createSelector(
  getSelectedCareReport, (report) => report.fullName);

export const getSelectedCareReportPriority = createSelector(
  getSelectedCareReport, (report) => report.priority);

export const getReportSpotEngagementEligible = createSelector(
  getSelectedCareReport, (report) => report.eligibleForSpotEngagement);

export const getCareReportHistory = createSelector(
  getSelectedCareReport, (report) => report.activityHistory);

export const getCareReportHistoryLoading = createSelector(
  getSelectedCareReport, (report) => report.historyLoading);

export const getCareReportHistoryError = createSelector(
  getSelectedCareReport, (report) => report.historyError);

export const getCareContactName = createSelector(
  getMemberType, getSelectedCareReport, getCareSelectedCustomer,
  (memberType, report, customer) => {
    return memberType === CareConstants.spotCustomer ? customer.firstName : report.firstName;
  });

export const getCareStatusOptions = createSelector(
  getStatusOptions, (options) => {
    let spotOptions = (opts) => !opts.sendSpotlightResponseToGuest;

    return R.filter(spotOptions, options);
  });

export const getCareStatusOptionsFilteredByEmail = createSelector(
  getCareStatusOptions,
  getCareServiceNowCase,
  (options, serviceNowCase) => {
    const hasEmail = !!serviceNowCase?.email && serviceNowCase?.email !== 'N/A';
    if (!options || hasEmail) {
      return options;
    }
    const nonSpotlightEmailOptions = CareConstants.nonSpotlightEmailOptions;
    return options.map(section => ({
      ...section,
      category: section.category?.map(subsection => ({
        ...subsection,
        options: subsection.options?.filter(option =>
          !nonSpotlightEmailOptions.includes(option.title)
        )
      }))
    }));
  });

export const getSpotStatusOptions = createSelector(
  getStatusOptions, (options) => {
    return options ? [R.find(R.propEq('sendSpotlightResponseToGuest', true))(options)] : null;
  });

export const getStatusOptionsByType = createSelector(
  getStatusOptions, (options) => {
    return options ? [R.find(R.propEq('sendSpotlightResponseToGuest', true))(options)] : null;
  });

export const getTotalSelectedCareRewards = createSelector(
  getSelectedCareRewards, (rewards) => {
    return rewards.reduce((prev, reward) => {
      return prev + reward.count;
    }, 0);
  });

export const getCareCopyChanged = createSelector(
  getCareDefaultCopy, getCareCopy, (defaultCopy, copy) => defaultCopy !== copy);

export const getCareStrippedEmailCopy = createSelector(getCareCopy,
  (copy) => {
    let newCopy = copy ? copy.replace(/<[^>]*>?/gm, '') : '';

    // Decode HTML entities (including &nbsp;, &#39;, &quot;, &amp;, etc.)
    // Guard against SSR/Node environments where document is undefined
    if (typeof document !== 'undefined') {
      const textArea = document.createElement('textarea');
      textArea.innerHTML = newCopy;
      newCopy = textArea.value;
    }

    // Decode URL-encoded characters (%20, %21, etc.)
    try {
      newCopy = decodeURIComponent(newCopy);
    } catch (e) {
      // If decoding fails (malformed URI), continue with current value
      // This is expected behavior - we don't want to crash on invalid input
      console.warn('Failed to decode URI component:', e);
    }

    // Strip HTML tags again in case entity decoding reintroduced them (e.g., &lt;script&gt;)
    newCopy = newCopy.replaceAll(/<[^>]*>?/gm, '');

    // Normalize non-breaking spaces to regular spaces
    newCopy = newCopy.replaceAll('\u00A0', ' ');

    // Normalize typographic apostrophes/quotes to ASCII equivalents
    newCopy = newCopy.replaceAll(/[\u2018\u2019]/g, "'"); // ' and '
    newCopy = newCopy.replaceAll(/[\u201C\u201D]/g, '"'); // " and "

    return newCopy;
  });

export const getCareThemeCopyChanged = createSelector(
  getCareThemes, getCareThemeName, getCareCopy,
  (themes, themeName, copy) => {
    let filteredTheme = R.find(R.propEq('themeName', themeName))(themes);
    return filteredTheme.emailCopy !== copy;
  });

export const getCareThemeChanged = createSelector(
  getCareDefaultThemeName, getCareThemeName,
  (defaultThemeName, themeName) => defaultThemeName !== themeName);

export const getCareSortObj = createSelector(
  getCareSortBy, getCareSortOrder,
  (prop, order) => {
    return { prop, order };
  });

export const getCasesRequest = createSelector(
  getCareSelectedView, getClosedCareSelectedView,
  (openView, closedView) => {
    const openDays = Number.parseInt(openView.slice(5, 7), 10);
    const closedDays = Number.parseInt(closedView.slice(5, 7), 10);
    const daysAgo = Math.max(openDays, closedDays);
    const startDate = moment().subtract(daysAgo, 'days').format('YYYY-MM-DD');
    const endDate = moment().format('YYYY-MM-DD');

    return {
      anyCfaoneCustomers: true,
      daysAgo,
      endDate,
      limit: 10000,
      page: 0,
      sortBy: 'priority',
      sortOrder: 'asc',
      startDate
    };
  });

export const getCareTemplateRequest = createSelector(
  getSelectedCareReportId, getIncludeReward, getMemberType,
  (incidentId, includeReward, memberType) => {
    let type = includeReward ? 'reward' : 'email';
    return { incidentId, type, memberType };
  });

export const getSpotCustomerCareTemplateRequest = createSelector(
  getIncludeReward, (includeReward) => {
    return includeReward ? 'reward' : 'email';
  });

const getCareHtmlData = createSelector( getCareCopy, getCareHeadline,
  getCarePreHeader, getCareSubject, getCareThemeUrl, getCareTemplateHtml, getCareContactName,
  (copy, headline, preHeader, subject, themeUrl, html, name) => ({
    copy,
    headline,
    preHeader,
    subject,
    themeUrl,
    html,
    name })
);
export const getFormattedCareHtml = createSelector(getSelectedCareRewards,
  getCareHtmlData, getSelectedLocation, (rewards, { copy, headline,
    preHeader, subject, themeUrl, html, name }, location) => {
    let placeholderImage = '../../../assets/img/care-reward-placeholder.svg';
    let placeholderQRCode = 'https://www.cfacdn.com/img/Spotlight/Care/QR.png'
    let rewardEndDate = moment().add('90', 'days');
    let endDates = {};
    let offerDateIndex = 0;
    rewards.forEach((reward) => {
      let offerDate = rewardEndDate;
      if (moment(reward.endDateRestriction) < rewardEndDate && reward.limitedtimeoffer) {
        offerDate = moment(reward.endDateRestriction);
      }
      let rewardCount = reward.count;
      while (rewardCount > 0) {
        endDates['offerEndDate' + offerDateIndex] = offerDate;
        rewardCount = rewardCount - 1;
        offerDateIndex = offerDateIndex + 1;
      }
    })
    let mergeVars = {
      ...endDates,
      copy,
      firstName: name,
      headline,
      placeholderImage,
      preHeader,
      rewards,
      subject,
      themeUrl,
      location,
      placeholderQRCode
    };

    return html ? replaceCareHtml(mergeVars, html): '';
  });

export const getCareRespondPayload = createSelector(getCareState, getCareThemeCopyChanged,
  (care, emailCopyChanged) => {
    let offerTemplateIds = null;

    // If care offers are sent, only send a list of ids
    // including duplicates if multiple are selected
    if (care.includeReward) {
      offerTemplateIds = care.selectedRewards.map((reward) => {
        return R.repeat(reward.id, reward.count);
      });
      offerTemplateIds = R.flatten(offerTemplateIds);
    }

    return {
      emailCopyChanged,
      cfaId: care.selectedReport.cfaId,
      customerMessage: care.copy,
      emailTemplateId: care.templateId,
      emailTemplateName: care.template,
      firstName: care.selectedReport.firstName,
      lastName: care.selectedReport.lastName,
      note: care.selectedReport.note,
      headline: care.headline,
      headLine: care.headline,
      incidentId: care.selectedReport.id,
      includeReward: care.includeReward,
      isP1: care.selectedReport.isP1,
      isResolved: care.isResolved,
      offerTemplateIds,
      preHeader: care.preHeader,
      resolutionStatus: care.resolutionStatus,
      selectedTheme: care.themeName,
      selectedThemeUrl: care.themeUrl,
      subject: care.subject
    };
  });

export const getCareSpotRespondPayload = createSelector(getCareState, getCareThemeCopyChanged,
  (care, emailCopyChanged) => {
    let offerTemplateIds = null;
    const activeSpotlightCustomer = care.selectedCustomer && care.selectedCustomer.showCustomerProfile;

    // If spotlight offers are sent, only send a list of ids
    // including duplicates if multiple are selected
    if (care.includeReward) {
      offerTemplateIds = care.selectedRewards.map((reward) => {
        return R.repeat(reward.id, reward.count);
      });
      offerTemplateIds = R.flatten(offerTemplateIds);
    }

    return {
      activeSpotlightCustomer,
      emailCopyChanged,
      cfaId: care.selectedCustomer.id,
      customerMessage: care.copy,
      emailTemplateId: care.templateId,
      emailTemplateName: care.template,
      firstName: care.selectedCustomer.firstName,
      lastName: care.selectedCustomer.lastName,
      note: care.selectedReport.note,
      headline: care.headline,
      headLine: care.headline,
      includeReward: care.includeReward,
      offerTemplateIds,
      preHeader: care.preHeader,
      resolutionStatus: care.resolutionStatus,
      selectedTheme: care.themeName,
      selectedThemeUrl: care.themeUrl,
      subject: care.subject
    };
  });

/**
 * Understand state root selector
 */
export const getUnderstandState = (state) => state.understand;

/**
 * Understand state children selectors
 */
export const getUnderstandDates = createSelector(getUnderstandState, fromUnderstand.getDates);

export const getUnderstandExcludedCemRewards = createSelector(getUnderstandState, fromUnderstand.getExcludeCemRewards);

export const getUnderstandTiles = createSelector(getUnderstandState, fromUnderstand.getUnderstandTiles);

export const getUnderstandLoading = createSelector(getUnderstandState, fromUnderstand.getUnderstandLoading);

export const getUnderstandError = createSelector(getUnderstandState, fromUnderstand.getUnderstandError);

export const getSelectedCategories = createSelector(getUnderstandState, fromUnderstand.getSelectedCategories);

export const getSelectedFilterChips = createSelector(getUnderstandState, fromUnderstand.getSelectedFilters);

export const getUnderstandDropDownFilters = createSelector(getUnderstandState, fromUnderstand.getDropDownFilters);

export const getUnderstandSearchBy = createSelector(getUnderstandState, fromUnderstand.getSearchBy);

export const getUnderstandSearchTerm = createSelector(getUnderstandState, fromUnderstand.getSearchTerm);

export const getSelectedAnalyticsCategories = createSelector(getUnderstandState, fromUnderstand.getSelectedAnalyticsCategories);

export const getSelectedTiles = createSelector(getUnderstandTiles,
  getSelectedCategories, getFeatureEnabled(EngageConstants.rewardMetricsFlag),
  (tiles, categories, rewardMetricsEnabled) => {
    let returnTiles = [];

    R.values(tiles).forEach((tile) => {
      if (rewardMetricsEnabled && isRewardCampaignsTile(tile)) {
        return;
      }
      if (R.includes(tile.category, categories) && tile.enabled) {
        // If tile is MLG, then add a tile for each instance of a MLG
        if (tile.marketGiveawayTile && !R.isEmpty(tile.data)) {
          tile.data.map((giveawayData) => {
            let giveawayTile = {
              ...tile,
              title: giveawayData.displayTitle,
              data: { ...giveawayData }
            };
            returnTiles.push(rewardMetricsEnabled ? withoutUniqueCustomerSections(giveawayTile) : giveawayTile);
          });
        } else {
          returnTiles.push(rewardMetricsEnabled ? withoutUniqueCustomerSections(tile) : tile);
        }

      }
    });

    return returnTiles;
  });

export const getUnderstandDetailView = createSelector(getUnderstandState, fromUnderstand.getDetailViews);

export const getUnderstandDetails = createSelector(getUnderstandDetailView, getUnderstandDates,
  getUnderstandExcludedCemRewards, getSelectedFilterChips, getUnderstandSearchBy, getUnderstandSearchTerm,
  (views, dates, isCemCheck, selectedFilterChips, searchBy, searchTerm) => {
    return {
      ...views,
      ...dates,
      isCemCheck,
      selectedFilterChips,
      searchBy,
      searchTerm
    };
  });

export const getTilesForMeetPage = createSelector(getUnderstandTiles, getUnderstandDates, getUnderstandExcludedCemRewards,
  (tiles: Tile[], dates, excludeCemRewards) => {
    let meetTiles = [];
    R.values(tiles).forEach((tile) => {
      if (tile.enabled && (tile.category === MeetConstants.analyticsTrendsCategory
          || tile.category === MeetConstants.analyticsCustomerCategory || tile.category === MeetConstants.analyticsCateringCategory)) {
        meetTiles.push(tile);
      }
    });

    return meetTiles.map((tile) => {
      return { ...tile, ...dates, excludeCemRewards };
    });
  });

export const getTrendTiles = createSelector(getUnderstandTiles, getUnderstandDates, getUnderstandExcludedCemRewards,
  (tiles: Tile[], dates, excludeCemRewards) => {
    let trendTiles = [];
    R.values(tiles).forEach((tile) => {
      if (tile.enabled && tile.category === MeetConstants.analyticsTrendsCategory) {
        trendTiles.push(tile);
      }
    });

    return trendTiles.map((tile) => {
      return { ...tile, ...dates, excludeCemRewards };
    });
  });

export const getCustomerTiles = createSelector(getUnderstandTiles, getUnderstandDates, getUnderstandExcludedCemRewards,
  (tiles: Tile[], dates, excludeCemRewards) => {
    let customerTiles = [];
    R.values(tiles).forEach((tile) => {
      if (tile.enabled && tile.category === MeetConstants.analyticsCustomerCategory) {
        customerTiles.push(tile);
      }
    });

    return customerTiles.map((tile) => {
      return { ...tile, ...dates, excludeCemRewards };
    });
  });

export const getCateringTiles = createSelector(getUnderstandTiles, getUnderstandDates, getUnderstandExcludedCemRewards,
  (tiles: Tile[], dates, excludeCemRewards) => {
    let cateringTiles = [];
    R.values(tiles).forEach((tile) => {
      if (tile.enabled && tile.category === MeetConstants.analyticsCateringCategory) {
        cateringTiles.push(tile);
      }
    });

    return cateringTiles.map((tile) => {
      return { ...tile, ...dates, excludeCemRewards };
    });
  });

export const getRewardTiles = createSelector(getUnderstandTiles, getUnderstandDates, getUnderstandExcludedCemRewards,
  getFeatureEnabled(EngageConstants.rewardMetricsFlag),
  (tiles: Tile[], dates, excludeCemRewards, rewardMetricsEnabled) => {
    let rewardTiles = [];
    R.values(tiles).forEach((tile) => {
      if (rewardMetricsEnabled && isRewardCampaignsTile(tile)) {
        return;
      }
      if (tile.enabled && tile.category === EngageConstants.analyticsTreatCategory) {
        if (tile.marketGiveawayTile && !R.isEmpty(tile.data)) {
          tile.data.forEach((giveawayData) => {
            let giveawayTile = {
              ...tile,
              title: giveawayData.displayTitle,
              data: { ...giveawayData }
            };
            rewardTiles.push(giveawayTile);
          });
        } else {
          rewardTiles.push(tile);
        }
      }
    });
    return rewardTiles.map((tile) => {
      const withDates = { ...tile, ...dates, excludeCemRewards };
      return rewardMetricsEnabled ? withoutUniqueCustomerSections(withDates) : withDates;
    });
  });

export const displayViewAllCampaignsButton = createSelector(getUnderstandTiles,
  getFeatureEnabled(EngageConstants.rewardMetricsFlag),
  (tiles: Tile[], rewardMetricsEnabled) => {
    if (rewardMetricsEnabled) {
      return undefined;
    }
    return R.values(tiles).find(tile => isRewardCampaignsTile(tile));
  });

export const getEventTiles = createSelector(getUnderstandTiles, getUnderstandDates, getUnderstandExcludedCemRewards,
  (tiles: Tile[], dates, excludeCemRewards) => {
    let eventTiles = [];
    R.values(tiles).forEach((tile) => {
      if (tile.enabled && tile.category === EngageConstants.analyticsEventCategory) {
        eventTiles.push(tile);
      }
    });
    return eventTiles.map((tile) => {
      return { ...tile, ...dates, excludeCemRewards };
    });
  });

export const getEmailTiles = createSelector(getUnderstandTiles, getUnderstandDates, getUnderstandExcludedCemRewards,
  (tiles: Tile[], dates, excludeCemRewards) => {
    let emailTiles = [];
    R.values(tiles).forEach((tile) => {
      if (tile.enabled && tile.category === EngageConstants.analyticsEmailCategory) {
        emailTiles.push(tile);
      }
    });
    return emailTiles.map((tile) => {
      return { ...tile, ...dates, excludeCemRewards };
    });
  });

export const getRecoverTiles = createSelector(getUnderstandTiles, getUnderstandDates, getUnderstandExcludedCemRewards,
  (tiles: Tile[], dates, excludeCemRewards) => {
    let recoverTiles = [];
    R.values(tiles).forEach((tile) => {
      if (tile.enabled && tile.category === CareConstants.analyticsCareCategory) {
        if (tile.marketGiveawayTile && !R.isEmpty(tile.data)) {
          tile.data.forEach((giveawayData) => {
            let giveawayTile = {
              ...tile,
              title: giveawayData.displayTitle,
              data: { ...giveawayData }
            };
            recoverTiles.push(giveawayTile);
          });
        } else {
          recoverTiles.push(tile);
        }
      }
    });
    return recoverTiles.map((tile) => {
      return { ...tile, ...dates, excludeCemRewards };
    });
  });

export const getUnderstandDetailError = createSelector(getUnderstandDetails, (details) => details.error);

export const getUnderstandDetailLoading = createSelector(getUnderstandDetails, (details) => details.loading);

export const getUnderstandDetailDisableSelect = createSelector(getUnderstandDetails, (details) => details.disableSelect);

export const getUnderstandDetailTitle = createSelector(getUnderstandDetails, (details) => details.title);

export const getUnderstandDetailInfoText = createSelector(getUnderstandDetails, (details) => details.infoText);

export const getUnderstandDetailMobileFullTable = createSelector(getUnderstandDetails, (details) => details.mobileFullTable);

export const getUnderstandDetailNoResultMessage = createSelector(getUnderstandDetails, (details) => details.noResultsMessage);

export const getUnderstandDetailNoResultMessageOnSearch = createSelector(getUnderstandDetails,
  (details) => details.noResultsMessageOnSearch);

export const getUnderstandDetailNoResultMessageWithSearchTermOnSearch = createSelector(getUnderstandDetails,
  (details) => details.noResultsMessageWithSearchTermOnSearch);

export const getUnderstandDetailData = createSelector(getUnderstandDetails, (details) => details.data);

export const getUnderstandDetailPages = createSelector(getUnderstandDetails, (details) => details.numberOfPages);

export const getUnderstandDetailCurrentPage = createSelector(getUnderstandDetails, (details) => details.page);

export const getUnderstandDetailPageSizeOptions = createSelector(getUnderstandDetails, (details) => details.pageSizeOptions);

export const getUnderstandDetailPageSize = createSelector(getUnderstandDetails, (details) => details.pageSize);

export const getUnderstandDetailTotalElements = createSelector(getUnderstandDetails, (details) => details.totalElements);

export const getDisplaySearchBox = createSelector(getUnderstandDetails, (details) => details.displaySearchBox);

export const getDisplayAdvancedSearchBox = createSelector(getUnderstandDetails, (details) => details.displayAdvancedSearchBox);

export const getDeepLinkMessage = createSelector(getUnderstandDetails,
  (details) => details.deepLinkMessage);

export const getVisibleDeepLinkMsg = createSelector(getUnderstandSearchTerm, getDeepLinkMessage, (searchTerm, deepLinkMessage) => {
  return searchTerm && searchTerm.termName && deepLinkMessage;
});

export const getSearchByCategories = createSelector(getUnderstandDetails, (details) => details.searchByCategories);

export const getSearchBoxPlaceholder = createSelector(getUnderstandDetails, (details) => details.searchBoxPlaceholder);

export const getMobileSearchBoxPlaceholder = createSelector(getUnderstandDetails, (details) => details.mobileSearchBoxPlaceholder);

export const getHelpText = createSelector(getUnderstandDetails, (details) => details.helpText);

export const getServerPaging = createSelector(getUnderstandDetails, (details) => details.serverPaging);

export const getUnderstandDisableIndividualRowAttribute = createSelector(getUnderstandDetails,
  (details) => details.disableIndividualRowAttribute);

export const getUnderStandDefaultSortObj = createSelector(getUnderstandDetails,
  ((understandDetails) => {
    if (understandDetails.defaultSortBy) {
      return understandDetails.defaultSortBy;
    }
    return (understandDetails.columns) ?
      { prop: understandDetails.columns[0].prop, order: 'asc' } : { prop: null, order: null };
  }));

export const getUnderstandDetailNextView = createSelector(getUnderstandDetails, (detail) => {
  return {
    viewIncidentDetails: detail.viewIncidentDetails,
    viewProfile: detail.viewProfile,
    nextView: detail.nextView,
    intermediateView: detail.intermediateView
  };
});

export const getIsRowNotInteractable = createSelector(getUnderstandDetailNextView, (detailNextView) => {
  return !detailNextView.viewIncidentDetails && !detailNextView.viewProfile
    && !detailNextView.nextView && !detailNextView.intermediateView;
});

export const getUnderstandDetailIdProp = createSelector(getUnderstandDetails, (detail) => {
  return detail.idProp || 'cfaId';
});

export const getUnderstandDetailColumns = (id) => {
  return createSelector(getUnderstandDetails, getFeatureEnabled('careMembershipColumn'), getIsMobile,
    (understandDetails, careMembershipColumnEnabled, isMobile) => {
      if (!careMembershipColumnEnabled
        && (id === 'careContactStatus' || id === 'careContact' || id === 'careStatus' || id === 'careOfferStat')
        && understandDetails.columns) {
        // To remove memberStatus column
        return understandDetails.columns.filter((col) => col.prop !== 'memberStatus');
      }
      if (understandDetails.mobileFullTable && isMobile && understandDetails.columns) {
        return understandDetails.columns.filter((col) => {
          return col.mobile;
        })
      }
      return understandDetails.columns ? understandDetails.columns : null;
    });
};

export const getUnderstandDetailInfoCol = createSelector(getUnderstandDetailView, (understandDetailView) => {
  let excludeCEM = understandDetailView.filters.filter(f => f.filterName === UnderstandConstants.ExcludeCemRewardText);
  return excludeCEM ? UnderstandConstants.ExcludeCemRewardCol : undefined;
})

export const getUnderstandDetailNoResult = createSelector(getUnderstandDetailNoResultMessage,
  getUnderstandDetailNoResultMessageOnSearch, getUnderstandDetailNoResultMessageWithSearchTermOnSearch, getUnderstandSearchTerm,
  (noResultMessage, noResultsMessageOnSearch, noResultsMessageWithSearchTermOnSearch, searchTerm) => {
    if (searchTerm && searchTerm.termName) {
      if (noResultsMessageWithSearchTermOnSearch) {
        return noResultsMessageWithSearchTermOnSearch + ' ' + searchTerm.termName + '.';
      } else {
        return noResultsMessageOnSearch ? noResultsMessageOnSearch : noResultMessage;
      }
    } else {
      return noResultMessage;
    }
  });

export const getDefaultFilters = createSelector(getUnderstandDetailView,
  getSelectedFilterChips,
  (understandDetailView, selectedFilterChips) => {
    let filters = understandDetailView.filters;
    if (selectedFilterChips.length > 0) {
      filters = R.clone(understandDetailView.filters);
      if (filters != null) {
        selectedFilterChips.map((element) => {
          let filterIndex = (R.findIndex(R.propEq('filterId', element.filterId))(filters));
          if (filterIndex !== -1) {
            filters[filterIndex] = element;
          }
        });
      }

    }
    return filters ? filters : [];
  });

export const shouldUseMembershipIconForMobile = (id) => {
  return createSelector(getFeatureEnabled('careMembershipColumn'),
    getUseMembershipIconForMobileFlag,
    (careMembershipColumnEnabled, userMembershipMobileIconFlag) => {
      return userMembershipMobileIconFlag && (careMembershipColumnEnabled
        || (id !== 'careContactStatus' && id !== 'careContact' && id !== 'careStatus' && id !== 'careOfferStat'));
    });
};

export const getUseMembershipIconForMobileFlag = createSelector(getUnderstandDetails,
  fromUnderstand.getUseMembershipIconForMobileFlag);

/**
 * Engage state root selector
 */
export const getEngageState = (state) => state.engage;

export const getCampaignEngageState = (state) => state.campaignEngage;

/**
 * Engage state children selectors
 */

export const getEngagePageSize = createSelector(getScreenSize,
  (screenSize) => {
    const isMobile = screenSize === AppConstants.extraSmall;
    return isMobile ? 10 : 20;
  });

export const getEngageLoading = createSelector(getEngageState, fromEngage.getLoading);

export const getSelectedTheme = createSelector(getEngageState, fromEngage.getSelectedTheme);

export const getEngageCustomersLoading = createSelector(getEngageState, fromEngage.getCustomerLoading);

export const getEngageCustomersError = createSelector(getEngageState, fromEngage.getCustomerError);

export const getGroupSendOption = createSelector(getEngageState, fromEngage.getGroupSendOption);

/* This selector responsible for calculating what is the current loading state.
Based on that it passes to ui */
export const getFinalLoadingState = createSelector(getEngageLoading, getEngageCustomersLoading,
  (engageLoading, cusLoading) => {
    return cusLoading ? cusLoading : engageLoading;
  });

export const getEngageError = createSelector(getEngageState, fromEngage.getError);

export const getEngageRewards = createSelector(getEngageState, fromEngage.getRewards);

export const getCampaignHtml = (layoutId?: string) => createSelector(getCampaignEngageState, (campaignState: fromCampaignEngage.State) => {
  const customizations = campaignState.customizations;
  const rewardDetails: RewardDetails = campaignState.rewardCampaign;
  const eventDetails: EventDetails = campaignState.eventCampaign;
  const templateDetails: TemplateDetails = campaignState.templateDetails;
  const notificationLayout: NotificationLayout = templateDetails.notificationLayouts.find(
    layout => layoutId ? layout.id === layoutId : layout.id === customizations.layoutID);
  const theme = notificationLayout?.themes.find(theme => theme.id === customizations.themeID);

  let replacements = {
    '.Theme.PreHeader': theme?.preHeader,
    '.Theme.HeaderImage.DesktopURL': theme?.headerImage.desktopURL,
    '.Theme.HeaderImage.MobileURL': theme?.headerImage.mobileURL,
    '.User.FirstName': '[First Name]',
    '.Theme.EmailBody': customizations.body,
    '.RecruitmentVisibility': customizations.includeRecruitment ? '' : 'style="display:none;"'
  }
  if (templateDetails.campaignType == CampaignType.Reward) {
    replacements['.EndDatetime'] = rewardDetails.endDatetime?.format('MM/DD/YYYY') ?? 'XX/XX/XXXX';
  }
  if (templateDetails.campaignType == CampaignType.Event) {
    replacements['.Event.TimePeriod'] = eventDetails.startDate.format('MM/DD/YYYY') + ' ' + eventDetails.startTime
      + ' to ' + eventDetails.endDate.format('MM/DD/YYYY') + ' ' + eventDetails.endTime;
    replacements['.Event.Venue'] = eventDetails.venue
    replacements['.Event.RSVPVisibility'] = eventDetails.rsvpRequired ? '' : 'style="display:none;"';
  }
  if (templateDetails.campaignType == CampaignType.Notification || templateDetails.campaignType == CampaignType.Event) {
    const fallbackDesktopImage = theme?.heroImage?.desktopURL ?? templateDetails.heroImages[0]?.desktopURL
    const fallbackMobileImage = theme?.heroImage?.mobileURL ?? templateDetails.heroImages[0]?.mobileURL
    replacements['.Theme.HeroImage.DesktopURL'] = customizations?.heroImage?.desktopURL ?? fallbackDesktopImage
    replacements['.Theme.HeroImage.MobileURL'] = customizations?.heroImage?.mobileURL ?? fallbackMobileImage
    replacements['.Theme.CtaButtonImage.DesktopURL'] = theme?.ctaButtonImage?.desktopURL ?? ''
    replacements['.Theme.CtaButtonImage.MobileURL'] = theme?.ctaButtonImage?.mobileURL ?? ''
  }
  let html = notificationLayout?.html?.replaceAll(/\[\[\s*([\w.]+)\s*]]/g, (match, key) => {
    return key in replacements ? replacements[key] : match;
  });
  html = html?.replaceAll('id="tableWrapper"', '');
  if (templateDetails.campaignType == CampaignType.Event && (!eventDetails?.maxReservations || eventDetails?.maxReservations === 100000)) {
    html = html?.replaceAll('Please be sure to RSVP with the', '');
    html = html?.replaceAll('number of guests in your party, as', '');
    html = html?.replaceAll('space for this event is limited.', '');
  }
  return html
});

export const getEngageRewardsByCategory = createSelector(getEngageRewards, (rewards) => {
  let groupedRewards: CategorizedRewards[] = [];
  if (rewards && rewards.templates) {
    let categories = R.without([undefined], R.uniq(R.pluck('category', rewards.templates)));
    if (rewards.ltoTemplates && rewards.ltoTemplates.length > 0) {
      groupedRewards.push({
        category: EngageConstants.currentCampaign,
        templates: rewards.ltoTemplates
      })
    }
    categories.forEach(category => {
      groupedRewards.push({
        category: category,
        templates: R.filter(reward => reward.category == category, rewards.templates)
      })
    })
  }
  return groupedRewards;
})

export const getCampaignEngageTemplatesByTab = createSelector(getCampaignEngageState, fromCampaignEngage.getTemplatesByTab);

export const getCampaignType = createSelector(getCampaignEngageState, fromCampaignEngage.getCampaignType);

export const getCampaignEngageTemplates = createSelector(getCampaignEngageTemplatesByTab,
  getCampaignType, (templatesByTab, campaignType) => {
    return templatesByTab.get(campaignType)?.templates;
  });
export const getCampaignEngageTemplatesByCategory = createSelector(getCampaignEngageTemplates, (templates) => {
  if (!templates) {
    return [];
  }
  const templatesMap: Map<string, CampaignTemplatesModel[]> = new Map<string, CampaignTemplatesModel[]>();
  for (let template of templates) {
    if (!template.category) {
      continue;
    }
    if (templatesMap.has(template.category)) {
      templatesMap.get(template.category).push(template)
      continue;
    }
    templatesMap.set(template.category, [template])
  }
  const groupedTemplates: CategorizedTemplates[] = [];
  for (let [key, value ] of templatesMap ) {
    if (key === 'Limited Time Offer' || key === 'Current Campaign') {
      groupedTemplates.unshift({ category: key, templates: value , filteredTemplates: value })
      continue;
    }
    groupedTemplates.push({ category: key, templates: value, filteredTemplates: value })
  }
  return groupedTemplates;
})
export const getEngageRewardCategories = createSelector(getEngageRewards, (rewards) => {
  let categories: string[] = [];
  if (rewards && rewards.templates) {
    categories = R.without([undefined], R.uniq(R.pluck('category', rewards.templates)));
    if (rewards.ltoTemplates && rewards.ltoTemplates.length > 0) {
      categories.unshift(EngageConstants.currentCampaign)
    }
    categories.unshift(EngageConstants.allRewardsCategory);
  }
  return categories;
});

export const getCampaignEngageCategories = createSelector(getCampaignEngageTemplates, getCampaignType,(templates, campaignType) => {
  let categories: string[] = [];
  if (templates) {
    categories = R.without([undefined], R.uniq(R.pluck('category', templates)));
    switch (campaignType) {
      case CampaignType.Reward:
        categories.unshift(EngageConstants.allRewardsCategory);
        break;
      case CampaignType.Notification:
        categories.sort((a,b)=> {
          if (a === 'Current Campaign') {
            return -1;
          } else if (b === 'Current Campaign') {
            return 1;
          } else {
            return a.localeCompare(b);
          }
        })
        categories.unshift(EngageConstants.allEmailsCategory);
        break;
      case CampaignType.Event:
        categories.unshift(EngageConstants.allEventsCategory);
        break;
    }
  }
  return categories;
});

export const getEngageRewardsLoading = createSelector(getEngageState, fromEngage.getRewardsLoading);

export const getCampaignEngageTemplatesLoading = createSelector(
  getCampaignEngageTemplatesByTab, getCampaignType, (templatesByTab, campaignType) => {
    return templatesByTab.get(campaignType)?.loading
  })

export const getEngageRewardsError = createSelector(getEngageState, fromEngage.getRewardsError);

export const getCampaignEngageTemplatesError = createSelector(
  getCampaignEngageTemplatesByTab, getCampaignType, (templatesByTab, campaignType) => {
    return templatesByTab.get(campaignType)?.error;
  });

export const getEngageEvents = createSelector(getEngageState, fromEngage.getEvents);

export const getEngageEventCategories = createSelector(getEngageEvents, (events) => {
  let categories = [];
  if (events) {
    categories = R.without([undefined], R.uniq(R.pluck('category', events)));
    categories.unshift(EngageConstants.allEventsCategory);
    categories = R.sort((a, b) => a.localeCompare(b), categories);
  }
  return categories;
});

export const getEngageEventsLoading = createSelector(getEngageState, fromEngage.getEventsLoading);

export const getEngageEventsError = createSelector(getEngageState, fromEngage.getEventsError);

export const getSelectedTreat = createSelector(getEngageState, fromEngage.getSelectedTreat);

export const getSelectedTemplateDetails = createSelector(getCampaignEngageState, fromCampaignEngage.getTemplateDetails);

export const getCampaignCustomizations = createSelector(getCampaignEngageState, fromCampaignEngage.getCustomizations);

export const getAudienceDetails = createSelector(getCampaignEngageState, fromCampaignEngage.getAudienceDetails);

export const getSelectedRewardCampaign = createSelector(getCampaignEngageState, fromCampaignEngage.getSelectedRewardCampaign);

export const getSelectedEventCampaign = createSelector(getCampaignEngageState, fromCampaignEngage.getSelectedEventCampaign);

export const eventStartTimeRestriction = createSelector(getCampaignEngageState, fromCampaignEngage.getEventStartTimeRestriction);

export const eventEndTimeRestriction = createSelector(getCampaignEngageState, fromCampaignEngage.getEventEndTimeRestriction);

export const eventSendFutureTimeRestriction = createSelector(getCampaignEngageState, fromCampaignEngage.getEventSendFutureTimeRestriction);

export const layoutTabIndex = createSelector(getCampaignEngageState, fromCampaignEngage.getLayoutTabIndex);

export const getSelectedCampaignLoading = createSelector(getCampaignEngageState, fromCampaignEngage.getLoading);

export const getSelectedCampaignError = createSelector(getCampaignEngageState, fromCampaignEngage.getError);

export const getCreateCampaignError = createSelector(getCampaignEngageState, fromCampaignEngage.getCreateError);

export const getSelectedCampaignID = createSelector(getCampaignEngageState, fromCampaignEngage.getCampaignID);

export const getSelectedNotificationID = createSelector(getCampaignEngageState, fromCampaignEngage.getNotificationID);

export const getSelectedNotifications = createSelector(getCampaignEngageState, fromCampaignEngage.getNotifications);

export const getSendingOptions = createSelector(getCampaignEngageState, fromCampaignEngage.getSendingOptions);

export const getSelectedCampaignCreatedDetails = createSelector(getCampaignEngageState, fromCampaignEngage.getCreatedDetails);

export const getCampaignAnalyticsLoading = createSelector(getCampaignEngageState, fromCampaignEngage.getAnalyticsLoading);

export const getCampaignAnalyticsError = createSelector(getCampaignEngageState, fromCampaignEngage.getAnalyticsError);

export const getCampaignRewardAnalytics = createSelector(getCampaignEngageState, fromCampaignEngage.getRewardAnalytics);

export const getSelectedEmailCategories = createSelector(getCampaignEngageState, fromCampaignEngage.getSelectedEmailCategories);

export const getCombinedCampaignDetails = createSelector(getSelectedTemplateDetails, getCampaignCustomizations, getSelectedRewardCampaign,
  getSelectedEventCampaign, getSelectedCampaignCreatedDetails, getSendingOptions, getAudienceDetails,
  getCampaignAnalyticsLoading, getCampaignAnalyticsError, getCampaignRewardAnalytics,
  (templateDetails, customizations, rewardDetails, eventDetails, createdDetails, sendingOptions, audienceDetails,
    analyticsLoading, analyticsError, rewardAnalytics) => {
    return {
      templateDetails, customizations, rewardDetails, eventDetails, createdDetails, sendingOptions, audienceDetails,
      analyticsLoading, analyticsError, rewardAnalytics
    }
  })

export const getSelectedCampaign = createSelector(getSelectedCampaignLoading,
  getSelectedCampaignError, getCombinedCampaignDetails,
  (loading, error, details ) => {
    return { loading, error, ...details }
  });

export const getRewardAnalyticsEnabled = createSelector(
  createSelector(
    getFeatureEnabled(EngageConstants.cev2EnhancedRewardAnalyticsFlag),
    getFeatureEnabled(EngageConstants.enhancedAnalyticsMetricsFlag),
    (legacyEnabled, metricsEnabled) => legacyEnabled || metricsEnabled
  ),
  getCampaignType,
  getSelectedRewardCampaign,
  getSelectedCampaignCreatedDetails,
  (enabled, campaignType, reward, createdDetails) => {
    if (!enabled) {
      return false;
    }
    if (campaignType !== CampaignType.Reward) {
      return false;
    }
    if (createdDetails?.createdStatus === CampaignStatus.Failed) {
      return false;
    }
    if (createdDetails?.createdStatus === CampaignStatus.Scheduled) {
      return false;
    }
    // reward campaigns can 'complete' successfully even if rewards didnt go out
    // like in the case of all recipients being excluded
    if (reward?.offerBatchID == null) {
      return false;
    }
    // a reward cancelled before it ever started never reached a customer, so it has no analytics
    if (reward?.deletedAt?.isBefore(reward.startDatetime)) {
      return false;
    }
    // reward analytics wont be available untile the next day
    if (reward?.startDatetime?.isAfter(moment().subtract(1, 'days'))) {
      return false;
    }

    return true;
  });

export const getSelectedTreatWithCampaignEngineV2 = createSelector(getSelectedTreat, getFeatureEnabled(SharedConstants.campaignEngineV2),
  (treat, campaignEngineV2) => {
    return { ...treat, campaignEngineV2 }
  })
export const getRecipientsSearchTerm = createSelector(getEngageState, fromEngage.getRecipientsSearchTerm);

export const getSelectedTabIndex = createSelector(getEngageState, fromEngage.getSelectedTabIndex);

export const getSelectedHtmlTabIndex = createSelector(getEngageState, fromEngage.getSelectedHtmlTabIndex);
export const getEngagementId = createSelector(getEngageState, fromEngage.getEngagementId);

export const getSelectedEvent = createSelector(getEngageState, fromEngage.getSelectedEvent);

export const getSelectedEmail = createSelector(getEngageState, fromEngage.getSelectedEmail);

export const getSelectedBonusPointPromotion = createSelector(getEngageState, fromEngage.getSelectedBonusPointPromotion);

export const getSelectedCommunityCareEvent = createSelector(getEngageState, fromEngage.getSelectedCommunityCareEvent);

export const getSelectedThirdPartyPromotion = createSelector(getEngageState, fromEngage.getSelectedThirdPartyPromotion);

export const getSelectedFreeInCartPromotion = createSelector(getEngageState, fromEngage.getSelectedFreeInCartPromotion);
export const getDefaultSort = createSelector(getEngageState, fromEngage.getDefaultSort);

export const getDefaultSortOrder = createSelector(getEngageState, fromEngage.getDefaultSortOrder);

export const getCustomizeSortArray = createSelector(getEngageState, fromEngage.getCustomizeSortArray);

export const getDefaultSortObjForRecipients = createSelector(getDefaultSort, getDefaultSortOrder, getCustomizeSortArray,
  (prop, order, customizeSortArray) => ({ prop, order, customizeSortArray }));

export const getSelectedEngageCustomers = createSelector(getEngageState, fromEngage.getSelectedCustomers);

export const getEngageAppliedIncludedCustomers = createSelector(getEngageState, fromEngage.getIncludedCustomers);

export const getEngageAppliedExcludedCustomers = createSelector(getEngageState, fromEngage.getExcludedCustomers);

export const getEngageAppliedFilterOnBetweenId = createSelector(getEngageState, fromEngage.getFilterOnBetweenId);

export const getEngageAppliedFilterStartDate = createSelector(getEngageState, fromEngage.getFilterStartDate);

export const getEngageAppliedFilterEndDate = createSelector(getEngageState, fromEngage.getFilterEndDate);

export const getSelectedEngageIndividualIds = createSelector(getEngageAppliedIncludedCustomers, (individualIds) => {
  return individualIds;
});

export const getSelectedEngageSavedFilters = createSelector(getEngageState, fromEngage.getSelectedSavedFilters);

export const getSelectedEngageSavedFiltersIds = createSelector(getSelectedEngageSavedFilters,
  (savedFilters) => {
    // On the edit flow, if the user does not click into the customer selection component at all, the
    // savedFilters variable will be an array of ids already and won't have any id attributes
    return savedFilters[0] && savedFilters[0].id ? R.pluck('id', savedFilters) : savedFilters;
  });

export const getSelectedEngageDefaultFilters = createSelector(getEngageState, fromEngage.getSelectedDefaultFilters);

export const getSelectedEngageDefaultFiltersIds = createSelector(getSelectedEngageDefaultFilters,
  (defaultFilters) => {
    // On the edit flow, if the user does not click into the customer selection component at all, the
    // defaultFilters variable will be an array of ids already and won't have any id attributes
    return defaultFilters[0] && defaultFilters[0].id ? R.pluck('id', defaultFilters) : defaultFilters;
  });

export const getTotalSelectedEngageFilters = createSelector(
  getSelectedEngageSavedFilters, getSelectedEngageDefaultFilters,
  (savedGroups, selectedFilters) => {
    return savedGroups.length + selectedFilters.length;
  });

export const areNewEngageDateFiltersSelected = createSelector(
  getEngageFiltersOnBetweenId, getEngageFiltersStartDate, getEngageFiltersEndDate,
  getEngageAppliedFilterOnBetweenId, getEngageAppliedFilterStartDate, getEngageAppliedFilterEndDate,
  (selectedOnBetweenId, selectedStartDate, selectedEndDate, appliedOnBetweenId, appliedStartDate, appliedEndDate) => {
    let areNewDateFiltersSelected = false;
    // Check if new On/Between date option has been selected
    if (selectedOnBetweenId !== appliedOnBetweenId) {
      areNewDateFiltersSelected = true;
    }

    // Check if new Custom Date has been selected
    if (selectedStartDate !== appliedStartDate || selectedEndDate !== appliedEndDate) {
      areNewDateFiltersSelected = true;
    }

    return areNewDateFiltersSelected;
  });

export const areNewEngageFiltersSelected = createSelector(
  getDisplayFilters(AppConstants.engagePage), getEngageSelectedFilters, getEngageSelectedSavedGroups, getEngageIncludedCustomers,
  getEngageExcludedCustomers, getSelectedEngageDefaultFilters, getSelectedEngageSavedFilters, getEngageAppliedIncludedCustomers,
  getEngageAppliedExcludedCustomers, areNewEngageDateFiltersSelected,
  (displayFilters, selectedDisplayFilters, selectedSavedGroups, selectedIncludedCustomers, selectedExcludedCustomers,
    appliedFilters, appliedSavedGroups, appliedIncludedCustomers, appliedExcludedCustomers, newDateFiltersSelected) => {
    let areNewFiltersSelected = false;

    if (appliedFilters.length !== selectedDisplayFilters.length) {
      areNewFiltersSelected = true;
    }

    // Check if new filters have been added/removed/modified
    appliedFilters.forEach(appliedFilter => {
      const selectedCategory = R.find(R.propEq('id', appliedFilter.parentCategory))(displayFilters);
      const selectedSubFilter = R.find(R.propEq('id', appliedFilter.id))(selectedCategory.subFilters);

      if (appliedFilter.buttonToggles) {
        let selectedDisplayToggle = R.find(R.propEq('selected', true))(selectedSubFilter.buttonToggles);

        if (appliedFilter.selectedButtonToggle !== selectedDisplayToggle.id) {
          areNewFiltersSelected = true;
        }
      }

      if (appliedFilter.buttonChipSections) {
        selectedSubFilter.buttonChipSections.map(buttonChipSection => {
          let displaySelectedChips = [];
          let appliedSelectedChips = [];
          let selectedButtonChipSection = R.find(R.propEq('id', buttonChipSection.id))(appliedFilter.selectedButtonChipSections);

          let selectedChips = R.filter((chip: any) => chip.selected, buttonChipSection.buttonChips as any[]);
          if (selectedChips.length > 0) {
            displaySelectedChips = R.flatten(R.pluck('id', selectedChips));
            appliedSelectedChips = selectedButtonChipSection ? selectedButtonChipSection.selectedButtonChips : [];
            if (!R.equals(appliedSelectedChips, displaySelectedChips)) {
              areNewFiltersSelected = true;
            }
          }
        });
      }

      if (appliedFilter.sliderOptions) {
        if (appliedFilter.sliderOptions.value !== selectedSubFilter.sliderOptions.value
            || appliedFilter.sliderOptions.highValue !== selectedSubFilter.sliderOptions.highValue) {
          areNewFiltersSelected = true;
        }
      }
    });

    // Check if new included individuals have been added/removed
    let selectedIncludedIds = R.flatten(R.pluck('customerUid', selectedIncludedCustomers));
    let appliedIncludedIds = R.flatten(R.pluck('customerUid', appliedIncludedCustomers));
    if (!R.equals(selectedIncludedIds, appliedIncludedIds)) {
      areNewFiltersSelected = true;
    }

    // Check if new excluded individuals have been added/removed
    let selectedExcludedIds = R.flatten(R.pluck('customerUid', selectedExcludedCustomers));
    let appliedExcludedIds = R.flatten(R.pluck('customerUid', appliedExcludedCustomers));
    if (!R.equals(selectedExcludedIds, appliedExcludedIds)) {
      areNewFiltersSelected = true;
    }

    // Check if new saved groups have been added/removed
    let selectedSavedGroupIds = R.flatten(R.pluck('id', selectedSavedGroups));
    let appliedSavedGroupsIds = R.flatten(R.pluck('id', appliedSavedGroups));
    if (!R.equals(selectedSavedGroupIds, appliedSavedGroupsIds)) {
      areNewFiltersSelected = true;
    }

    if (newDateFiltersSelected) {
      areNewFiltersSelected = true;
    }

    return areNewFiltersSelected;
  });

export const getEngageType = createSelector(getEngageState, fromEngage.getType);

export const getEngageMode = createSelector(getEngageState, fromEngage.getMode);

export const getSelectedEngagement = createSelector(getSelectedTreatWithCampaignEngineV2,
  getSelectedEvent, getSelectedEmail, getSelectedBonusPointPromotion, getSelectedCommunityCareEvent,
  getSelectedThirdPartyPromotion, getSelectedFreeInCartPromotion, getEngageType, getSelectedCampaign,
  (selectedTreat, selectedEvent, selectedEmail, selectedBonusPointPromotion,
    selectedCommunityCareEvent, selectedThirdPartyPromotion, selectedFreeInCartPromotion,
    type, selectedCampaign) => {
    let selectedEngagement
    if (type === EngageConstants.offer
      || type === EngageConstants.discover ) {
      selectedEngagement = selectedTreat;
    } else if (type === EngageConstants.event) {
      selectedEngagement = selectedEvent;
    } else if (type === EngageConstants.email) {
      selectedEngagement = selectedEmail;
    } else if (type === EngageConstants.bonusPoint) {
      selectedEngagement = selectedBonusPointPromotion;
    } else if ( type == EngageConstants.communityCare) {
      selectedEngagement = selectedCommunityCareEvent;
    } else if ( type == EngageConstants.thirdPartyPromotion || type == EngageConstants.nationalThirdPartyPromotion) {
      selectedEngagement = selectedThirdPartyPromotion
    } else if (type == EngageConstants.freeItemInCartPromotion) {
      if (selectedFreeInCartPromotion && !selectedFreeInCartPromotion.selectedDestinationIdsForMultiSelect?.includes('delivery')) {
        selectedFreeInCartPromotion = {
          ...selectedFreeInCartPromotion,
          customerTypes: selectedFreeInCartPromotion.customerTypes?.filter(type => type.displayTitle !== 'All') }
      }
      selectedEngagement = selectedFreeInCartPromotion
    } else if ([CampaignType.Reward, CampaignType.Notification, CampaignType.Event].includes(type as CampaignType)) {
      selectedEngagement = selectedCampaign;
    }
    return { ...selectedEngagement, route: 'engage/engage-details',
      campaignEngineV2: [CampaignType.Reward, CampaignType.Notification, CampaignType.Event].includes(type as CampaignType) } ;
  });

export const getEngageSendImmediate = createSelector(getSelectedEngagement, (selectedEngagement) => {
  return selectedEngagement.sendImmediate;
});

export const getSelectedEngagementImage = createSelector(getEngageType,
  (type) => {
    let selectedEngagementImage: string;

    if (type === EngageConstants.offer || type === EngageConstants.discover) {
      selectedEngagementImage = '../../../assets/img/red_gift.png';
    } else if (type === EngageConstants.email || type === EngageConstants.event) {
      selectedEngagementImage = '../../../assets/img/red_bag.png';
    }

    return selectedEngagementImage;
  });

export const getLocationStreetAddress = createSelector(getSelectedLocation,
  (selectedLocation) => {
    let address: any = selectedLocation.address;
    let formattedAddress = address.address1;

    if (address.address2 !== null) {
      formattedAddress += ' ' + address.address2;
    }
    if (address.address3 !== null) {
      formattedAddress += ' ' + address.address3;
    }
    formattedAddress += ', ' + address.city + ', ' + address.state + ' ' + address.zipCode;
    return formattedAddress;
  });

export const getEngageCopyHeader = createSelector(getEngageType, (type) => {
  let copyHeader;

  if (type === EngageConstants.offer) {
    copyHeader = EngageConstants.rewardCopyHeader;
  } else {
    copyHeader = EngageConstants.emailCopyHeader;
  }

  return copyHeader;
});

export const getEngageCopyText = createSelector(getEngageType, (type) => {
  let copyText;

  if (type === EngageConstants.offer) {
    copyText = EngageConstants.rewardEmailCopyText;
  } else if (type === EngageConstants.event) {
    copyText = EngageConstants.eventCopyText;
  } else if (type === EngageConstants.email) {
    copyText = EngageConstants.emailCopyText;
  }

  return copyText;
});

export const isPushNotificationEnabled = createSelector(
  getFeatureEnabled(EngageConstants.pushNotificationFlag),
  getSelectedEngagement,
  (pushNotificationFlag, engagement) => {
    let disablePushNotification = engagement.endDate ? moment(engagement.endDate).diff(moment(engagement.startDate), 'days') < 7 : true;
    return pushNotificationFlag && !disablePushNotification;
  });

export const getEngageCampaignName = createSelector(getEngageState, fromEngage.getCampaignName);

export const getStockImages = createSelector(getEngageState, fromEngage.getStockImages);

export const getEngageSavedFilters = createSelector(getEngageState, fromEngage.getSavedFilters);

export const getEngageDefaultFilters = createSelector(getEngageState, fromEngage.getDefaultFilters);

export const getEngageSavedFiltersLoading = createSelector(getEngageState,
  fromEngage.getSavedFiltersLoading);

export const getEngageDefaultFiltersLoading = createSelector(getEngageState,
  fromEngage.getDefaultFiltersLoading);

export const getEngageSavedFiltersError = createSelector(getEngageState,
  fromEngage.getSavedFiltersError);

export const getEngageDefaultFiltersError = createSelector(getEngageState,
  fromEngage.getDefaultFiltersError);

export const getEngageThemes = createSelector(getEngageState, fromEngage.getThemes);

export const getEngageDisplayThemes = createSelector(getSelectedEngagement,
  (engagement) => engagement.displayThemes);

export const getEngageSubject = createSelector(getSelectedEngagement,
  (engagement) => engagement.subject);

export const getEngageThemeTitle = createSelector(getSelectedEngagement,
  (engagement) => engagement.title);

export const getEngageEmailHeadline = createSelector(getSelectedEngagement,
  (engagement) => engagement.emailheadline);

export const getEngagementEmailCopy = createSelector(getSelectedEngagement,
  (engagement) => engagement.emailCopy);

export const getEngagementStrippedEmailCopy = createSelector(getEngagementEmailCopy,
  (copy) => {
    let newCopy = copy ? copy.replace(/<[^>]*>?/gm, '') : '';
    return newCopy.replace(/&nbsp;/gm, ' ');
  });

export const getEngageAppCopy = createSelector(getSelectedEngagement,
  (engagement) => engagement.defaultMessageForApp);

export const getEngagePushMessageCopy = createSelector(getSelectedEngagement,
  (engagement) => engagement.pushMsgBody);

export const getRewardRecipients = createSelector(getEngageState,
  fromEngage.getRewardRecipients);

export const getDuplicateRecipients = createSelector(getEngageState,
  fromEngage.getDuplicateRecipients);

export const getChangeImageOption = createSelector(getSelectedEngagement, getStockImages,
  (engagement, stockImage) => {
    let stockImages = R.clone(stockImage);
    stockImages.map(options => {
      options.changeImageOptions.map(image => {
        image.isSelected = image.imageUrl === engagement.imageUrl;
      });
    });
    return stockImages;
  });

export const getStockImagesAvailable = createSelector(getStockImages,
  (stockImage) => {
    return stockImage && stockImage.length > 0 && stockImage[0].changeImageOptions
      && stockImage[0].changeImageOptions.length > 0;
  });

export const getAreAllDuplicates = createSelector(
  getRewardRecipients,
  getDuplicateRecipients,
  (totalCustomers, duplicateCustomers) => {
    if (totalCustomers && duplicateCustomers) {
      return totalCustomers.length === 0 && duplicateCustomers.length > 0;
    }
    return false;
  });

export const shouldFetchIndividualCustomers = createSelector(
  getSelectedEngageIndividualIds,
  getSelectedEngageCustomers,
  (selectedCustomerIds, selectedCustomerObjs) => {
    // Only want to fetch customer objects from ids if they are not already on the state
    return selectedCustomerIds.length !== selectedCustomerObjs.length;
  });

// Check for null specifically so that empty strings are allowed
export const getEngageDisplayCampaignName = createSelector(getEngageCampaignName,
  (campaignName) => campaignName !== null ? campaignName : '');

export const getEngageSendOperatorImage = createSelector(getSelectedTreat,
  (treat) => treat.sendOperatorImage);

export const getEngageStartDate = createSelector(getSelectedEngagement,
  (engagement) => {
    if (engagement.startDate) {
      return engagement.startDate;
    }
    if (engagement.templateDetails) {
      if (engagement.templateDetails.campaignType === CampaignType.Reward) {
        return engagement.rewardDetails?.startDatetime;
      } else {
        return engagement.eventDetails?.startDate;
      }
    }
    return undefined;
  });

export const getEngageEndDate = createSelector(getSelectedEngagement,
  (engagement) => {
    if (engagement.endDate) {
      return engagement.endDate;
    }
    if (engagement.templateDetails) {
      if (engagement.templateDetails.campaignType === CampaignType.Reward) {
        return engagement.rewardDetails?.endDatetime;
      } else {
        return engagement.eventDetails?.endDate;
      }
    }
    return undefined;
  });

export const getEngageCalendarPayload = createSelector(
  getEngageStartDate, getEngageEndDate, getSelectedEngagement, getSelectedCampaign,
  getFeatureEnabled(SharedConstants.campaignEngineV2),
  getFeatureEnabled(SharedConstants.campaignEngineV2Events), getFeatureEnabled(CalendarConstants.nationalEmails),
  (start, end, engagement, campaign,
    campaignEngineV2,
    campaignEngineV2Event, nationalEmails) => {
    if (start === undefined || end === undefined) {
      start = campaign.rewardDetails.startDatetime;
      end = campaign.rewardDetails.endDatetime;
    }
    return {
      campaignEngineV2,
      campaignEngineV2Email: true,
      campaignEngineV2Event,
      campaignTypes:  ['rewards', 'events', 'bonusPointPromotions', 'communityCareEvent',
        'free-item-in-cart-promotion', 'third-party-promotion', 'nationalThirdPartyPromotion'],
      fromDate: start.format('YYYY-MM-DD'),
      id: engagement.id,
      nationalEmails,
      toDate: end.format('YYYY-MM-DD')
    };
  });
export const getEngageMaxDaysAllowed = createSelector(getSelectedEngagement,
  (engagement) => engagement.maxDaysAllowed);

export const getEngageSendDate = createSelector(getSelectedEngagement,
  (engagement) => engagement.sendDate);

export const getEngageSendTime = createSelector(getSelectedEngagement,
  (engagement) => engagement.sendTime);

export const getEngageStartTime = createSelector(getSelectedEngagement,
  (engagement) => engagement.startTime);

export const getEngageEndTime = createSelector(getSelectedEngagement,
  (engagement) => engagement.endTime);

export const getEngageStartTimeRestriction = createSelector(getSelectedEngagement,
  (engagement) => engagement.startTimeRestriction);

export const getEngageEndTimeRestriction = createSelector(getSelectedEngagement,
  (engagement) => engagement.endTimeRestriction);

export const getEngageSendTimeStartRestriction = createSelector(getSelectedEngagement,
  (engagement) => engagement.sendTimeStartRestriction);

export const getEngageSendTimeEndRestriction = createSelector(getSelectedEngagement,
  (engagement) => engagement.sendTimeEndRestriction);

export const getEngageVenue = createSelector(getSelectedEngagement,
  (engagement) => engagement.venue);

export const getEngageRsvpRequired = createSelector(getSelectedEvent,
  (event) => event.rsvpRequired);

export const getEngageLimitedReservations = createSelector(getSelectedEvent,
  (event) => event.limitedReservations);

export const getEngageMaxNumberOfReservations = createSelector(getSelectedEvent,
  (event) => event.maxNumberOfReservations);

export const getEngageAdultsOnly = createSelector(getSelectedEvent,
  (event) => event.adultsOnly);

export const getTreatEndDateRestriction = createSelector(getSelectedTreat,
  (treat) => treat.endDateRestriction);

export const getEngageReminderEmailsEnabled = createSelector(getSelectedEngagement,
  (engagement) => {
    let { startDate, sendDate, sendImmediate } = engagement;
    let beginningOfToday = moment().set({ hour:0,minute:0,second:0,millisecond:0 });
    let inviteSendDate = sendImmediate ? beginningOfToday : sendDate;
    return moment(startDate).diff(inviteSendDate, 'days') > 1;
  });

export const getCampaignReminderEmailsEnabled = createSelector(getSelectedCampaign,
  (campaign) => {
    const { eventDetails, sendingOptions } = campaign;
    const beginningOfToday = moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    // If the campaign is in the past, use beginningOfToday to correctly enable/disable the RSVP reminder
    const inviteSendDate = sendingOptions.sendImmediate || moment().isAfter(sendingOptions.sendDate)
      ? beginningOfToday : sendingOptions.sendDate;
    return moment(eventDetails.startDate).diff(inviteSendDate, 'days') > 1;
  });

export const getEngageNonRsvpReminderEnabled = createSelector(getSelectedEngagement,
  (engagement) => {
    let { startDate, sendDate, sendImmediate } = engagement;
    let beginningOfToday = moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    let inviteSendDate = sendImmediate ? beginningOfToday : sendDate;
    return moment(startDate).diff(inviteSendDate, 'days') > 3;
  });

export const getCampaignNonRsvpReminderEnabled = createSelector(getSelectedCampaign,
  (campaign): boolean => {
    const { eventDetails, sendingOptions } = campaign;
    const beginningOfToday = moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    // If the campaign is in the past, use beginningOfToday to correctly enable/disable the non-RSVP reminder
    const inviteSendDate = sendingOptions.sendImmediate || moment().isAfter(sendingOptions.sendDate)
      ? beginningOfToday : sendingOptions.sendDate;
    return moment(eventDetails.startDate).diff(inviteSendDate, 'days') > 3;
  });

export const getResponsiveOptionFlag = createSelector(getSelectedEngagement, getEngageType,
  getTotalSelectedEngageFilters,getFeatureEnabled(SharedConstants.campaignEngineV2),
  (engagement, type, totalSelectedEngageFilters, campaignEngineV2) => {
    let { startDate, sendDate, sendImmediate } = engagement;

    if ((type === EngageConstants.email || type === EngageConstants.event)
      && !sendImmediate && totalSelectedEngageFilters > 0
      && moment().add(1, 'd').isBefore(moment(sendDate))) {
      return true;
    }

    if (type === EngageConstants.offer && totalSelectedEngageFilters > 0
      && moment().add(1, 'd').isBefore(moment(startDate)) && !campaignEngineV2 ) {
      return true;
    }

    return false;
  });

export const getEngageAppliedFiltersWithIndividualsAndDate = createSelector(
  getDisplayFilters(AppConstants.engagePage), getSelectedEngageDefaultFilters, getEngageAppliedIncludedCustomers,
  getEngageAppliedExcludedCustomers, getEngageAppliedFilterOnBetweenId, getEngageAppliedFilterStartDate, getEngageAppliedFilterEndDate,
  getMeetSavedGroupBeingEdited,
  (allFilters, selectedFilters, includedCustomers, excludedCustomers, onBetweenId, startDate, endDate, meetSavedGroupBeingEdited) => {
    return addExtraFilters(allFilters, selectedFilters, includedCustomers, excludedCustomers, onBetweenId, startDate, endDate,
      meetSavedGroupBeingEdited);
  }
)

export const getEngageAllSelectedCustomerIds = createSelector(getEngageState, fromEngage.getAllSelectedCustomerIds);

export const getEngageMeetSelectedFilters = createSelector(getEngageState, fromEngage.getMeetSelectedFilters);

export const getEngageAllFilters = createSelector(
  getEngageMeetSelectedFilters, getSelectedEngageSavedFilters, getEngageAppliedFiltersWithIndividualsAndDate,
  (meetSelectedFilters, selectedSavedGroups, selectedFilters)=> {
    let allFilters = meetSelectedFilters.concat(selectedFilters);
    selectedSavedGroups.forEach(savedGroup =>{
      allFilters = allFilters.concat(savedGroup.selectedFilters)
    })
    return allFilters
  })

export const getSendOfferRequest = createSelector(
  getSelectedTreat, getEngageDisplayCampaignName, getIncludeActiveCustomers, getEngageAllSelectedCustomerIds, getActiveRewardCustomers,
  getSelectedTheme, getGroupSendOption, getEngageAppliedFiltersWithIndividualsAndDate, getSelectedEngageSavedFilters,
  getFeatureEnabled(SharedConstants.campaignEngineV2), getEngageAllFilters,
  (treat, campaignName, includeActive, totalCustomers, activeCustomers, selectedTheme, dynamicFlag,
    selectedFilters, selectedSavedGroups, campaignEngineV2, allFilters ) => {
    let allCustomerIds = R.clone(totalCustomers);
    let individualCustomersFilter = R.find(R.propEq('id', MeetConstants.includeFilterId))(selectedFilters);
    const numberOfCustomers = includeActive ? totalCustomers.length : totalCustomers.length - activeCustomers.length;
    // Remove all activeCustomers from the individual customer filter if includeActive is false
    if (individualCustomersFilter && individualCustomersFilter.cfaIdList && activeCustomers && !includeActive) {
      activeCustomers.forEach((activeCustomer) => {
        const cfaIdIndex = individualCustomersFilter.cfaIdList.indexOf(activeCustomer);
        if (cfaIdIndex > -1) {
          individualCustomersFilter.cfaIdList.splice(cfaIdIndex, 1);
        }
        const customerProfileIndex = R.findIndex(R.propEq('id', activeCustomer))(individualCustomersFilter.customerProfiles);
        if (customerProfileIndex > -1) {
          individualCustomersFilter.customerProfiles.splice(customerProfileIndex, 1);
        }
      });
    }
    if (campaignEngineV2 && activeCustomers && !includeActive) {
      activeCustomers.forEach((activeCustomer) => {
        const allIdsIndex = allCustomerIds.indexOf(activeCustomer);
        if (allIdsIndex > -1) {
          allCustomerIds.splice(allIdsIndex, 1);
        }
      })
    }
    return {
      ...treat,
      campaignName,
      dynamicFlag,
      includeActive,
      numberOfCustomers,
      selectedFilters,
      selectedSavedGroups,
      selectedTheme,
      campaignEngineV2,
      totalCustomers : allCustomerIds,
      allFilters
    };
  });

export const getCreateCampaignAudienceInfo = createSelector(getEngageAllSelectedCustomerIds,
  getEngageSelectedFilters, (cfaIDs , selectedFilters) =>{
    return {
      cfaIDs,
      selectedFilters
    }
  });

const getCreateCampaignIdsAndAudience = createSelector(
  getSelectedCampaignID, getSelectedNotificationID, getCreateCampaignAudienceInfo,
  (campaignID, notificationID, createCampaignAudienceInfo) => ({
    campaignID,
    notificationID,
    ...createCampaignAudienceInfo
  })
);

export const getCreateCampaignRequest = createSelector(
  getCampaignCustomizations, getSelectedRewardCampaign, getSelectedEventCampaign, getSelectedTemplateDetails,
  getSendingOptions, getCreateCampaignIdsAndAudience,
  (customizations, rewardInformation, eventInformation, template, sendingOptions, idsAndAudience) => {
    let startDate = moment().add(10, 'seconds')
    if (!sendingOptions.sendImmediate &&
        (template.campaignType === CampaignType.Notification || template.campaignType === CampaignType.Event)) {
      const time = moment(sendingOptions.sendTime, 'h:mm a');
      startDate = sendingOptions.sendDate.set(
        { 'hour': time.hour(), 'minute': time.minute() }
      )
    } else if (template.campaignType === CampaignType.Reward) {
      if (!rewardInformation.startDatetime.isSameOrBefore(moment())) {
        startDate = rewardInformation.startDatetime.set('hour', 5);
      }
      rewardInformation.startDatetime = startDate;
      rewardInformation.endDatetime = rewardInformation.endDatetime.set('hour', 23);
    }

    if (template.campaignType === CampaignType.Event) {
      const startTime = moment(eventInformation.startTime, 'h:mm a');
      const endTime = moment(eventInformation.endTime, 'h:mm a');
      eventInformation.startDate = eventInformation.startDate.set({ 'hour': startTime.hour(), 'minute': startTime.minute() });
      eventInformation.endDate = eventInformation.endDate.set({ 'hour': endTime.hour(), 'minute': endTime.minute() });
    }

    sendingOptions.sendDate = startDate;

    return {
      customizations,
      rewardInformation,
      eventInformation,
      template,
      sendingOptions,
      ...idsAndAudience
    };
  });

export const getCampaignFilterInfo = createSelector(getSelectedLocation, getSelectedCampaign, (location, campaign) =>{
  return {
    location,
    groupNow: campaign.sendingOptions.groupNow,
    audienceID: campaign.audienceDetails.audienceID,
    disableCustomerSelection: campaign.audienceDetails.disableCustomerSelection
  }
});

/**
 * Maps API / client errors for scheduled campaign sends to the user-facing copy.
 */
export const resolveCampaignSendErrorMessage = (error: any): any => {
  if (error === true || error == null || error === '') {
    return error;
  }
  const message = typeof error === 'string' ? error : String(error);
  if (message === EngageConstants.scheduledTimeElapsedError
      || isScheduledTimeElapsedErrorMessage(message)) {
    return EngageConstants.scheduledTimeElapsedError;
  }
  return error;
};

const isScheduledTimeElapsedErrorMessage = (message: string): boolean => {
  const normalized = message.toLowerCase();
  if (normalized.includes('must be in the future')) {
    return true;
  }
  const hasSchedule = normalized.includes('schedul');
  const hasElapsedOrPast = normalized.includes('elaps')
    || normalized.includes('past')
    || normalized.includes('expire');
  const hasSendTime = normalized.includes('send date')
    || normalized.includes('send time')
    || normalized.includes('senddatetime')
    || normalized.includes('send datetime');
  const hasFuture = normalized.includes('future');

  return (hasSchedule && hasElapsedOrPast)
    || (hasSendTime && (hasElapsedOrPast || hasFuture));
};

export const getCampaignConfirmation = (update)=> {
  return createSelector(
    getSelectedCampaignLoading, getCreateCampaignError, getSelectedCampaignID, getIsMobile, getCampaignType,
    (loading, error, campaignID, isMobile, type) => {
      let displayType: string = type == CampaignType.Notification ? 'email' : type.toLowerCase();
      let headingMessage = `Yay! Your ${displayType} has been ${update? 'updated': 'created'} successfully!`;
      let subText = `Please wait up to 15 minutes after email sending completes to see statistics for this campaign.`
      if (error) {
        headingMessage = 'Oh no! Looks like something went wrong.';
        subText = resolveCampaignSendErrorMessage(error);
      }
      return {
        loading,
        error,
        campaignID,
        isMobile,
        headingMessage,
        subText,
        type,
        update,
        actionType: update ? ActionType.Edit : ActionType.Create
      };
    });
}
export const getAppPreviewOverride = createSelector(getSelectedCampaign,
  getSelectedEngagement, getFeatureEnabled(SharedConstants.streamlineReward),
  (campaign, engagement, isStreamline) => {

    if (campaign?.templateDetails?.category === 'Catering' || engagement.category === 'Catering') {
      return 'https://www.cfacdn.com/img/Spotlight/Treats/catering-reward-preview.png';
    } else if (isStreamline) {
      return 'https://www.cfacdn.com/img/Spotlight/Treats/streamline-rewards-app-preview.png';
    }
    return null;
  });

export const getEngageRecruitmentModuleEnabled = createSelector(getSelectedEngagement, (selectedEngagement) => {
  return selectedEngagement.recruitmentModuleEnabled;
});

export const getEngageRecruitmentModuleLink = createSelector(getSelectedEngagement, (selectedEngagement) => {
  return selectedEngagement.recruitmentModuleLink;
});

export const getEventPayload = createSelector(getSelectedEvent,
  getEngageDisplayCampaignName, getSelectedLocation, getGroupSendOption, getResponsiveOptionFlag,
  getEngageAppliedFiltersWithIndividualsAndDate, getSelectedEngageSavedFilters, getEngageAllSelectedCustomerIds,
  getEngageAllFilters, (event, campaignName, location, dynamicFlag, isResponsiveOption, selectedFilters,
    selectedSavedGroups, customerIds, allFilters) => {
    // The following combines all the dates and times and formats them the way the backend expects
    // endDate, startDate, and sendDate are formatted like 2019-09-12T00:00:00.000Z
    // endDateParam and startDateParam are formatted like 2019-09-12T00:00:00-04:00
    let endDate = event.endDate.format('YYYY-MM-DD') + ' ' + event.endTime;
    let formattedEndDate = moment(endDate, 'YYYY-MM-DD h:mm:ss a');
    let startDate = event.startDate.format('YYYY-MM-DD') + ' ' + event.startTime;
    let formattedStartDate = moment(startDate, 'YYYY-MM-DD h:mm:ss a');
    let sendDate = event.sendDate.format('YYYY-MM-DD') + ' ' + event.sendTime;
    let formattedSendDate = moment(sendDate, 'YYYY-MM-DD h:mm:ss a');
    let sendDateString = event.sendDate.format('YYYY-MM-DD');
    let isDynamicFlag = isResponsiveOption ? dynamicFlag : null;

    const eventChronology = [
      {
        endDate: moment.utc(formattedEndDate).format(),
        endDateParam: formattedEndDate.format(),
        startDate: moment.utc(formattedStartDate).format(),
        startDateParam: formattedStartDate.format(),
        timeIndicator: true
      }
    ];
    return {
      // adultsOnly and rsvpRequired were initially set up as 'Yes' or 'No' strings and have been
      // saved in Dynamo as such ever since so it would be very difficult to use booleans now, which
      // would be preferred, as that would break the calendarEvents call unless every existing Dynamo
      // row were changed from the 'Yes' and 'No' strings to booleans (and the backend was updated)
      adultsOnly: event.adultsOnly ? 'Yes' : 'No',
      campaignName,
      createdAt: moment(),
      description: event.emailCopy,
      dynamicFlag: isDynamicFlag,
      emailheadline: event.emailheadline,
      emailheadlineOptions: event.emailheadlineOptions,
      eventChronology,
      eventTitle: campaignName,
      displayThemes: event.displayThemes,
      externalLink: event.externalLink,
      imageUrl: event.imageUrl,
      imageUrl2: event.imageUrl2,
      mobileImageUrl: event.mobileImageUrl,
      mobileHeaderImageUrl: event.mobileHeaderImageUrl,
      locationNumber: location.locationNumber,
      maxCustomers: event.maxNumberOfReservations,
      name: event.title,
      rsvpRequired: event.rsvpRequired ? 'Yes' : 'No',
      sendDate: event.sendImmediate ? moment.utc().format() : moment.utc(formattedSendDate).format(),
      sendDateString: event.sendImmediate ? moment.utc().format('YYYY-MM-DD') : sendDateString,
      sendImmediate: event.sendImmediate,
      sendReminderForNotRSVPed: event.sendNonRsvpReminder,
      sendReminderForRSVPed: event.sendRsvpReminder,
      subject: event.subject,
      templateId: event.id,
      venue: event.venue,
      recruitmentModuleEnabled: event.recruitmentModuleEnabled,
      recruitmentModuleLink: event.recruitmentModuleLink,
      type: EngageConstants.event,
      selectedFilters,
      numberOfCustomers: customerIds.length,
      selectedSavedGroups,
      allFilters
    };
  });

export const getEmailPayload = createSelector(
  getSelectedEmail, getEngageDisplayCampaignName, getSelectedLocation, getGroupSendOption,
  getResponsiveOptionFlag, getEngageAppliedFiltersWithIndividualsAndDate, getSelectedEngageSavedFilters,
  getEngageAllSelectedCustomerIds, getEngageAllFilters,
  (email, campaignName, location, dynamicFlag, isResponsiveOption,
    selectedFilters, selectedSavedGroups, customerIds, allFilters) => {
    let fullSendDate = email.sendDate;
    let isDynamicFlag = isResponsiveOption ? dynamicFlag : null;
    let startDateString = email.sendDate.format('YYYY-MM-DD');
    if (!email.sendImmediate) {
      let fullSendDateString = `${fullSendDate.format('MM/DD/YYYY')} ${email.sendTime}`;
      fullSendDate = moment(fullSendDateString, 'MM/DD/YYYY h:mm a');
    }

    // return null for id since backend will override previous email if sent
    return {
      ...email,
      id: null,
      campaignName,
      ctaButtonLink: email.showCtaButton ? email.ctaButtonLink : 'style="display:none;"',
      description: email.emailCopy,
      locationNumber: location.locationNumber,
      name: email.subject,
      sendImmediate: email.sendImmediate,
      startDate: email.sendImmediate ? moment() : fullSendDate,
      startDateString: email.sendImmediate ? moment().format('YYYY-MM-DD') : startDateString,
      templateTitle: email.templateName,
      recruitmentModuleEnabled: email.recruitmentModuleEnabled,
      recruitmentModuleLink: email.recruitmentModuleLink,
      type: EngageConstants.email,
      dynamicFlag: isDynamicFlag,
      themes: [],
      stockImages: [],
      numberOfCustomers: customerIds.length,
      selectedFilters,
      selectedSavedGroups,
      allFilters
    };
  });

export const getEventEditPayload = createSelector(
  getSelectedEvent, getEngageDisplayCampaignName, getEngageAppliedFiltersWithIndividualsAndDate, getSelectedEngageSavedFilters,
  (event, campaignName, selectedFilters, selectedSavedGroups) => {
    // The following combines all the dates and times and formats them the way the backend expects
    // endDate, startDate, and sendDate are formatted like 2019-09-12T00:00:00.000Z
    // endDateParam and startDateParam are formatted like 2019-09-12T00:00:00-04:00
    let endDate = event.endDate.format('YYYY-MM-DD') + ' ' + event.endTime;
    let formattedEndDate = moment(endDate, 'YYYY-MM-DD h:mm:ss a');
    let startDate = event.startDate.format('YYYY-MM-DD') + ' ' + event.startTime;
    let formattedStartDate = moment(startDate, 'YYYY-MM-DD h:mm:ss a');
    let sendDate = event.sendDate.format('YYYY-MM-DD') + ' ' + event.sendTime;
    let formattedSendDate = moment(sendDate, 'YYYY-MM-DD h:mm:ss a');
    const eventChronology = [
      {
        endDate: moment.utc(formattedEndDate).format(),
        endDateParam: formattedEndDate.format(),
        startDate: moment.utc(formattedStartDate).format(),
        startDateParam: formattedStartDate.format(),
        timeIndicator: true
      }
    ];

    return {
      campaignName,
      description: event.emailCopy,
      eventChronology,
      eventId: event.id,
      displayThemes: event.displayThemes,
      externalLink: event.externalLink,
      imageUrl2: event.imageUrl2,
      maxCustomers: event.maxNumberOfReservations,
      name: event.title,
      sendDate: event.sendImmediate ? moment.utc().format() : moment.utc(formattedSendDate).format(),
      sendImmediate: event.sendImmediate,
      sendReminderForNotRSVPed: event.sendNonRsvpReminder,
      sendReminderForRSVPed: event.sendRsvpReminder,
      subject: event.subject,
      type: EngageConstants.event,
      venue: event.venue,
      imageUrl: event.imageUrl,
      dynamicFlag: event.dynamicFlag,
      recruitmentModuleEnabled: event.recruitmentModuleEnabled,
      recruitmentModuleLink: event.recruitmentModuleLink,
      createdAt: moment(event.createdAt),
      selectedFilters,
      selectedSavedGroups
    };
  });

export const getEmailEditPayload = createSelector(
  getSelectedEmail, getEngageDisplayCampaignName, getSelectedLocation, getResponsiveOptionFlag,
  getEngageAppliedFiltersWithIndividualsAndDate, getSelectedEngageSavedFilters, getEngageState,
  (email, campaignName, location, isResponsiveOption, selectedFilters, selectedSavedGroups, engState) => {
    let fullSendDate = email.sendDate;
    let dynamicFlag = isResponsiveOption ? email.dynamicFlag : null;
    if (!email.sendImmediate) {
      let fullSendDateString = `${fullSendDate.format('MM/DD/YYYY')} ${email.sendTime}`;
      fullSendDate = moment(fullSendDateString, 'MM/DD/YYYY h:mm a');
    }

    return {
      campaignName,
      description: email.emailCopy,
      headerImage: email.headerImage,
      id: email.id,
      image: email.image,
      imageUrl: email.imageUrl,
      imageUrl2: email.imageUrl2,
      locationNumber: location.locationNumber,
      name: email.subject,
      sendImmediate: email.sendImmediate,
      startDate: email.sendImmediate ? moment() : fullSendDate,
      templateTitle: email.templateTitle,
      title: email.title,
      themeId: email.themeId,
      type: email.type,
      recruitmentModuleEnabled: email.recruitmentModuleEnabled,
      recruitmentModuleLink: email.recruitmentModuleLink,
      dynamicFlag,
      createdAt: moment(email.createdAt),
      mgCampaignJobId: email.mgCampaignJobId,
      showCtaButton: email.showCtaButton,
      ctaButtonDesktopImage: email.ctaButtonDesktopImage,
      ctaButtonLink:email.ctaButtonLink,
      ctaButtonMobileImage: email.ctaButtonMobileImage,
      selectedFilters,
      selectedSavedGroups,
      customerCount: engState.allSelectedCustomerIds.length,
      cfaIds: engState.allSelectedCustomerIds
    };
  });

export const getUpdateSavedGroupPayload = createSelector(
  getMeetFiltersStartDate, getMeetFiltersEndDate, getMeetFiltersOnBetweenId, getMeetSavedGroupBeingEdited, getTotalCountOfCustomers,
  getFormattedFiltersWithDateAndIndividuals(AppConstants.meetPage),
  (startDate, endDate, onBetweenId, savedGroup, customerCount, selectedFilters) => {
    return {
      customerCount,
      selectedFilters,
      startDate,
      endDate,
      onBetweenId,
      id: savedGroup.id,
      name: savedGroup.name
    };
  });

export const getEngagementUpdated = createSelector(getEngageState, fromEngage.getUpdated);

export const getOverlappingEngagements = createSelector(getEngageState, fromEngage.getOverlappingEngagements);

export const getEngagementCalendarPayload = createSelector(getEngagementStartDate, getEngagementEndDate,
  getFeatureEnabled(CalendarConstants.nationalEmails),
  (start, end, nationalEmails) => {
    return {
      campaignTypes:  ['rewards', 'events',
        'bonusPointPromotions', 'communityCareEvent',
        'free-item-in-cart-promotion', 'third-party-promotion',
        'nationalThirdPartyPromotion'],
      fromDate: start.format('YYYY-MM-DD'),
      nationalEmails,
      toDate: end.format('YYYY-MM-DD')
    };
  });

export const getEngagementStartAndEndDate = createSelector(getEngagementStartDate, getEngagementEndDate,
  (start, end) => {
    return {
      startDate: start,
      endDate: end
    };
  });

export const getEngageHtml = createSelector(getEngageState, getSelectedEngagement, getScreenSize,
  (eng: fromEngage.State, engagement, screenSize) => {
    let merge;
    const isMobile = screenSize === AppConstants.extraSmall;
    const html = eng.previewHtml;
    const heroImage = isMobile ? engagement.mobileImageUrl : (engagement.imageUrl || engagement.mobileImageUrl);
    // Only replace html if engagement has been selected and base html exists
    if ((engagement.id || engagement.treatId) && html) {
      merge = {
        breakfast: engagement.breakfast,
        breakfastDisclaimerText: engagement.breakfastDisclaimerText || '',
        ctaButtonDesktopImage: engagement.ctaButtonDesktopImage,
        ctaButtonLink: engagement.ctaButtonLink,
        ctaButtonMobileImage: engagement.ctaButtonMobileImage,
        description: engagement.emailCopy || engagement.offerDetails,
        endDate: moment(engagement.endDate),
        endTime: engagement.endTime,
        headerImage: engagement.headerImage || engagement.image || engagement.imageUrl2,
        headline: engagement.emailheadline || engagement.subject,
        imageUrl: heroImage || engagement.imageUrl || engagement.offerImage,
        limitedReservations: engagement.limitedReservations,
        limitedTimeOffer: engagement.limitedtimeoffer,
        mobileHeaderImage: engagement.mobileHeaderImage || engagement.mobileHeaderImageUrl,
        mobileImageUrl:heroImage || engagement.imageUrl || engagement.offerImage,
        name: engagement.offerName,
        recruitmentModuleEnabled: engagement.recruitmentModuleEnabled,
        rsvpRequired: engagement.rsvpRequired,
        showCountdownTimer: engagement.showCountdownTimer,
        showCtaButton: engagement.showCtaButton,
        startDate: moment(engagement.startDate),
        startTime: engagement.startTime,
        subTitle: engagement.subTitle,
        title: engagement.title || '',
        validDetails: engagement.details || engagement.validDetails,
        venue: engagement.venue
      };
      return replaceMGHtml(merge, html);
    } else {
      return '';
    }
  });

export const getRsvpHtml = createSelector(getEngageState, getSelectedEngagement,
  (eng: fromEngage.State, engagement) => {
    const html = eng.rsvpHtml;
    const merge = {
      startDate: moment(engagement.startDate),
      startTime: engagement.startTime,
      endDate: moment(engagement.endDate),
      endTime: engagement.endTime,
      title: engagement.title || '',
      description: engagement.emailCopy || engagement.offerDetails,
      name: engagement.offerName,
      validDetails: engagement.details || engagement.validDetails,
      imageUrl: engagement.imageURL || engagement.imageUrl || engagement.offerImage,
      mobileImageUrl: engagement.imageURL || engagement.imageUrl || engagement.offerImage,
      breakfastDisclaimerText: engagement.breakfastDisclaimerText || '',
      mobileHeaderImage: engagement.mobileHeaderImage || engagement.mobileHeaderImageUrl,
      headerImage: engagement.headerImage || engagement.image || engagement.imageUrl2,
      headline: engagement.emailheadline || engagement.subject,
      rsvpRequired: engagement.rsvpRequired,
      limitedReservations: engagement.limitedReservations,
      limitedTimeOffer: engagement.limitedtimeoffer,
      breakfast: engagement.breakfast,
      venue: engagement.venue
    };
    return replaceMGHtml(merge, html);
  });

export const getNoRsvpHtml = createSelector(getEngageState, getSelectedEngagement,
  (eng: fromEngage.State, engagement) => {
    const html = eng.noRsvpHtml;
    const merge = {
      startDate: moment(engagement.startDate),
      startTime: engagement.startTime,
      endDate: moment(engagement.endDate),
      endTime: engagement.endTime,
      title: engagement.title || '',
      description: engagement.emailCopy || engagement.offerDetails,
      name: engagement.offerName,
      validDetails: engagement.details || engagement.validDetails,
      imageUrl: engagement.imageURL || engagement.imageUrl || engagement.offerImage,
      mobileImageUrl: engagement.imageURL || engagement.imageUrl || engagement.offerImage,
      breakfastDisclaimerText: engagement.breakfastDisclaimerText || '',
      mobileHeaderImage: engagement.mobileHeaderImage || engagement.mobileHeaderImageUrl,
      headerImage: engagement.headerImage || engagement.image || engagement.imageUrl2,
      headline: engagement.emailheadline || engagement.subject,
      rsvpRequired: engagement.rsvpRequired,
      limitedReservations: engagement.limitedReservations,
      limitedTimeOffer: engagement.limitedtimeoffer,
      breakfast: engagement.breakfast,
      venue: engagement.venue
    };
    return replaceMGHtml(merge, html);
  });

export const getCategoryTreatsById = (id) => {
  return createSelector(getEngageRewards, (treats) => {
    if (id === EngageConstants.limitedTimeOffer ) {
      return treats.ltoTemplates;
    } else {
      const treatsByCategory = R.groupBy(R.prop('category'), treats.templates);
      return treatsByCategory[id];
    }
  });
};

export const getCopyTitle = createSelector(getEngageState, (eng: fromEngage.State) => {
  let type = eng.type === EngageConstants.offer ? 'reward' : eng.type;
  return type + ' message';
});

export const getEngageFilterDisplayCustomers = createSelector(getEngageState, fromEngage.getFilterDisplayCustomers);

export const getEngageFilterDisplayCustomerLoading = createSelector(getEngageState, fromEngage.getFilterDisplayCustomerLoading);

export const getEngageFilterDisplayCustomerError = createSelector(getEngageState, fromEngage.getFilterDisplayCustomerError);

export const getEngageFilterDisplayCustomerSort = createSelector(getEngageState, fromEngage.getFilterDisplayCustomerSort);

export const getEngageFilterDisplayCustomerSortOrder = createSelector(getEngageState, fromEngage.getFilterDisplayCustomerSortOrder);

export const getEngageFilterDisplayCustomerSortObj = createSelector(
  getEngageFilterDisplayCustomerSort, getEngageFilterDisplayCustomerSortOrder, (prop, order) => {
    return ({ prop, order });
  });

export const getEngageFilterDisplayCustomerLimit = createSelector(getEngageState, fromEngage.getFilterDisplayCustomerLimit);

export const getEngageFilterDisplayCustomerPage = createSelector(getEngageState, fromEngage.getFilterDisplayCustomerPage);

export const getEngageFilterDisplayCustomerNumberOfPages = createSelector(getEngageState, fromEngage.getFilterDisplayCustomerNumberOfPages);

export const getEngageFilterDisplayCustomerAllCustomerIds =
  createSelector(getEngageState, fromEngage.getFilterDisplayCustomerAllCustomerIds);

export const getEngageFilterDisplayCustomerCount = createSelector(getEngageState, fromEngage.getFilterDisplayCustomerCount);

// True when no filters, included/excluded customers, saved groups, or a complete custom date range are selected
// on the Engage filter-selection page. Used to disable the Apply Filters button and to zero out its customer count.
export const getEngageHasNoSelectionCriteria = createSelector(
  getEngageSelectedFilters, getIncludedCustomersByPage(AppConstants.engagePage), getExcludedCustomersByPage(AppConstants.engagePage),
  getSelectedSavedGroupsByPage(AppConstants.engagePage), getDisplayFilters(AppConstants.engagePage),
  getFiltersStartDateByPage(AppConstants.engagePage), getFiltersEndDateByPage(AppConstants.engagePage),
  (selectedFilters, includedCustomers, excludedCustomers, selectedSavedGroups, displayFilters, startDate, endDate) =>
    selectedFilters.length === 0 && includedCustomers.length === 0 && excludedCustomers.length === 0
    && selectedSavedGroups.length === 0 && isCustomDateRangeFilterIncomplete(displayFilters, startDate, endDate)
);

// Apply Filters Button customer count should show zero when no filters have been applied
export const getEngageApplyButtonCustomerCount = createSelector(
  getEngageHasNoSelectionCriteria, getEngageFilterDisplayCustomerCount,
  (hasNoSelectionCriteria, customerCount) => hasNoSelectionCriteria ? 0 : customerCount
);

export const getEngageSelectedCustomerLoading = createSelector(getEngageState, fromEngage.getSelectedCustomerLoading);

export const getEngageSendLoading = createSelector(getEngageState, fromEngage.getSendLoading);

export const getEngageStepperProgress = createSelector(getEngageState, fromEngage.getStepperProgress);

export const getEngageSendError = createSelector(getEngageState, fromEngage.getSendError);

export const shouldGetAllCustomers = createSelector(getEngageState, fromEngage.shouldGetAllCustomers);

export const getEngageIsCompleted = createSelector(getEngageStepperProgress,
  (sendProgress) => {
    let count = 0;
    let errorCount = 0;
    if (sendProgress) {
      sendProgress.map(menuItem => {
        if (menuItem.isCompleted === true) {
          count = count + 1;
        }
        if (menuItem.error) {
          errorCount = errorCount + 1;
        }
      });
    }
    return count < 3 && errorCount === 0;
  });

export const getFlaggedEngageAllSelectedCustomerIds = createSelector(getEngageAllSelectedCustomerIds,
  (allSelectedIds) => {
    return allSelectedIds;
  });

export const getEngageTotalSelectedCustomers = createSelector(getEngageAllSelectedCustomerIds,
  (allSelectedCustomers) => allSelectedCustomers.length);

export const getEngageAllAppliedFilters = createSelector(
  getSelectedEngageDefaultFilters,
  getSelectedEngageSavedFilters,
  getEngageAppliedIncludedCustomers,
  getEngageAppliedExcludedCustomers,
  getEngageAppliedFilterOnBetweenId,
  getEngageAppliedFilterStartDate,
  getEngageAppliedFilterEndDate,
  (selectedFilters, selectedSavedGroups, includedCustomers,
    excludedCustomers, onBetweenId, startDate, endDate) => {
    return { selectedFilters, selectedSavedGroups, includedCustomers,
      excludedCustomers, onBetweenId, startDate, endDate };
  });

/**
 * Calendar state root selector
 */
export const getCalendarState = (state) => state.calendar;

/**
 * Calendar state children selectors
 */
export const getCalendarError = createSelector(getCalendarState, fromCalendar.getError);

export const getCalendarEventDetails = createSelector(
  getCalendarState, fromCalendar.getCalendarEventDetails);

export const getCampaignsToggleActive = createSelector(
  getCalendarState, fromCalendar.getCampaignToggleActive);

export const getRewardToggleActive = createSelector(
  getCalendarState, fromCalendar.getRewardToggleActive);

export const getRewardAutomationToggleActive = createSelector(
  getCalendarState, fromCalendar.getRewardAutomationToggleActive);

export const getRewardOneRecipientToggleActive = createSelector(
  getCalendarState, fromCalendar.getRewardOneRecipientToggleActive);

export const getEventToggleActive = createSelector(
  getCalendarState, fromCalendar.getEventToggleActive);

export const getEmailToggleActive = createSelector(
  getCalendarState, fromCalendar.getEmailToggleActive);

export const getNationalEmailToggleActive = createSelector(
  getCalendarState, fromCalendar.getNationalEmailToggleActive);

export const getIncentivizeOrderToggleActive = createSelector(
  getCalendarState, fromCalendar.getIncentivizeOrderToggleActive);

export const getCommunityCareToggleActive = createSelector(
  getCalendarState, fromCalendar.getCommunityCareToggleActive);

export const getThirdPartyPromotionToggleActive = createSelector(
  getCalendarState, fromCalendar.getThirdPartyPromotionToggleActive);

export const getNationalThirdPartyPromotionToggleActive = createSelector(
  getCalendarState, fromCalendar.getNationalThirdPartyPromotionToggleActive);

export const getFreeItemInCartPromotionToggleActive = createSelector(
  getCalendarState, fromCalendar.getFreeItemInCartPromotionToggleActive);

export const getCurrentDate = createSelector(
  getCalendarState, fromCalendar.getCurrentDate);

export const getCurrentView = createSelector(
  getCalendarState, fromCalendar.getCurrentView);

export const getEarliestShownDate = createSelector(
  getCalendarState, fromCalendar.getEarliestShownDate);

export const getLatestShownDate = createSelector(
  getCalendarState, fromCalendar.getLatestShownDate);

export const getCalendarSearchTerm = createSelector(
  getCalendarState, fromCalendar.getSearchObj);

export const getAutoCompleteSelectedCustomer = createSelector(
  getMeetState, fromMeet.getAutoCompleteSelectedCustomer);

export const getSelectedCategoryTitle = createSelector(
  getCalendarSearchTerm, (searchObject) => {
    return R.find(R.propEq('value', searchObject.searchBy))(CalendarConstants.searchByCategories).title;
  }
);

export const getCaresSelectedCategoryTitle = createSelector(
  getCareSearchBy, (searchBy) => {
    return R.find(R.propEq('value', searchBy))(CareConstants.searchByCategories).title;
  }
)

export const getCalendarEventsPayload = createSelector(
  getEarliestShownDate, getLatestShownDate, getFeatureEnabled(SharedConstants.campaignEngineV2),
  getFeatureEnabled(SharedConstants.campaignEngineV2Events),
  getFeatureEnabled(CalendarConstants.nationalEmails), getAutomationsFeatureEnabledMap,
  (start, end, campaignEngineV2, campaignEngineV2Event, nationalEmails, automationsFeatureMap) => {
    let campaignTypes = ['rewards', 'emails', 'events','holidaysAndCampaigns', 'bonusPointPromotions', 'communityCareEvent',
      'third-party-promotion', 'nationalThirdPartyPromotion', 'free-item-in-cart-promotion'];
    //default start and end, for when calendar is "refreshed" but calendar has no dates
    start = start ?? moment().startOf('month').startOf('week');
    end = end ?? moment(start).add(5, 'weeks').endOf('week');
    return {
      campaignEngineV2,
      campaignEngineV2Email: true,
      campaignEngineV2Event,
      anyAutomations: Object.values(automationsFeatureMap).some(Boolean),
      campaignTypes,
      fromDate: start.format('YYYY-MM-DD'),
      includeAutomationCampaigns: true,
      includeSingleRecipientRewards: true,
      nationalEmails,
      toDate: end.format('YYYY-MM-DD'),
    };
  });

export const getCalendarFeatureFlags= createSelector(getFeatureEnabled(CalendarConstants.bonusPointPromotionsFlag),
  getFeatureEnabled(DiscoverConstants.communityCareEventsFlag),
  getThirdPartyEnabled(), getFeatureEnabled(DiscoverConstants.freeItemInCartPromotionFlag),
  getFeatureEnabled(CalendarConstants.nationalEmails),
  (bonusPointFlag, communityCareFlag, thirdPartyFlag, freeItemFlag, nationalEmailsFlag) =>{
    return {
      bonusPointFlag: bonusPointFlag,
      communityCareFlag: communityCareFlag,
      thirdPartyFlag: thirdPartyFlag,
      freeItemFlag: freeItemFlag,
      nationalEmailsFlag: nationalEmailsFlag
    };
  })

export const getCalendarTogglesActive = createSelector(getIncentivizeOrderToggleActive,
  getCommunityCareToggleActive,
  getThirdPartyPromotionToggleActive,
  getNationalThirdPartyPromotionToggleActive,
  getFreeItemInCartPromotionToggleActive,
  (incentivizeOrderToggleActive, communityCareToggleActive, thirdPartyPromotionToggleActive,
    nationalThirdPartyPromotionToggleActive, freeItemInCartPromotionActive) => {
    return { incentivizeOrderToggleActive: incentivizeOrderToggleActive,
      communityCareToggleActive: communityCareToggleActive,
      thirdPartyPromotionToggleActive: thirdPartyPromotionToggleActive,
      nationalThirdPartyPromotionToggleActive : nationalThirdPartyPromotionToggleActive,
      freeItemInCartPromotionActive: freeItemInCartPromotionActive };
  })

export const getCalendarCategories = createSelector(getRewardToggleActive,
  getEventToggleActive, getEmailToggleActive,
  getNationalEmailToggleActive, getRewardAutomationToggleActive,
  getRewardOneRecipientToggleActive, getCalendarFeatureFlags, getCalendarTogglesActive,
  (rewardToggleActive, eventToggleActive, emailToggleActive, nationalEmailToggleActive,
    automationToggleActive, oneRecipientActive, calendarFeatureFlags, calendarTogglesActive) => {

    let rewardSubToggles = CalendarConstants.rewardSubToggles;
    let automationToggleIndex = R.findIndex(R.propEq('categoryId', CalendarConstants.automationId))(rewardSubToggles);
    let oneRecipientToggleIndex = R.findIndex(R.propEq('categoryId', CalendarConstants.singleRecipientId))(rewardSubToggles);
    rewardSubToggles[automationToggleIndex].active = automationToggleActive;
    rewardSubToggles[oneRecipientToggleIndex].active = oneRecipientActive;

    let calendarCategories: CalendarCategory[] = [
      {
        active: rewardToggleActive,
        calendarIds: ['offer', CampaignType.Reward, CampaignType.Automation],
        name: 'Rewards',
        color: '#3EB1C8',
        categories: rewardSubToggles
      },
      { active: eventToggleActive, calendarIds: ['event', CampaignType.Event], name: 'Events', color: '#FA9370' },
      { active: emailToggleActive, calendarIds: ['email', CampaignType.Notification], name: 'Emails', color: '#994878' }
    ];
    if (calendarFeatureFlags.nationalEmailsFlag) {
      calendarCategories.push(
        {
          active: nationalEmailToggleActive,
          calendarIds: ['nationalEmail'],
          name: 'National Email',
          color: '#249E6B',
        });
    }

    if (calendarFeatureFlags.freeItemFlag) {
      calendarCategories.push(
        {
          active: calendarTogglesActive.freeItemInCartPromotionActive,
          calendarIds: ['free-item-in-cart-promotion'],
          name: 'Free Item on Order',
          color: '#5C3454'
        });
    }

    if (calendarFeatureFlags.bonusPointFlag) {
      calendarCategories.push(
        {
          active: calendarTogglesActive.incentivizeOrderToggleActive,
          calendarIds: ['bonus-point'],
          name: 'Bonus Points',
          color: '#DD0031'
        });
    }

    if (calendarFeatureFlags.communityCareFlag) {
      calendarCategories.push(
        {
          active: calendarTogglesActive.communityCareToggleActive,
          calendarIds: ['communityCareEvent'],
          name: 'Community Care Events',
          color: '#84001D'
        }
      );
    }

    if (calendarFeatureFlags.thirdPartyFlag) {
      calendarCategories.push(
        {
          active: calendarTogglesActive.thirdPartyPromotionToggleActive,
          calendarIds: ['third-party-promotion'],
          name: 'Free Item on 3rd Party Order',
          color: '#00635B'
        }
      );
      calendarCategories.push(
        {
          active: calendarTogglesActive.nationalThirdPartyPromotionToggleActive,
          calendarIds: ['nationalThirdPartyPromotion'],
          name: 'National Third Party Promotions',
          color: '#249E6B'
        }
      );
    }
    return calendarCategories;
  });

export const getCalendarEventResendTreat = (ids) => {
  return createSelector(getCalendarEventDetails, (details) => {
    const treat = details.treats[0];

    // filter out recipients who are not in the resend id list
    let alreadyReceivedTreat = (recipient) => R.includes(recipient.customerUid, ids);
    let recipients = R.filter(alreadyReceivedTreat, treat.recipients);

    return { ...treat, recipients, resentSameTreat: true, earlierBatchId: treat.batchId };
  });
};

export const getCalendarDetailsDateRange = createSelector(getCalendarEventDetails, (details) => {
  if (details) {
    const treat = details.treats[0];
    const start = moment(treat.startDate).format('ddd, MMM DD');
    const end = moment(treat.endDate).format('ddd, MMM DD');
    return `${start} - ${end}`;
  }
});

export const getAutomatedCampaignDuration = createSelector(getCalendarEventDetails, (details) => {
  if (details) {
    const duration = details.treatValidityDays;
    return duration + ' day reward duration';
  }
});

export const getAutomatedCampaign = createSelector(getCalendarEventDetails, (details) => {
  if (details) {
    return details.automatedCampaign;
  }
});

export const getCampaignTimeSpan = createSelector(getAutomatedCampaign, getAutomatedCampaignDuration, getCalendarDetailsDateRange,
  (automatedCampaign, automatedCampaignDuration, calendarDateRange) => {
    return automatedCampaign ? automatedCampaignDuration : calendarDateRange;
  });

export const getCalendarDetailsSummary = createSelector(getCalendarEventDetails, (details) => {
  if (details) {
    let treatString = details.treats.length > 1 ? 'Rewards' : 'Reward';
    let customerString = details.totalCustomers > 1 ? 'Customers' : 'Customer';

    return `${details.treats.length} ${treatString} / ${details.totalCustomers} ${customerString}`;
  }
});

/**
 * Notifications state root selector
 */
export const getNotificationsState = (state) => state.notifications;

/**
 * Notifications state children selectors
 */
export const getAllNotifications = createSelector(getNotificationsState, fromNotifications.getNotifications);

export const getNotificationsByCategory = createSelector(getAllNotifications, (notifications) => {
  const types = NotificationListConstants.notificationTypes;

  const grouped = R.groupBy(R.prop('type'), notifications);
  return types.map((type) => {
    const name = type.name;
    return {
      ...type,
      notifications: grouped[name] || []
    };
  });
});

export const isNotificationsEmpty = createSelector(getNotificationsByCategory,
  (notificationCategories) => {
    let isEmpty = true;
    notificationCategories.forEach((categories) => {
      if (categories.notifications.length > 0) {
        isEmpty = false;
      }
    });
    return isEmpty;
  });
export const getNotificationUnreadCount = createSelector(
  getNotificationsState, fromNotifications.getUnreadCount);

export const getNotificationLoading = createSelector(
  getNotificationsState, fromNotifications.getLoading);

export const getNotificationError = createSelector(
  getNotificationsState, fromNotifications.getError);

/**
 * Error state root selector
 */
export const getErrorState = (state) => state.error;

/**
 * Error state children selectors
 */
export const getErrorPages = createSelector(
  getErrorState, fromError.getErrorPages);

/**
 * Settings state root selector
 */
export const getSettingsState = (state: State) => state.settings;

/**
 * Settings state children selectors
 */
export const getSuccessMessage = createSelector(
  getSettingsState, fromSettings.getSuccessMessage);

export const getFilteredRecipients = createSelector(
  getRecipientsSearchTerm,
  getSelectedEngagement,
  getEngageType,
  getEngageRsvpRequired,
  (recipientsSearchTerm, selectedEngagement, type, rsvpRequired) => {
    if (type === EngageConstants.offer || type === EngageConstants.discover || type === EngageConstants.communityCare) {
      let filterRecipients;
      if (selectedEngagement.recipients) {
        filterRecipients = selectedEngagement.recipients.filter((customer) => {
          let customerFullName = '';
          if (customer.customerName && customer.customerName.displayName) {
            customerFullName = customer.customerName.displayName.toUpperCase();
          } else if (customer.firstName && customer.lastName) {
            customerFullName = (customer.firstName + ' ' + customer.lastName).toUpperCase();
          }
          return customerFullName.includes(recipientsSearchTerm.toUpperCase());
        });
      } else {
        filterRecipients = [];
      }

      return filterRecipients;
    } else if (type === EngageConstants.email) {
      let filterEmailRecipients = {
        'openedEmailCustomers': [],
        'unOpenedEmailCustomers': []
      };
      filterEmailRecipients.openedEmailCustomers = selectedEngagement.openedEmailCustomers.filter((customer) => {
        return getMatchingCustomer(customer, recipientsSearchTerm);
      });
      filterEmailRecipients.unOpenedEmailCustomers = selectedEngagement.unOpenedEmailCustomers.filter((customer) => {
        return getMatchingCustomer(customer, recipientsSearchTerm);
      });

      return filterEmailRecipients;
    } else if (type === EngageConstants.event) {
      let filterEventRecipients = {
        'acceptedRecipients': [],
        'declinedRecipients': [],
        'noResponseRecipients': []
      };
      if (rsvpRequired) {
        filterEventRecipients.acceptedRecipients = selectedEngagement.acceptedRecipients.filter((customer) => {
          return getMatchingCustomer(customer, recipientsSearchTerm);
        });
        filterEventRecipients.declinedRecipients = selectedEngagement.declinedRecipients.filter((customer) => {
          return getMatchingCustomer(customer, recipientsSearchTerm);
        });
      }
      filterEventRecipients.noResponseRecipients = selectedEngagement.noResponseRecipients.filter((customer) => {
        return getMatchingCustomer(customer, recipientsSearchTerm);
      });

      return filterEventRecipients;
    }
  });

export const getFilteredRecipientsForCommunityCareEvent = createSelector(
  getRecipientsSearchTerm, getSelectedEngagement, (recipientsSearchTerm, selectedEngagement) => {
    let filteredRecipients = [];
    (selectedEngagement.organizations || []).forEach((organization, index) => {
      if (organization.recipients) {
        const matchingRecipients = organization.recipients.filter((customer) => {
          let customerFullName = '';
          if (customer.customerName && customer.customerName.displayName) {
            customerFullName = customer.customerName.displayName.toUpperCase();
          } else if (customer.firstName && customer.lastName) {
            customerFullName = (customer.firstName + ' ' + customer.lastName).toUpperCase();
          }
          return customerFullName.includes(recipientsSearchTerm.toUpperCase());
        });
        filteredRecipients[index] = groupCommunityCareRecipientsByDate(matchingRecipients);
      } else {
        filteredRecipients[index] = [];
      }
    });

    return filteredRecipients;
  });

function groupCommunityCareRecipientsByDate(recipients) {
  const recipientsByDate = new Map<string, any[]>();
  recipients.forEach((customer) => {
    const date = customer.transactionDate || '';
    const customersForDate = recipientsByDate.get(date) || [];
    customersForDate.push(customer);
    recipientsByDate.set(date, customersForDate);
  });

  return Array.from(recipientsByDate.entries())
    .sort(([leftDate], [rightDate]) => compareCommunityCareDates(leftDate, rightDate))
    .map(([date, customers]) => ({
      date,
      subtotal: formatCommunityCareSubtotal(customers),
      recipients: customers
    }));
}

function compareCommunityCareDates(leftDate: string, rightDate: string): number {
  const left = moment(leftDate, 'MM/DD', true);
  const right = moment(rightDate, 'MM/DD', true);
  if (left.isValid() && right.isValid()) {
    return left.valueOf() - right.valueOf();
  }
  if (left.isValid()) {
    return -1;
  }
  if (right.isValid()) {
    return 1;
  }
  return 0;
}

function formatCommunityCareSubtotal(customers): string {
  const sum = customers.reduce((total, customer) => {
    const value = Number(customer.subtotal);
    return total + (Number.isFinite(value) ? value : 0);
  }, 0);
  return (Math.round(sum * 100) / 100).toFixed(2);
}

export const isFutureEmailOrEventSend = createSelector(getSelectedEngagement, (selectedEngagement) => {
  return !(selectedEngagement.sendImmediate) && (moment(selectedEngagement.sendDate).isAfter(moment()));
});

export const getCustomerTabTitle = createSelector(getFilteredRecipients, isFutureEmailOrEventSend, getEngageType,
  (filteredRecipients, scheduledEventOrEmail, type) => {
    let count, title;
    if (type === EngageConstants.event) {
      count = filteredRecipients.noResponseRecipients.length;
      title = 'No Response';
    } else if (type === EngageConstants.email) {
      count = filteredRecipients.unOpenedEmailCustomers.length;
      title = 'Unopened Emails';
    }
    if (scheduledEventOrEmail) {
      return 'Scheduled Recipients (' + count + ')';
    } else {
      return title + ' (' + count + ')';
    }
  });

export const getEngageSendLoadingMessage = createSelector(getEngageType, getEngageMode,
  getEngageSendImmediate, (type, mode, sendImmediateBool) => {
    let sendImmediate = sendImmediateBool;
    let typeValue = type;
    let sendImmediateValue = 'created';

    if (type === EngageConstants.offer) {
      sendImmediate = true;
      typeValue = 'reward';
    }

    if (mode === EngageConstants.editMode) {
      sendImmediateValue = 'updated';
    } else if (!sendImmediate) {
      sendImmediateValue = 'scheduled';
    }
    return `Please wait while the ${typeValue} is being ${sendImmediateValue}!`;
  });

export const getEngageSendErrorMessage = createSelector(getEngageSendError,
  (sendError) => {
    return {
      headingMessage: 'Oh no! Looks like something went wrong.',
      errorSubText: sendError
    };
  });

export const getEngageSendSuccessMessage = createSelector(getEngageType, getEngageMode,
  getEngageSendImmediate, (type, mode, sendImmediateBool) => {
    let sendImmediate = sendImmediateBool;
    let typeValue = type;
    let sendImmediateValue = 'created';

    if (type === EngageConstants.offer) {
      sendImmediate = true;
      typeValue = 'reward';
    }

    if (mode === EngageConstants.editMode) {
      sendImmediateValue = 'updated';
    } else if (!sendImmediate) {
      sendImmediateValue = 'scheduled';
    }

    return {
      headingMessage: `Yay! Your ${typeValue} has been ${sendImmediateValue} successfully!`,
      subText: `Please wait 24 to 48 hours to access statistics for this ${typeValue}.`
    };
  });

export const getSendConfirmationAction = createSelector(getEngageSendLoading, getEngageSendError,
  getEngageSendLoadingMessage, getEngageSendErrorMessage, getEngageSendSuccessMessage,
  (isLoading, sendError, loadingMessage, errorMessage, successMessage) => {
    if (isLoading) {
      return loadingMessage;
    } else {
      return sendError ? errorMessage : successMessage;
    }
  });

export const getEngagementSendLoadingMessage = createSelector(getEngagementType, getEngagementMode, getEngagementAutomationAction,
  (type, mode, engagementAction) => {
    let typeValue = type;
    let actionValue = 'created';

    if (type === EngageConstants.email) {
      typeValue = EngageConstants.email;
    }
    if (type === EngageConstants.thirdPartyPromotion) {
      typeValue = 'Third Party Promotion';
    }
    if (type === EngageConstants.offer) {
      typeValue = 'reward';
    }
    if (type === EngageConstants.freeItemInCartPromotion) {
      typeValue = 'free item in cart promotion';
    }

    if (mode === EngageConstants.editMode) {
      actionValue = 'updated';
      if (engagementAction === DiscoverConstants.stop) {
        actionValue = 'stopped'
      }
    }
    return `Please wait while the ${typeValue} is being ${actionValue}!`;
  });

export const getEngagementSendErrorMessage = createSelector(getEngagementSendError,
  (sendError) => {
    return {
      errorSubText: sendError === true ? '' : resolveCampaignSendErrorMessage(sendError),
      headingMessage: 'Oh no! Looks like something went wrong.'
    };
  });

export const getEngagementSendSuccessMessage = createSelector(getEngagementType, getEngagementMode, getEngagementAutomationAction,
  getCurrentEngagementId, (type, mode, engagementAction, flowId) => {
    let typeValue = type;
    let actionValue = 'created';
    if (type === EngageConstants.offer) {
      typeValue = 'reward';
    }

    // On delete the type for CCE is still automation so check flowId as well
    if (type === EngageConstants.communityCare || flowId === DiscoverConstants.communityCareEventTileId) {
      typeValue = 'event';
    }

    let subText = `Please wait 24 to 48 hours to access statistics for this ${typeValue}.`;

    if (type === EngageConstants.thirdPartyPromotion || flowId === EngageConstants.thirdPartyPromotion
      || type === EngageConstants.freeItemInCartPromotion || flowId === EngageConstants.freeItemInCartPromotion) {
      typeValue = 'promotion';
      subText = '';
    }

    if (mode === EngageConstants.editMode) {
      actionValue = 'updated';
      subText = '';
      if (engagementAction === DiscoverConstants.stop) {
        actionValue = 'stopped'
      }
    }
    return {
      headingMessage: `Yay! Your ${typeValue} has been ${actionValue} successfully!`,
      subText: subText
    };
  });

export const getEngagementSendAction = createSelector(getEngagementSendLoading, getEngagementSendError,
  getEngagementSendLoadingMessage, getEngagementSendErrorMessage, getEngagementSendSuccessMessage,
  (isLoading, sendError, loadingMessage, errorMessage, successMessage) => {
    if (isLoading) {
      return loadingMessage;
    } else {
      return sendError ? errorMessage : successMessage;
    }
  });

export const enabledRecruitmentModule = createSelector(getSelectedEngagement,
  getFeatureEnabled(EngageConstants.recruitmentModuleFlag), (engagement, recruitmentModuleEnabled) => {
    if (recruitmentModuleEnabled) {
      if (engagement.defaultSelectedTheme && (engagement.defaultSelectedTheme === EngageConstants.themeWeApologize ||
          engagement.defaultSelectedTheme === EngageConstants.themeSorryForTheSnag)) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  });

export const getClosedCareContactsSearchTerm = createSelector(getCareState, fromCare.getClosedCareContactsSearchTerm);

export const getClosedCareContactsSearchBy = createSelector(getCareState, fromCare.getClosedCareContactsSearchBy);

export const getClosedCareContactsPage = createSelector(getCareState, fromCare.getClosedCareContactsPage);

export const getClosedCareContactsPageSize = createSelector(getCareState, fromCare.getClosedCareContactsPageSize);

export const getClosedCareContactsSortBy = createSelector(getCareState, fromCare.getClosedCareContactsSortBy);

export const getClosedCareContactsSortOrder = createSelector(getCareState, fromCare.getClosedCareContactsSortOrder);

export const getClosedCareContactsError = createSelector(getCareState, fromCare.getClosedCareContactsError);

export const getClosedCareContactsSelectedCategoryTitle = createSelector(
  getClosedCareContactsSearchBy, (searchBy) => {
    return R.find(R.propEq('value', searchBy))(CareConstants.searchByCategories).title;
  }
)

export const getClosedCareContactsSortObj = createSelector(
  getClosedCareContactsSortBy, getClosedCareContactsSortOrder,
  (prop, order) => {
    return { prop, order };
  });

// ---------------------------------------------------------------------------
// Client-side selector pipeline helpers
// ---------------------------------------------------------------------------

function isClosed(c: any): boolean {
  const s = (c.status || '').toLowerCase();
  return s.startsWith('closed') || s.startsWith('resolved');
}

export function applySearch(cases: any[], term: string, by: string): any[] {
  if (!term) { return cases; }
  const lower = term.toLowerCase();
  return cases.filter(c => {
    if (by === 'customerName') {
      return (c.firstName || '').toLowerCase().includes(lower) ||
             (c.lastName || '').toLowerCase().includes(lower) ||
             (c.fullName || '').toLowerCase().includes(lower);
    }
    if (by === 'syncReportNumber' || by === 'reportNumber') {
      return (c.number || '').toLowerCase().includes(lower);
    }
    return String(c[by] ?? '').toLowerCase().includes(lower);
  });
}

export function applySortOrder(cases: any[], prop: string, order: string): any[] {
  if (!prop) { return cases; }
  return [...cases].sort((a, b) => {
    const propA = (prop === 'reportDateTime' && a.lastReportDateTime) ? 'lastReportDateTime' : prop;
    const propB = (prop === 'reportDateTime' && b.lastReportDateTime) ? 'lastReportDateTime' : prop;
    const av = a[propA];
    const bv = b[propB];
    let cmp = 0;
    if (typeof av === 'number' && typeof bv === 'number') {
      cmp = av - bv;
    } else {
      cmp = String(av ?? '').localeCompare(String(bv ?? ''));
    }
    return order === 'desc' ? -cmp : cmp;
  });
}

export const getOpenCasesForView = createSelector(
  getCasesList, getCareSelectedView,
  (cases: any[], timeframeView: string) => {
    const daysAgo = Number.parseInt(timeframeView.slice(5, 7), 10);
    const cutoff = moment().subtract(daysAgo, 'days').valueOf();
    return cases.filter(c => {
      const caseDate = c.reportDateTime || 0;
      return !isClosed(c) && caseDate >= cutoff;
    });
  });

export const getClosedCasesForView = createSelector(
  getCasesList, getClosedCareSelectedView,
  (cases: any[], timeframeView: string) => {
    const daysAgo = Number.parseInt(timeframeView.slice(5, 7), 10);
    const cutoff = moment().subtract(daysAgo, 'days').valueOf();
    return cases.filter(c => {
      const caseDate = c.reportDateTime || 0;
      return isClosed(c) && caseDate >= cutoff;
    });
  });

export const getSearchedOpenCases = createSelector(
  getOpenCasesForView, getCareSearchTerm, getCareSearchBy,
  (cases: any[], term: string, by: string) => applySearch(cases, term, by));

export const getSearchedClosedCases = createSelector(
  getClosedCasesForView, getClosedCareContactsSearchTerm, getClosedCareContactsSearchBy,
  (cases: any[], term: string, by: string) => applySearch(cases, term, by));

export const getSortedOpenCases = createSelector(
  getSearchedOpenCases, getCareSortBy, getCareSortOrder,
  (cases: any[], prop: string, order: string) => applySortOrder(cases, prop, order));

export const getSortedClosedCases = createSelector(
  getSearchedClosedCases, getClosedCareContactsSortBy, getClosedCareContactsSortOrder,
  (cases: any[], prop: string, order: string) => applySortOrder(cases, prop, order));

export const getOpenTotalElements = createSelector(getSearchedOpenCases, (cases: any[]) => cases.length);
export const getClosedTotalElements = createSelector(getSearchedClosedCases, (cases: any[]) => cases.length);

export const getOpenCareContactsNumberOfPages = createSelector(
  getOpenTotalElements, getCarePageSize,
  (total: number, size: number) => Math.ceil(total / size));

export const getClosedCareContactsNumberOfPages = createSelector(
  getClosedTotalElements, getClosedCareContactsPageSize,
  (total: number, size: number) => Math.ceil(total / size));

export const getPaginatedOpenCases = createSelector(
  getSortedOpenCases, getCarePage, getCarePageSize,
  (cases: any[], page: number, size: number) => cases.slice(page * size, page * size + size));

export const getPaginatedClosedCases = createSelector(
  getSortedClosedCases, getClosedCareContactsPage, getClosedCareContactsPageSize,
  (cases: any[], page: number, size: number) => cases.slice(page * size, page * size + size));

export const getClosedCareContactsNoSearchResultMessage = createSelector(getClosedCareContactsSearchTerm, (searchTerm) => {
  if (searchTerm != null) {
    return CareConstants.noClosedCareContactsSearchResultMessage + ' ' + searchTerm + '.';
  } else {
    return CareConstants.noClosedCareContactsSearchResultMessage;
  }
});

export const getClosedCareContactsNoResultsMessage =
  createSelector(getClosedCareContactsNoSearchResultMessage, getClosedCareContactsSearchTerm,
    (noSearchResultMessage, searchTerm) => {
      if (searchTerm) {
        return noSearchResultMessage;
      } else {
        return CareConstants.noResultsMessage;
      }
    });

export const getRestaurantRecoveryColumns =
  createSelector(getSpotlightContactsSearchBy, getFeatureEnabled(CareConstants.digitalRefundsFlag),(searchBy, digitalRefundsEnabled) => {
    let columns = digitalRefundsEnabled
      ? CareConstants.spotCustomerColumnsNewWithRedFlag : CareConstants.spotCustomerColumnsNewWithOutRedFlag;

    if (searchBy === CareConstants.phoneNumberValue || searchBy === CareConstants.customerEmailValue) {
      // To remove memberStatus column
      return columns.filter((column) => column.prop !== 'membership');
    } else {
      return columns;
    }
  });

export const getEnableMembershipAsIcon = createSelector(getSpotlightContactsSearchBy, (searchBy) => {
  return (searchBy != CareConstants.phoneNumberValue && searchBy != CareConstants.customerEmailValue);
});

export const getRecoveredCfaOneContactsLoading = createSelector(getCareState, fromCare.getRecoveredCfaOneContactsLoading);

export const getRecoveredCfaOneContactsError = createSelector(getCareState, fromCare.getRecoveredCfaOneContactsError);

export const getRecoveredCfaOneContactsSearchTerm = createSelector(getCareState, fromCare.getRecoveredCfaOneContactsSearchTerm);

export const getRecoveredCfaOneContactsSearchBy = createSelector(getCareState, fromCare.getRecoveredCfaOneContactsSearchBy);

export const getRecoveredCfaOneContactsPageSize = createSelector(getCareState, fromCare.getRecoveredCfaOneContactsPageSize);

export const getRecoveredCfaOneContactsCurrentPage = createSelector(getCareState, fromCare.getRecoveredCfaOneContactsCurrentPage);

export const getRecoveredCfaOneContactsNumberOfPages = createSelector(getCareState, fromCare.getRecoveredCfaOneContactsNumberOfPages);

export const getRecoveredCfaOneContactsSortBy = createSelector(getCareState, fromCare.getRecoveredCfaOneContactsSortBy);

export const getRecoveredCfaOneContactsSortOrder = createSelector(getCareState, fromCare.getRecoveredCfaOneContactsSortOrder);

export const getRecoveredCfaOneContactsDisplayErrorText = createSelector(getCareState, fromCare.getRecoveredCfaOneContactsDisplayErrorText);

export const getLoadRecoveredCfaOneContacts = createSelector(getCareState, fromCare.getLoadRecoveredCfaOneContacts);

export const getCareSelectedTabIndex = createSelector(getCareState, fromCare.getCareSelectedTabIndex);

export const getDigitalRefundColumns = createSelector(getFeatureEnabled(ProfileConstants.curbsideFlag), (curbsideEnabled)=>{
  let col = ProfileConstants.digitalRefundsTableColumns;
  if (curbsideEnabled) {
    col.splice(col.length - 1, 0, ProfileConstants.recoveryIssuedColumn);
    col.join();
  }
  return col;
});

export const getRecoveredCfaOneContactsNoSearchResultMessage = createSelector(getRecoveredCfaOneContactsSearchTerm, (searchTerm) => {
  if (searchTerm != null) {
    return CareConstants.recoveredCfaOneContactsNoSearchResultMessage + ' ' + searchTerm + '.';
  } else {
    return CareConstants.recoveredCfaOneContactsNoSearchResultMessage;
  }
});

export const getRecoveredCfaOneContactsNoResultsMessage =
  createSelector(getRecoveredCfaOneContactsNoSearchResultMessage, getRecoveredCfaOneContactsSearchTerm,
    (noSearchResultMessage, searchTerm) => {
      if (searchTerm) {
        return noSearchResultMessage;
      } else {
        return CareConstants.recoveredCfaOneContactsNoResultsMessage;
      }
    });

export const getRecoveredCfaOneContactsSortObj = createSelector(
  getRecoveredCfaOneContactsSortBy, getRecoveredCfaOneContactsSortOrder,
  (prop, order) => {
    return { prop, order };
  });

export const getRecoveredCfaOneContactsRequest = createSelector(
  getRecoveredCfaOneContactsCurrentPage, getRecoveredCfaOneContactsPageSize, getRecoveredCfaOneContactsSortBy,
  getRecoveredCfaOneContactsSortOrder, getRecoveredCfaOneContactsSearchTerm, getRecoveredCfaOneContactsSearchBy, getRecoveredSelectedView,
  (page, limit, sortBy, sortOrder, searchTerm, searchBy, view) => {
    let fromDate = moment().subtract(view.slice(5,7), 'days').format('YYYY-MM-DD');
    let toDate = moment().format('YYYY-MM-DD');
    let recoveredCfaOneContactsSearchTerm = searchTerm === '' ? null : searchTerm;
    let recoveredCfaOneContactsSearchBy = recoveredCfaOneContactsSearchTerm ? searchBy : null;
    return {
      page,
      limit,
      sortBy,
      sortOrder,
      searchBy : recoveredCfaOneContactsSearchBy,
      searchTerm : recoveredCfaOneContactsSearchTerm,
      fromDate,
      toDate
    };
  });

export const getRecoveredCfaOneContactsSelectedCategoryTitle = createSelector(
  getRecoveredCfaOneContactsSearchBy, (searchBy) => {
    return R.find(R.propEq('value', searchBy))(CareConstants.recoveredCfaOneContactsSearchByCategories).title;
  }
);
